/**
 * French message catalog. Mirrors every key in the English base catalog. Any key
 * not present here degrades to English via the resolver's fallback chain.
 */
import type { Messages } from "../types";

export const fr: Messages = {
  // Sidebar navigation - group labels
  "nav.group.build":   "Creer",
  "nav.group.ship":    "Publier",
  "nav.group.operate": "Exploiter",
  "nav.group.account": "Compte",
  "nav.group.help":    "Centre d'aide",

  // Sidebar navigation - item titles
  "nav.item.dashboard":     "Tableau de bord",
  "nav.item.projects":      "Projets",
  "nav.item.deployments":   "Deploiements",
  "nav.item.mobileBuilds":  "Builds mobiles",
  "nav.item.marketplace":   "Place de marche",
  "nav.item.creativeStudio": "Studio creatif",
  "nav.item.library":       "Bibliotheque",
  "nav.item.creatorStudio": "Studio createur",
  "nav.item.billing":       "Facturation",
  "nav.item.settings":      "Parametres",
  "nav.item.helpCenter":    "Centre d'aide",
  "nav.item.capabilities":  "Capacites",
  "nav.item.whatsNew":      "Nouveautes",
  "nav.item.documents":     "Documents",
  "nav.item.engineering":   "Ingenierie",

  // Sidebar footer - live platform status
  "footer.status.nominal":  "systemes nominaux",
  "footer.status.degraded": "degrade",
  "footer.status.outage":   "panne detectee",

  // User avatar menu
  "menu.billing":              "Facturation",
  "menu.settings":             "Parametres",
  "menu.platformIntelligence": "Intelligence de la plateforme",
  "menu.signOut":              "Se deconnecter",
  "menu.admin":                "Administrateur",
  "menu.userFallback":         "Utilisateur",
  "menu.language":             "Langue",

  // Settings - item labels
  "settings.item.profile":       "Profil",
  "settings.item.security":      "Securite",
  "settings.item.notifications": "Notifications",
  "settings.item.billing":       "Facturation et credits",
  "settings.item.apiKeys":       "Cles API",
  "settings.item.connectors":    "Connecteurs",
  "settings.item.aiPreferences": "Preferences IA",
  "settings.item.memory":        "Memoire",
  "settings.item.export": "Exportation des données",
  "settings.item.audit": "Journal d’audit",
  "settings.item.privacy":       "Confidentialité et données",
  "settings.item.systemStatus":  "Etat du systeme",
  "settings.item.language":      "Langue de l'interface",
  "settings.item.appearance":    "Apparence",
  "settings.item.team":          "Equipe",
  "settings.item.webhooks":      "Webhooks",
  "settings.item.helpCenter":    "Centre d'aide",
  "settings.item.dangerZone":    "Zone dangereuse",
  "settings.item.integrations":  "Intégrations",

  // Settings - header
  "settings.title":    "Parametres",
  "settings.subtitle": "Gerez votre compte, la securite, les integrations et vos donnees depuis un seul endroit.",

  // Language panel
  "language.title":       "Langue de l'interface",
  "language.description": "Choisissez la langue de l'interface Nuvra. Cela ne change pas la langue des applications que vous creez.",
  "language.label":       "Langue",
  "language.search":      "Rechercher des langues",
  "language.noMatch":     "Aucune langue ne correspond a votre recherche.",
  "language.current":          "Actuelle",
  "language.autoSaved":         "Enregistrée automatiquement",
  "language.clearSearch":       "Effacer la recherche",
  "language.trySearch":         "Essayez un nom de langue ou un code ISO.",
  "language.scope":             "S'applique uniquement à l'interface Nuvra. La langue des logiciels que vous créez est contrôlée séparément.",
  "language.region.global":     "Global",
  "language.region.americas":   "Amériques",
  "language.region.europe":     "Europe",
  "language.region.middle_east":"Moyen-Orient",
  "language.region.africa":     "Afrique",
  "language.region.asia":       "Asie",

  // 404 page
  "notFound.kicker":    "Route introuvable",
  "notFound.body":      "Cette route n'existe pas dans l'usine. Revenez vers un systeme connu.",
  "notFound.goBack":    "Retour",
  "notFound.dashboard": "Tableau de bord",

  // Auth - shared
  "auth.backToHome":  "Retour a l'accueil",
  "auth.backToLogin": "Retour a la connexion",
  "auth.email":       "E-mail",
  "auth.password":    "Mot de passe",
  "auth.or":          "ou",
  "auth.error":       "Erreur",
  "auth.signIn":      "Se connecter",

  // Auth - login
  "auth.login.welcome":     "Bon retour",
  "auth.login.subtitle":    "Accedez a votre usine logicielle IA",
  "auth.login.forgot":      "Mot de passe oublie ?",
  "auth.login.noAccount":   "Vous n'avez pas de compte ?",
  "auth.login.signUpLink":  "S'inscrire",
  "auth.login.failedTitle": "Echec de la connexion",

  // Auth - signup
  "auth.signup.title":           "Creez votre compte",
  "auth.signup.subtitle":        "Votre usine logicielle IA vous attend",
  "auth.signup.fullName":        "Nom complet",
  "auth.signup.namePlaceholder": "Votre nom",
  "auth.signup.createAccount":   "Creer le compte",
  "auth.signup.haveAccount":     "Vous avez deja un compte ?",
  "auth.signup.loginLink":       "Connexion",
  "auth.signup.failedTitle":     "Echec de l'inscription",
  "auth.signup.createdTitle":    "Compte cree !",
  "auth.signup.createdBody":     "Vous etes connecte. Votre espace de travail est pret.",

  // Auth - forgot password
  "auth.forgot.title":          "Mot de passe oublie",
  "auth.forgot.subtitleSent":   "Verifiez votre e-mail pour le lien de reinitialisation",
  "auth.forgot.subtitlePrompt": "Saisissez votre e-mail et nous vous enverrons un lien de reinitialisation",
  "auth.forgot.sentBefore":     "Nous avons envoye un lien de reinitialisation a",
  "auth.forgot.sentAfter":      ". Le lien expirera dans 60 minutes.",
  "auth.forgot.tryAnother":     "Essayer un autre e-mail",
  "auth.forgot.send":           "Envoyer le lien",
  "auth.forgot.remember":       "Vous vous souvenez de votre mot de passe ?",

  // Auth - reset password
  "auth.reset.titleSuccess":    "Mot de passe mis a jour",
  "auth.reset.title":           "Definir un nouveau mot de passe",
  "auth.reset.subtitleSuccess": "Redirection vers la connexion...",
  "auth.reset.subtitle":        "Choisissez un mot de passe robuste pour votre compte",
  "auth.reset.successBody":     "Votre mot de passe a ete reinitialise. Vous allez etre redirige vers la page de connexion.",
  "auth.reset.verifying":       "Verification de votre lien de reinitialisation...",
  "auth.reset.newPassword":     "Nouveau mot de passe",
  "auth.reset.confirmPassword": "Confirmer le mot de passe",
  "auth.reset.submit":          "Reinitialiser le mot de passe",
  "auth.reset.mismatchTitle":   "Les mots de passe ne correspondent pas",
  "auth.reset.mismatchBody":    "Assurez-vous que les deux mots de passe sont identiques.",
  "auth.reset.tooShortTitle":   "Mot de passe trop court",
  "auth.reset.tooShortBody":    "Le mot de passe doit comporter au moins 8 caracteres.",
  "auth.reset.failedTitle":     "Echec de la reinitialisation",

  "deploy.header.kicker":   "Operations de deploiement",
  "deploy.header.title":    "Deploiement",
  "deploy.header.subtitle": "Apercu, promotion, publication, observation, restauration.",
  "deploy.header.refresh":  "Actualiser",
  "deploy.loadingProjects": "Chargement des projets...",
  "deploy.noProjects":      "Aucun projet trouve. Creez-en un depuis le tableau de bord d'abord.",
  "deploy.env.preview":     "Apercu",
  "deploy.env.production":  "Production",
  "deploy.env.domain":      "Domaine",
  "deploy.env.live":        "En ligne",
  "deploy.env.none":        "Aucun",
  "deploy.tab.deploy":      "Deploiement",
  "deploy.tab.domains":     "Domaines",
  "deploy.tab.publishing":  "Publication",
  "deploy.tab.lineage":     "Lignage",
  "deploy.tab.visibility":  "Visibilite",
  "deploy.hosted.liveTitle":         "Votre application est en ligne",
  "deploy.hosted.notPublishedTitle": "Pas encore publiee",
  "deploy.hosted.subtitle":          "Nuvra heberge votre application sur son runtime: publication instantanee, sans etape de build.",
  "deploy.hosted.manageVisibility":  "Gerer la visibilite",
  "deploy.hosted.liveUrl":           "URL en ligne",
  "deploy.hosted.vanityUrl":         "URL personnalisee",
  "deploy.hosted.liveHint":          "Reconstruisez dans l'espace de travail du projet pour mettre a jour l'application en ligne instantanement. Utilisez l'onglet Visibilite pour gerer l'URL personnalisee ou rendre l'application privee.",
  "deploy.hosted.notPublishedHint":  "Construisez votre application dans l'espace de travail du projet, puis publiez-la via le bouton Publier pour la rendre accessible publiquement a une URL partageable.",
  "deploy.time.justNow":    "a l'instant",
  "deploy.time.minutesAgo": "il y a {n} min",
  "deploy.time.hoursAgo":   "il y a {n} h",

  // Projects page (the founder's project list)
  "projects.title":             "Projets",
  "projects.loading":           "Chargement de vos projets",
  "projects.countOne":          "1 projet",
  "projects.countOther":        "{count} projets",
  "projects.new":               "Nouveau projet",
  "projects.search":            "Rechercher des projets",
  "projects.loadingList":       "Chargement des projets",
  "projects.emptyTitle":           "Démarrez votre premier projet Nuvra.",
  "projects.emptyBody":            "Décrivez ce que vous voulez construire et Nuvra le fera passer par la vraie chaîne d'ingénierie. Votre projet en devient l'espace de travail durable.",
  "projects.page.eyebrow":             "Espace de travail",
  "projects.page.subtitle":            "Vos projets, vos déploiements en direct et votre activité d'ingénierie au même endroit.",
  "projects.page.searchPlaceholder":   "Rechercher un projet par nom ou description",
  "projects.page.sort":                "Trier",
  "projects.page.sortRecent":          "Récents",
  "projects.page.sortName":            "Nom",
  "projects.page.sortStatus":          "Statut",
  "projects.page.gridView":            "Vue en grille",
  "projects.page.listView":            "Vue en liste",
  "projects.page.readyBadge":          "Prêt à construire",
  "projects.page.createProject":       "Créer un projet",
  "projects.page.mobileBuild":         "Build mobile",
  "projects.page.yourProjects":        "Vos projets",
  "projects.page.yourProjectsBody":    "Ouvrez un espace de travail pour reprendre l'ingénierie là où vous l'aviez laissée.",
  "projects.page.clearSearch":         "Effacer la recherche",
  "projects.page.tabAll":              "Tous",
  "projects.page.tabActive":           "En construction",
  "projects.page.tabDeployed":         "En ligne",
  "projects.page.tabDraft":            "Brouillon / Attention",
  "projects.page.noMatchTitle":        "Aucun projet correspondant",
  "projects.page.noMatchBody":         "Essayez un autre nom, une autre description ou un autre filtre.",
  "projects.page.usage":               "Utilisation",
  "projects.page.usageBody":           "L'utilisation du compte en un coup d'oeil. Le coût de chaque build est disponible depuis le build correspondant.",
  "projects.page.currentAccount":      "Compte actuel",
  "projects.page.creditsRemaining":    "Crédits restants",
  "projects.page.liveProjects":        "Projets en ligne",
  "projects.page.autoTopUp":           "Recharge automatique",
  "projects.page.on":                  "Activée",
  "projects.page.off":                 "Désactivée",
  "projects.page.plan":                "Formule",
  "projects.page.manage":              "Gérer",
  "projects.quick.newLabel":           "Nouveau projet",
  "projects.quick.newDesc":            "Partir de zéro",
  "projects.quick.mobileLabel":        "Build mobile",
  "projects.quick.mobileDesc":         "iOS et Android",
  "projects.quick.marketLabel":        "Place de marché",
  "projects.quick.marketDesc":         "Parcourir les extensions",
  "projects.quick.deployLabel":        "Déployer",
  "projects.quick.deployDesc":         "Publiez votre projet",
  "projects.page.statProjects":        "Projets",
  "projects.page.statLive":            "En ligne",
  "projects.page.statBuilding":        "En construction",
  "projects.page.statAttention":       "Nécessite une attention",
  "projects.page.statCredits":         "Crédits",
  "projects.page.footerNav":           "Navigation du pied de page",
  "projects.page.footerDocs":          "Documentation",
  "projects.page.footerSupport":       "Assistance",
  "projects.page.footerStatus":        "État",
  "projects.page.footerPricing":       "Tarifs",
  "projects.noMatch":           "Aucun projet ne correspond a \"{query}\".",
  "projects.openLive":          "Ouvrir en direct",
  "projects.actions":           "Actions du projet",
  "projects.openWorkspace":     "Ouvrir l'espace de travail",
  "projects.shareProject":      "Partager le projet",
  "projects.deleteProject":     "Supprimer le projet",
  "projects.deleteTitle":       "Supprimer ce projet ?",
  "projects.deleteBody":        "\"{name}\" sera definitivement supprime. Cette action est irreversible.",
  "projects.cancel":            "Annuler",
  "projects.delete":            "Supprimer",
  "projects.deleting":          "Suppression...",
  "projects.status.building":   "Creation en cours",
  "projects.status.live":       "En direct",
  "projects.status.error":      "Echec de la creation",
  "projects.status.draft":      "Brouillon",
  "projects.error.title":   "Impossible de charger vos projets",
  "projects.error.body":    "C'est generalement temporaire. Vos projets sont en securite. Reessayez maintenant ou rechargez la page.",
  "projects.error.details": "Details techniques",
  "projects.error.retry":   "Reessayer",
  "projects.time.justNow":      "A l'instant",
  "projects.time.minAgo":       "il y a {min} min",
  "projects.time.hourAgo":      "il y a {h} heure",
  "projects.time.hoursAgo":     "il y a {h} heures",
  "projects.time.yesterday":    "Hier",
  "projects.time.daysAgo":      "il y a {d} jours",
  "projects.toast.deleteFail":  "Impossible de supprimer le projet",
  "projects.toast.deleted":     "Projet supprime",
  "projects.toast.deletedBody": "\"{name}\" a ete supprime.",

  // Billing page (plan, usage, payments, auto top-up)
  "billing.title":            "Facturation",
  "billing.subtitle": "Votre offre, vos credits et chaque paiement, suivis en temps reel.",
  "billing.transparency":     "Les paiements, les credits et l'utilisation sont suivis en temps reel en toute transparence.",
  "billing.tab.plan":         "Forfait",
  "billing.tab.usage":        "Utilisation",
  "billing.tab.payments":     "Paiements",
  "billing.tab.settings":     "Parametres",
  "billing.planBadge":        "Forfait {tier}",
  "billing.creditsAvailable": "{balance} credits disponibles",
  "billing.viewPlans":        "Voir les forfaits",
  "billing.creditsUsedUp":    "Vos credits sont epuises. Passez a un forfait superieur pour continuer a creer.",
  "billing.upgradeTitle":     "Ameliorez votre forfait",
  "billing.perMonth":         "/mois",
  "billing.paymentsInstant":  "Les paiements sont traites instantanement une fois les cles de la passerelle configurees dans votre deploiement.",
  "billing.creditBalance":    "Solde de credits",
  "billing.live":             "En direct",
  "billing.creditsUsed":      "{used} credits utilises",
  "billing.paidBillingOptionsTitle": "Les credits supplementaires sont disponibles avec les forfaits payants",
  "billing.paidBillingOptionsBody": "Passez a un forfait payant pour acheter des credits supplementaires lorsque vous en avez besoin.",
  "billing.paymentPreferences": "Preferences de paiement",
  "billing.paymentPreferencesBody": "Les options de paiement sont disponibles apres le choix d'un forfait payant.",
  "billing.autoTopupBody": "Achetez automatiquement le forfait de credits selectionne lorsque votre solde atteint le seuil.",
  "billing.acuUpgrade": "Vos credits inclus sont epuises. Passez a un forfait payant pour continuer a construire.",
  "billing.lowBalance":       "Votre solde de credits est faible. Rechargez ou activez la recharge automatique.",
  "billing.txHistory":        "Historique des transactions",
  "billing.securePayments":   "Tous les paiements sont traites de maniere securisee. Les credits sont ajoutes uniquement apres confirmation du paiement.",
  "billing.topupPaidTitle":   "Les recharges sont une fonctionnalite payante",
  "billing.topupPaidBody":    "Passez a un forfait payant pour acheter des credits supplementaires a tout moment de votre cycle de facturation.",
  "billing.seePlans":         "Voir les forfaits",
  "billing.autoTopup":        "Recharge automatique",
  "billing.autoPaidTitle":    "La recharge automatique est une fonctionnalite payante",
  "billing.autoPaidBody":     "Passez a un forfait payant pour activer les recharges automatiques de credits.",
  "billing.enableAuto":       "Activer la recharge automatique",
  "billing.thresholdZero":    "Quand le solde atteint 0",
  "billing.thresholdBelow":   "Quand il est inferieur a {n} credits",
  "billing.choosePackage":    "Choisissez un pack de recharge",
  "billing.packageOption":    "{amount} credits · {price}",
  "billing.autoTriggerNote":  "La recharge automatique ne se declenche qu'apres confirmation du paiement.",
  "billing.loading":          "Chargement...",
  "billing.pastDueTitle": "Votre paiement n'a pas abouti",
  "billing.pastDueBody": "Votre offre prend fin dans {days} si le paiement n'aboutit pas. Rien de ce que vous avez cree ne sera supprime.",
  "billing.pastDueToday": "Votre offre prend fin aujourd'hui si le paiement n'aboutit pas. Rien de ce que vous avez cree ne sera supprime.",
  "billing.pastDueDays": "{n} jours",
  "billing.pastDueOneDay": "1 jour",
  "billing.updateCard": "Mettre a jour le moyen de paiement",
  "billing.cancelPlan": "Annuler l'offre",
  "billing.cancelling": "Annulation...",
  "billing.creditsLabel": "Credits disponibles",
  "billing.usedOfTotal": "{used} utilises sur {total}",
  "billing.planPrice": "{price} $/mois",
  "billing.planFree": "Gratuit",

  // Marketplace page (buyer/discovery storefront)
  "marketplace.eyebrow":            "Marketplace",
  "marketplace.heroTitle":          "Des logiciels de production, prets a livrer",
  "marketplace.heroSubtitle":       "Modeles, plugins, composants et workflows d'IA crees sur Nuvra. Installez-les en une etape et personnalisez-les.",
  "marketplace.sell":               "Vendez sur Nuvra",
  "marketplace.trust.verifiedLabel":  "Createurs verifies",
  "marketplace.trust.verifiedSub":    "Vendeurs a l'identite verifiee",
  "marketplace.trust.secureLabel":    "Paiement securise",
  "marketplace.trust.secureSub":      "Paiements geres par la passerelle",
  "marketplace.trust.licensingLabel": "Licences claires",
  "marketplace.trust.licensingSub":   "Chaque annonce indique sa licence",
  "marketplace.prefill.loading":    "Preparation des metadonnees de l'annonce a partir de votre projet.",
  "marketplace.prefill.error":      "Le pre-remplissage des metadonnees est indisponible. Saisissez les details de l'annonce manuellement.",
  "marketplace.prefill.ready":      "Metadonnees de l'annonce pretes",
  "marketplace.prefill.issueOne":   "{n} probleme",
  "marketplace.prefill.issueOther": "{n} problemes",
  "marketplace.prefill.readyBadge": "pret",
  "marketplace.prefill.dismiss":    "ignorer",
  "marketplace.prefill.titleLabel": "Titre",
  "marketplace.prefill.shortDesc":  "Description courte",
  "marketplace.prefill.continue":   "Continuer dans Creator Studio",
  "marketplace.featured":           "A la une",
  "marketplace.trending":           "Tendances",
  "marketplace.trendingTitle":      "Ce qui gagne en popularite",
  "marketplace.trendingNote":       "Classement fonde sur l'activite reelle des annonces publiees au cours des 7 derniers jours.",
  "marketplace.mostUsed":           "Les plus adoptes",
  "marketplace.mostUsedTitle":      "Utilises par de vrais acheteurs",
  "marketplace.mostUsedNote":       "Classement par adoptions marketplace terminees. Aucun chiffre invente.",
  "marketplace.searchPlaceholder":  "Rechercher dans le marketplace",
  "marketplace.sort.newest":        "Plus recents",
  "marketplace.sort.popular":       "Plus populaires",
  "marketplace.sort.rating":        "Les mieux notes",
  "marketplace.sort.priceAsc":      "Prix: bas",
  "marketplace.sort.priceDesc":     "Prix: eleve",
  "marketplace.filterAll":          "Tous",
  "marketplace.error":              "Le catalogue n'a pas pu etre charge. Reessayez dans un instant.",
  "marketplace.retry":              "Reessayer",
  "marketplace.resultOne":          "{total} resultat",
  "marketplace.resultOther":        "{total} resultats",
  "marketplace.resultsFor":         " pour \"{search}\"",
  "marketplace.free":               "Gratuit",
  "marketplace.perMonth":           "/mois",
  "marketplace.license.standard":   "Licence standard",
  "marketplace.license.extended":   "Licence etendue",
  "marketplace.license.enterprise": "Licence entreprise",
  "marketplace.aria.verified":      "Createur verifie",
  "marketplace.aria.sort":          "Trier les annonces",
  "marketplace.aria.featured":      "Annonce a la une",
  "marketplace.aria.close":         "Fermer",
  "marketplace.by":                 "par {name}",
  "marketplace.installOne":         "{n} installation",
  "marketplace.installOther":       "{n} installations",
  "marketplace.reviews.loading":    "Chargement des avis.",
  "marketplace.reviews.none":       "Aucun avis pour le moment.",
  "marketplace.reviews.title":      "Avis",
  "marketplace.toast.added":        "Ajoute a votre bibliotheque",
  "marketplace.toast.addedBody":    "{title} est pret a l'emploi.",
  "marketplace.toast.failed":       "Impossible de finaliser",
  "marketplace.toast.failedBody":   "Veuillez reessayer.",
  "marketplace.toast.signInBody":   "Veuillez vous connecter et reessayer.",
  "marketplace.getFree":            "Obtenir gratuitement",
  "marketplace.buy":                "Acheter {price}",
  "marketplace.empty.noResults":    "Aucun resultat pour \"{search}\"",
  "marketplace.empty.open":         "Le marketplace est ouvert",
  "marketplace.empty.tryDifferent": "Essayez un autre terme de recherche ou effacez le filtre.",
  "marketplace.empty.beFirst":      "Soyez le premier a publier. Creez un projet et publiez-le dans Creator Studio.",
  "marketplace.empty.clear":        "Effacer la recherche",
  "marketplace.empty.build":        "Creez quelque chose",

  // Mobile Builds page (native packaging dashboard)
  "mobile.eyebrow":              "Builds natifs",
  "mobile.title":                "Builds mobiles",
  "mobile.subtitle":             "Generez des artefacts natifs Android et iOS a partir de votre projet.",
  "mobile.projectAria":          "Projet",
  "mobile.target.aab.label":     "Android App Bundle",
  "mobile.target.aab.desc":      "Bundle signe pour le Google Play Store.",
  "mobile.target.apk.label":     "APK Android",
  "mobile.target.apk.desc":      "APK signe pour installation directe et sideloading.",
  "mobile.target.ipa.label":     "Archive d'app iOS",
  "mobile.target.ipa.desc":      "Archive signee pour soumission a l'App Store.",
  "mobile.target.twa.label":     "Trusted Web Activity",
  "mobile.target.twa.desc":      "Votre application web sous forme de package Play Store.",
  "mobile.github.connectedAs":   "Connecte en tant que",
  "mobile.github.connectedTail": ". Les builds sont enregistres dans votre depot et empaquetes sur CI.",
  "mobile.github.connect":       "Connectez GitHub pour empaqueter des builds natifs. Le code de votre projet est pousse vers votre propre depot et l'artefact signe est compile sur CI.",
  "mobile.build":                "Compiler",
  "mobile.buildAgain":           "Compiler a nouveau",
  "mobile.buildStarted":         "Build demarre",
  "mobile.track":                "Suivre",
  "mobile.err.selectProject":    "Selectionnez d'abord un projet.",
  "mobile.err.connectGithub":    "Connectez GitHub pour compiler.",
  "mobile.err.buildFirst":       "Construisez le projet avant de generer des artefacts.",
  "mobile.err.couldNotStart":    "Impossible de demarrer le build.",
  "mobile.noProjectsHint":       "Creez d'abord un projet, puis revenez ici pour generer des artefacts natifs.",
  "mobile.historyTitle":         "Historique des builds",
  "mobile.succeeded":            "{n} reussis",
  "mobile.failed":               "{n} echoues",
  "mobile.running":              "{n} en cours",
  "mobile.loadingHistory":       "Chargement de l'historique des builds",
  "mobile.noBuilds":             "Aucun build pour le moment.",
  "mobile.noBuildsHint":         "Choisissez une cible ci-dessus pour generer votre premier artefact signe.",
  "mobile.startProject":         "Demarrer un projet",
  "mobile.time.dayAgo":          "il y a {d}j",
  "mobile.time.hourAgo":         "il y a {h}h",
  "mobile.time.minAgo":          "il y a {m}m",
  "mobile.time.justNow":         "a l'instant",
  "mobile.comingSoon":           "Bientot disponible",
  "mobile.openProject": "Ouvrir le projet",
  "mobile.projectContext": "Compiler depuis le projet",
  "mobile.noProjectSelected": "Selectionnez un projet pour continuer",
  "mobile.stat.builds": "Builds",
  "mobile.stat.successRate": "Taux de reussite",
  "mobile.stat.lastBuild": "Dernier build",
  "mobile.readinessTitle": "Preparation du build",
  "mobile.readinessSubtitle": "Nuvra validera votre projet et votre compte avant d'admettre un build natif. Aucun etat n'est affiche avant son enregistrement par le backend reel.",
  "mobile.github.ready": "GitHub connecte",
  "mobile.github.required": "Connexion GitHub requise",
  "mobile.projectSelected": "Projet selectionne",
  "mobile.buildCenterEyebrow": "Livraison native",
  "mobile.buildCenterTitle": "Choisissez une cible de build",
  "mobile.buildCenterSubtitle": "Creez l'artefact natif dont votre projet a besoin. Chaque action utilise le chemin existant de build et de facturation gouvernes.",
  "mobile.filter.all": "Tous",
  "mobile.filter.android": "Android",
  "mobile.filter.ios": "iOS",
  "mobile.refresh": "Actualiser l'historique des builds",
  "mobile.recentBuildsTitle": "Builds recents",
  "mobile.artifactReady": "Artefact enregistre",
  "mobile.intelligenceTitle": "Intelligence et gouvernance du build",
  "mobile.intelligenceSubtitle": "Observabilite reelle, historique des versions, provenance de signature et gouvernance du build pour ce projet.",

  // Platform Intelligence page (systems visibility)
  "platform.title":                    "Intelligence de la plateforme",
  "platform.subtitle":                 "Visibilite en direct sur les systemes d'orchestration, de release, de build et d'audit",
  "platform.live":                     "En direct",
  "platform.eyebrow":                  "Console operateur",
  "platform.adminOnly":                "Visibilite operateur. Cette console ne fait pas partie du produit client.",
  "platform.verifying":                "Verification de l acces...",
  "platform.loading":                  "Chargement...",
  "platform.stat.projects":            "Projets",
  "platform.stat.releases":            "Releases",
  "platform.stat.builds":              "Builds mobiles",
  "platform.stat.audit":               "Entrees d'audit",
  "platform.releaseEvents.title":      "Evenements du pipeline de release",
  "platform.releaseEvents.empty":      "Aucun evenement de release enregistre pour le moment.",
  "platform.releaseEvents.emptyHint":  "Les evenements apparaissent ici lorsque des releases sont declenchees.",
  "platform.audit.title":              "Journal d'audit des releases",
  "platform.audit.empty":              "Aucune entree d'audit pour le moment.",
  "platform.audit.emptyHint":          "Chaque decision de release, utilisation d'identifiants et soumission est enregistree ici.",
  "platform.mobile.title":             "File d'attente des builds mobiles",
  "platform.mobile.empty":             "Aucun build mobile enregistre pour le moment.",
  "platform.mobile.emptyHint":         "Les builds AAB, APK et IPA apparaissent ici lorsqu'ils sont declenches depuis Deploy.",
  "platform.infra.title":              "Systemes d'infrastructure",
  "platform.infra.active":             "actif",

  // What's New page (nouveautes produit pour les clients)
  "whatsNew.eyebrow":         "Nouveautes du produit",
  "whatsNew.title":           "Nouveautes",
  "whatsNew.subtitle":        "Decouvrez ce que nous avons lance, ameliore et securise dans Nuvra.",
  "whatsNew.shippedOnly":     "Mises a jour deployees uniquement",
  "whatsNew.latest":          "Derniere nouveaute",
  "whatsNew.seeIncluded":     "Voir ce qui est inclus",
  "whatsNew.hideDetails":     "Masquer les details",
  "whatsNew.customerValue":   "Les changements sont decrits par le resultat qu’ils vous apportent, pas par l’implementation interne de Nuvra.",
  "whatsNew.search":          "Rechercher des mises a jour…",
  "whatsNew.filter":          "Filtrer",
  "whatsNew.all":             "Toutes",
  "whatsNew.releases":        "Nouveaux lancements",
  "whatsNew.improvements":    "Ameliorations",
  "whatsNew.security":        "Securite",
  "whatsNew.history":         "Historique du produit",
  "whatsNew.earlier":         "Mises a jour precedentes",
  "whatsNew.matching":        "Mises a jour correspondantes",
  "whatsNew.update":          "mise a jour",
  "whatsNew.updates":         "mises a jour",
  "whatsNew.noMatches":       "Aucune mise a jour ne correspond a ces filtres.",
  "whatsNew.clearFilters":    "Effacer les filtres",
  "whatsNew.helpPitch":       "Besoin d’aide sur une fonctionnalite ? Ouvrez le Centre d’aide ou contactez le support Nuvra.",
  "whatsNew.helpCenter":      "Centre d’aide",
  "whatsNew.support":         "Support",

  "whatsNew.customerOutcome": "Concu pour votre flux de travail",
  "whatsNew.filterUpdates":  "Filtrer les nouveautes du produit",

  // Command Center (dashboard creation surface)
  "cc.prompt":      "Que construisons-nous ?",
  "cc.eyebrow":     "Nouveau projet",
  "cc.chipsLabel":  "Partir d'une direction",
  "cc.flow.describe": "Décrire",
  "cc.flow.plan":    "Planifier",
  "cc.flow.build":   "Construire",
  "cc.flow.ship":    "Publier",
  "cc.promptNamed": "Que construisons-nous, {name} ?",
  "cc.subtitle":    "Decrivez le produit. Nuvra concoit le systeme.",
  "cc.degraded":    "Certains services degrades",
  "cc.outage":      "Interruption de service en cours",
  "cc.viewStatus":  "Voir le statut →",
  "cc.recentProjects": "Projets récents",
  "cc.recentProjectsHint": "Reprenez là où vous vous êtes arrêté.",
  "cc.viewAllProjects": "Tout voir",
  "cc.recentProjectsLoading": "Chargement des projets récents",
  "cc.projectsLoadErrorTitle": "Vos projets n'ont pas pu être chargés",
  "cc.projectsLoadErrorBody": "Vos projets sont en sécurité. Vérifiez votre connexion et réessayez.",
  "cc.retryProjects": "Réessayer",

  // Connectors page (integration catalog)
  "connectors.page.title":               "Intégrations",
  "connectors.page.subtitle":            "Connectez les services que votre projet utilise réellement. Les identifiants restent protégés dans le coffre chiffré de connecteurs de Nuvra et chaque connexion peut être testée avant usage.",
  "connectors.page.project":             "Projet",
  "connectors.page.selectProject":       "Sélectionner un projet",
  "connectors.page.noProject":           "Aucun projet sélectionné",
  "connectors.page.statConnected":       "Connectés",
  "connectors.page.statHealthy":         "Sains",
  "connectors.page.statAttention":       "Nécessitent une attention",
  "connectors.page.search":              "Rechercher une intégration, une capacité ou un fournisseur",
  "connectors.page.tabAll":              "Toutes",
  "connectors.page.tabConnected":        "Connectées",
  "connectors.page.tabAttention":        "Nécessitent une attention",
  "connectors.page.clearFilters":        "Effacer les filtres",
  "connectors.page.pickProjectTitle":    "Sélectionnez un projet pour connecter des services",
  "connectors.page.pickProjectBody":     "Vous pouvez parcourir le catalogue d'intégrations dès maintenant. Les changements de connexion sont toujours limités à un projet Nuvra réel.",
  "connectors.page.suggestedTitle":      "Suggérées pour ce projet",
  "connectors.page.suggestedBody":       "Déduites des capacités déjà présentes dans le schéma Nuvra du projet.",
  "connectors.page.notConnectedYet":     "Pas encore connectées",
  "connectors.page.noMatchTitle":        "Aucune intégration correspondante",
  "connectors.page.noMatchBody":         "Essayez un autre fournisseur, une autre capacité ou une autre catégorie. Nuvra n'invente jamais un connecteur pour remplir ce catalogue.",
  "connectors.page.securityTitle":       "Sécurité des connexions",
  "connectors.page.securityBody":        "Nuvra conserve les identifiants de connecteur dans son coffre protégé, garde les secrets côté serveur, limite la gestion des connecteurs au projet propriétaire et propose un vrai test de connectivité avant l'utilisation d'un service.",
  "connectors.page.suggestedBadge":      "Suggéré",

  // Collaboration page (live session environment)
  "collab.title":              "Collaboration",
  "collab.conn.connected":     "Connecte",
  "collab.conn.reconnecting":  "Reconnexion",
  "collab.conn.disconnected":  "Deconnecte",
  "collab.noProjectTitle":     "Aucun projet selectionne",
  "collab.noProjectBody":      "Ouvrez un projet et accedez a Collaboration pour voir l'etat de la session en direct.",
  "collab.urlParam":           "Parametre d'URL:",
  "collab.security":           "Observabilite de la securite",
  "collab.active":             "actif",
  "collab.participants.aria":  "Participants de la session",
  "collab.participants":       "Participants",
  "collab.activity.aria":      "Flux d'activite",
  "collab.activity":           "Activite en direct",
  "collab.ai.aria":            "Orchestration IA",
  "collab.ai":                 "Execution IA",

  // Help Center page (support + documentation)
  "help.eyebrow":          "Assistance",
  "help.title":            "Centre d'aide",
  "help.subtitle":         "Guides pour construire, deployer et exploiter vos produits sur Nuvra.",
  "help.searchPlaceholder":"Rechercher des articles d'aide...",
  "help.noResults":        "Aucun article ne correspond a \"{query}\". Essayez un autre terme ou ecrivez-nous a",
  "help.stillNeedHelp":    "Besoin d'aide ?",
  "help.emailPrefix":      "Ecrivez a notre equipe a",
  "help.emailSuffix":      ". Nous lisons chaque message.",
  "help.systemStatus":     "Etat du systeme",
  "help.whatsNew":         "Nouveautes",
  "help.startHere":         "Commencer ici",
  "help.documentation":     "Documentation",
  "help.guides":            "Guides",
  "help.searchResults":     "Resultats de recherche",
  "help.clearSearch":       "Effacer la recherche",
  "help.resources":         "Ressources",
  "help.searchHint":        "Rechercher",
  "help.gettingStarted.title": "Demarrage",
  "help.gettingStarted.q1": "Comment fonctionne Nuvra",
  "help.gettingStarted.a1": "Decrivez le produit que vous voulez construire. Nuvra cree un projet et concoit une application reelle et fonctionnelle avec des modeles de donnees, des pages, l'authentification et un stockage persistant. Chaque nouvelle demande de build affine ce meme produit.",
  "help.gettingStarted.q2": "Les projets sont l'objet central",
  "help.gettingStarted.a2": "Chaque produit que vous construisez est un projet avec son propre espace de travail : Apercu, Exigences, Architecture, Demandes de build, Builds, Fichiers, Environnements et Integrations. Soumettre un nouveau prompt depuis le tableau de bord demarre un nouveau projet distinct.",
  "help.building.title": "Construction et projets",
  "help.building.q1": "Soumettre une demande de build",
  "help.building.a1": "Ouvrez un projet et soumettez une demande de build decrivant le changement, la fonctionnalite, le correctif ou la mise a jour d'architecture souhaites. Nuvra effectue le travail et l'enregistre comme un registre de build montrant l'analyse, le travail realise ainsi que les fichiers et resultats produits.",
  "help.building.q2": "Examiner ce qui a ete construit",
  "help.building.a2": "Les sections Apercu, Architecture et Builds montrent les pages, les modeles de donnees, les routes et l'historique complet des builds de votre projet, generes a partir de votre application reelle, pas d'echantillons.",
  "help.deploying.title": "Deploiement et environnements",
  "help.deploying.q1": "Staging et Production",
  "help.deploying.a1": "Chaque projet dispose d'environnements Staging et Production. Les nouveaux builds arrivent automatiquement en Staging ; vous promouvez en Production quand vous etes pret et vous pouvez revenir a toute version verifiee precedente.",
  "help.deploying.q2": "Publier sur les stores d'applications",
  "help.deploying.a2": "Les projets mobiles peuvent etre compiles nativement pour Android et iOS et publies sur Google Play et l'App Store d'Apple, avec des deploiements progressifs que vous controlez et un traitement chiffre de vos identifiants de signature.",
  "help.integrations.title": "Integrations",
  "help.integrations.q1": "Connecter des services a un projet",
  "help.integrations.a1": "Connectez des integrations comme GitHub et votre base de donnees depuis l'espace de travail d'un projet. Les integrations sont limitees a ce seul projet, donc chaque produit gere ses propres connexions.",
  "help.billing.title": "Facturation et credits",
  "help.billing.q1": "Comment fonctionnent les credits",
  "help.billing.a1": "Construire consomme des credits. Votre solde actuel s'affiche dans l'en-tete de l'espace de travail et sur la page Facturation. Quand il devient faible, rechargez ou ameliorez votre forfait pour continuer a construire.",
  "help.billing.q2": "Forfaits et recharges",
  "help.billing.a2": "Choisissez un forfait adapte a votre usage ou achetez une recharge unique. Les credits sont appliques des que votre paiement est confirme.",
  "help.account.title": "Compte et securite",
  "help.account.q1": "Authentification a deux facteurs",
  "help.account.a1": "Activez l'authentification a deux facteurs par application d'authentification (TOTP) dans Parametres → Securite pour exiger une deuxieme etape a la connexion.",
  "help.account.q2": "Cles d'API",
  "help.account.a2": "Creez des cles d'API en lecture seule et a portee limitee dans Parametres → Cles d'API pour lire vos projets, builds et deploiements par programmation. Le secret est affiche une seule fois a la creation.",
  "help.account.q3": "Exporter vos donnees",
  "help.account.a3": "Telechargez a tout moment une copie JSON des donnees de votre compte dans Parametres → Centre d'export.",
  "help.account.q4": "Journaux d'audit",
  "help.account.a4": "Consultez un enregistrement immuable des actions effectuees sur votre compte et vos projets dans Parametres → Journaux d'audit.",

  // Magic-link sign-in landing page
  "magicLink.missingToken": "Le token manque a ce lien de connexion. Demandez-en un nouveau.",
  "magicLink.invalid":      "Ce lien de connexion est invalide ou a expire.",
  "magicLink.signingIn":    "Connexion en cours...",
  "magicLink.backToSignIn": "Retour a la connexion",

  // Share-link join page
  "join.missingToken":   "Le token manque a ce lien de partage.",
  "join.accessGranted":  "Vous disposez maintenant d'un acces {role}.",
  "join.invalid":        "Ce lien est invalide ou a expire.",
  "join.joining":        "Acces au projet...",
  "join.goToDashboard":  "Aller au tableau de bord",

  // Creator Studio (seller operating system)
  "cs.loading":            "Chargement de Creator Studio.",
  "cs.loadError":          "Creator Studio n'a pas pu etre charge. Reessayez dans un instant.",
  "cs.retry":              "Reessayer",
  "cs.eyebrow":            "Creator Studio",
  "cs.sellBlocked":        "La vente n'est pas disponible sur votre compte pour le moment.",
  "cs.planAndAbove":       "Forfait {tier} et superieurs",
  "cs.upsellTitle":        "Transformez ce que vous construisez en revenus.",
  "cs.upsellBody":         "Publiez sur le Marketplace Nuvra, conservez 80% de chaque vente et soyez paye selon un calendrier fixe et automatique.",
  "cs.becomeCreator":      "Devenir createur",
  "cs.noFees":             "Aucuns frais initiaux. Aucuns frais de publication.",
  "cs.keep80Title":        "Conservez 80% de chaque vente",
  "cs.keep80Sub":          "Votre part de chaque transaction",
  "cs.keep80Body":         "Vous conservez la majeure partie de chaque vente. Aucun cout initial et aucuns frais de publication, jamais.",
  "cs.paidTitle":          "Paye selon un calendrier fixe",
  "cs.paidSub":            "Automatique, deux fois par mois",
  "cs.scheduleWithPolicy": "Emis automatiquement le {dates}. Paiement minimum {amount}.",
  "cs.scheduleDefault":    "Emis automatiquement deux fois par mois.",
  "cs.earnings":           "Revenus",
  "cs.payouts":            "Paiements",
  "cs.yourListings":       "Vos annonces",
  "cs.affiliate":          "Affiliation",
  "cs.analytics":          "Analyses",
  "cs.verification":       "Verification",
  "cs.noEarningsYet":      "Aucun revenu pour le moment",
  "cs.next":               "Prochain {date}",
  "cs.automaticSchedule":  "Calendrier automatique",
  "cs.nPublished":         "{n} publiees",
  "cs.nothingPublished":   "Rien de publie pour le moment",
  "cs.active":             "Actif",
  "cs.notEnrolled":        "Non inscrit",
  "cs.available":          "Disponible",
  "cs.noAnalytics":        "Aucune analyse pour le moment",
  "cs.verified":           "Verifie",
  "cs.inReview":           "En cours d'examen",
  "cs.notVerified":        "Non verifie",
  "cs.insideEyebrow":      "Dans Creator Studio",
  "cs.whatYouUnlock":      "Ce que vous debloquez avec Pro",
  "cs.locked":             "Verrouille",
  "cs.nProjectsOne":       "{n} projet dans votre espace de travail",
  "cs.nProjectsOther":     "{n} projets dans votre espace de travail",
  "cs.becomeToPublish":    "Devenez createur pour publier votre travail sur le marketplace.",
  "cs.unlockPublishing":   "Debloquer la publication",
  "cs.openOnUpgrade":      "Ils s'ouvrent des que vous passez a un forfait superieur. Jusque-la, rien ici n'est actif.",
  "cs.controlPlaneTitle":  "Votre centre de controle createur",
  "cs.controlPlaneSub":    "Creez, publiez, developpez et gagnez, au meme endroit.",
  "cs.newProject":         "Nouveau projet",
  "cs.seeYourWork":        "Voyez votre travail sur le marketplace",
  "cs.browseCatalog":      "Parcourez le catalogue public comme le font les acheteurs.",
  "cs.openMarketplace":    "Ouvrir le Marketplace",
  "cs.yourPath":           "Votre parcours",
  "cs.journeyTitle":       "Creer, publier, developper, gagner",
  "cs.step.create":        "Creer",
  "cs.step.publish":       "Publier",
  "cs.step.verify":        "Verifier",
  "cs.step.earn":          "Gagner",
  "cs.step.createDesc":    "Construisez un projet dans votre espace de travail.",
  "cs.step.publishDesc":   "Mettez un build en vente sur le marketplace.",
  "cs.step.verifyDesc":    "Verifiez pour activer les paiements.",
  "cs.step.earnDesc":      "Les revenus arrivent apres votre premiere vente.",
  "cs.toast.published":      "Publie",
  "cs.toast.publishedBody":  "Votre annonce est maintenant en ligne.",
  "cs.toast.publishFail":    "Impossible de publier",
  "cs.toast.publishFailBody":"Verifiez les details de l'annonce et reessayez.",
  "cs.toast.removed":        "Supprime",
  "cs.toast.removedBody":    "L'annonce a ete supprimee.",
  "cs.toast.removeFail":     "Impossible de supprimer",
  "cs.toast.tryAgain":       "Veuillez reessayer.",
  "cs.listings":           "Annonces",
  "cs.publishFirstBody":   "Publiez votre premiere creation pour commencer a vendre sur le marketplace.",
  "cs.publishFirst":       "Publier votre premiere creation",
  "cs.nSold":              "{n} vendues",
  "cs.publish":            "Publier",
  "cs.remove":             "Supprimer",
  "cs.totalInstalls":      "Installations totales",
  "cs.totalRevenue":       "Revenus totaux",
  "cs.totalViews":         "Vues totales",
  "cs.avgRating":          "Note moyenne",
  "cs.noRatings":          "Aucune note pour le moment",
  "cs.earningsAppear":     "Les revenus apparaitront ici apres votre premiere vente.",
  "cs.totalEarned":        "Total gagne",
  "cs.pending":            "En attente",
  "cs.paidOut":            "Verse",
  "cs.nextPayout":         "Prochain paiement",
  "cs.onSchedule":         "Planifie",
  "cs.minimumPayout":      "Paiement minimum",
  "cs.issuedOn":           "Emis automatiquement le {dates}",
  "cs.issuedDefault":      "Emis automatiquement le 15 et le dernier jour de chaque mois",
  "cs.history":            "Historique",
  "cs.loadingDot":         "Chargement.",
  "cs.payoutHistoryEmpty": "L'historique des paiements apparaitra ici.",
  "cs.scheduled":          "Planifie",
  "cs.toast.refRequired":    "Reference requise",
  "cs.toast.refRequiredBody":"Ajoutez un id de session du fournisseur ou un lien que vous controlez.",
  "cs.toast.submitted":      "Envoye pour examen",
  "cs.toast.submittedBody":  "Un specialiste confirmera.",
  "cs.toast.submitFail":     "Impossible d'envoyer",
  "cs.verifiedCreator":    "Createur verifie",
  "cs.payoutsEnabled":     "Les paiements sont actives sur votre compte.",
  "cs.verifInReview":      "Verification en cours d'examen",
  "cs.confirmShortly":     "Nous confirmerons sous peu.",
  "cs.verifyToReceive":    "Verifiez pour recevoir des paiements",
  "cs.completeVerif":      "Completez la verification pour activer les paiements vers votre compte.",
  "cs.refPlaceholder":     "Id de session du fournisseur ou un lien que vous controlez",
  "cs.aria.kind":          "Type de provenance",
  "cs.aria.project":       "Selectionnez un projet",
  "cs.submit":             "Envoyer",
  "cs.toast.affJoined":      "Vous avez rejoint le programme d'affiliation",
  "cs.toast.joinFail":       "Impossible de rejoindre",
  "cs.toast.genFail":        "Impossible de generer le lien",
  "cs.toast.linkCopied":     "Lien copie",
  "cs.affEarnTitle":       "Gagnez en referant des ventes du marketplace",
  "cs.affEarnBody":        "Rejoignez le programme d'affiliation pour generer des liens de parrainage suivis.",
  "cs.joinProgram":        "Rejoindre le programme",
  "cs.clicks":             "Clics",
  "cs.conversions":        "Conversions",
  "cs.approved":           "Approuve",
  "cs.genLink":            "Generer un lien de parrainage",
  "cs.copyLink":           "Copier le lien",
  "cs.commissions":        "Commissions",
  "cs.publishingReadiness":"Pret a publier",

  // Project workspace - build stage labels (the real backend stages over SSE)
  "pw.stage.queued":        "Produit",
  "pw.stage.blueprint":     "Architecture",
  "pw.stage.schema":        "Donnees",
  "pw.stage.generate":      "Ingenierie",
  "pw.stage.security_scan": "Securite",
  "pw.stage.write_files":   "Ingenierie",
  "pw.stage.validate":      "QA",
  "pw.stage.deploy_pages":  "Publication",
  "pw.stage.complete":      "En direct",
  "pw.stage.intent":        "Reflexion",
  "pw.stage.constraints":   "Verification",
  "pw.stage.agents":        "Conception",
  "pw.stage.planning":      "Planification",
  "pw.stage.validation":    "Controle",
  "pw.stage.explanation":   "Raisonnement",
  "pw.stage.enriching":     "Synthese",

  // Project workspace - navigation
  "pw.nav.conversation": "Construction",
  "pw.nav.overview":     "Apercu",
  "pw.nav.requirements": "Exigences",
  "pw.nav.architecture": "Architecture",
  "pw.nav.builds":       "Constructions",
  "pw.nav.code":         "Fichiers",
  "pw.nav.analytics":                  "Analyses",
  "pw.analytics.window.today":         "Aujourd'hui",
  "pw.analytics.window.7d":            "7 jours",
  "pw.analytics.window.30d":           "30 jours",
  "pw.analytics.window.all":           "Depuis le debut",
  "pw.analytics.visitors":             "Visiteurs",
  "pw.analytics.pageViews":            "Pages vues",
  "pw.analytics.deployments":          "Deploiements",
  "pw.analytics.builds":               "Builds termines",
  "pw.analytics.successRate":          "Reussite des builds",
  "pw.analytics.previous":             "etait {n}",
  "pw.analytics.trend.new":            "nouveau",
  "pw.analytics.unavailable":          "Lecture impossible",
  "pw.analytics.noBuildsYet":          "Aucun build termine",
  "pw.analytics.none":                 "Aucune activite",
  "pw.analytics.noneHint":             "Des que votre app est deployee et visitee, les visites apparaissent ici.",
  "pw.analytics.failed":               "Analyses indisponibles",
  "pw.analytics.failedHint":           "Probleme d'affichage, pas de votre app. Reessayez bientot.",
  "pw.analytics.degraded":             "Certains chiffres n'ont pas pu etre lus, cette vue est incomplete. Les nombres affiches sont reels; les manquants sont signales.",
  "pw.analytics.computedAt":           "Mesure a {time}",
  "pw.nav.environments": "Environnements",
  "pw.nav.memory":       "Memoire",
  "pw.nav.agent":        "Agent",
  "pw.nav.deployments":  "Deploiements",
  "pw.settings.title": "Paramètres du projet",
  "pw.settings.subtitle": "Gérez l'identité et la visibilité de ce projet sans quitter son espace de travail.",
  "pw.settings.nameLabel": "Nom du projet",
  "pw.settings.nameHint": "Ce nom apparaît dans l'espace de travail et la liste des projets.",
  "pw.settings.publicLabel": "Rendre le projet public",
  "pw.settings.publicHint": "Les projets publics peuvent être ouverts via leur URL d'exécution partagée. Les projets privés restent contrôlés par leur propriétaire.",
  "pw.settings.identityTitle": "Identité du projet",
  "pw.settings.identityBody": "ID canonique du projet : {id}",
  "pw.settings.save": "Enregistrer les modifications",
  "pw.settings.saving": "Enregistrement…",
  "pw.settings.saved": "Paramètres du projet enregistrés",
  "pw.settings.savedBody": "L'identité et la visibilité de votre projet sont à jour.",
  "pw.settings.saveFailed": "Impossible d'enregistrer les paramètres du projet",
  "pw.settings.saveFailedBody": "Vos modifications n'ont pas été enregistrées. Réessayez.",
  "pw.nav.integrations": "Integrations",
  "pw.nav.settings":     "Parametres",
  "pw.nav.group.product":  "Votre produit",
  "pw.nav.group.changes":  "Modifications",
  "pw.nav.group.ship":     "Publier",
  "pw.mode.stream":            "Flux",
  "pw.mode.preview":           "Apercu",
  "pw.mode.files":             "Fichiers",
  "pw.nav.preview":        "Apercu",
  "pw.nav.previewSoon":    "apres le premier build",
  "pw.nav.previewAfterBuild": "Disponible apres votre premier build",
  "pw.nav.allProjects":  "Tous les projets",
  "pw.nav.buildsOne":    "{n} construction",
  "pw.nav.buildsOther":  "{n} constructions",
  "pw.nav.originalIntent": "Votre brief",
  "pw.plan.button":      "Planifier",
  "pw.plan.buttonTitle": "Voyez ce que ce prompt construira et son cout, gratuitement, avant de lancer",
  "pw.plan.cost":        "Le build utilise {n} credits",
  "pw.plan.buildThis":   "Construire ceci",
  "pw.plan.dismiss":     "Fermer le plan",
  "pw.plan.failed":      "Impossible de deriver le plan. Reessayez.",
  "pw.plan.entities":    "Donnees",
  "pw.plan.workflows":   "Processus",
  "pw.plan.security.standard": "Isolation des donnees par utilisateur avec securite au niveau des lignes",
  "pw.plan.security.elevated": "Securite au niveau des lignes, audit et roles",
  "pw.plan.security.critical": "Isolation stricte, audit complet et roles",
  "pw.defaultName":      "Nouveau projet",
  "pw.projectLoading": "Ouverture de votre projet",
  "pw.projectLoadingBody": "Chargement du projet et restauration de son contexte de travail.",
  "pw.projectNotFoundTitle": "Projet introuvable",
  "pw.projectNotFoundBody": "Ce projet a peut-être été déplacé, supprimé ou vous n’y avez pas accès.",
  "pw.projectLoadErrorTitle": "Impossible de charger le projet",
  "pw.projectLoadErrorBody": "Votre projet n’a pas été modifié. Vérifiez votre connexion et réessayez.",
  "pw.backToProjects": "Retour aux projets",
  "pw.retryProject": "Réessayer",

  // Project workspace - files panel
  "pw.files.noBuild":      "Aucune construction pour le moment",
  "pw.files.noBuildHint":  "Les fichiers apparaitront ici une fois la premiere construction terminee.",
  "pw.files.pagesN":       "Pages ({n})",
  "pw.files.componentsN":  "Composants ({n})",
  "pw.files.dataModelsN":  "Modeles de donnees ({n})",
  "pw.files.moreComponents": "+{n} composants supplementaires",

  // Project workspace - overview panel
  "pw.overview.statusBuilding": "Construction",
  "pw.overview.statusFailed":   "Derniere construction echouee",
  "pw.overview.statusBuilt":    "Construit",
  "pw.overview.statusDeployed": "Deploye",
  "pw.overview.statusNotBuilt": "Pas encore construit",
  "pw.overview.stat.pages":      "Pages",
  "pw.overview.stat.components": "Composants",
  "pw.overview.stat.dataModels": "Modeles de donnees",
  "pw.overview.stat.builds":     "Constructions",
  "pw.overview.openLiveApp":     "Ouvrir l'application en direct",
  "pw.overview.originalRequirement": "Exigence initiale",

  // Project workspace - architecture panel
  "pw.arch.none":          "Aucune architecture pour le moment",
  "pw.arch.noneHint":      "Elle apparait une fois la premiere construction terminee.",
  "pw.arch.whyBuilt":      "Pourquoi Nuvra a construit ceci",
  "pw.arch.fieldsN":       "({n} champs)",
  "pw.arch.assumptions":   "Hypotheses de Nuvra",
  "pw.arch.youCanChange":  "Vous pouvez modifier : {areas}.",
  "pw.arch.whatChanged":   "Ce qui a change dans cette construction",
  "pw.arch.routesN":       "Routes ({n})",
  "pw.arch.dataLayerN":    "Couche de donnees ({n} modeles)",
  "pw.arch.capabilitiesN": "Capacites ({n})",
  "pw.arch.componentsGeneratedOne":   "{n} composant genere.",
  "pw.arch.componentsGeneratedOther": "{n} composants generes.",
  "pw.arch.showComponentsOnPage": "Afficher les composants de cette page",
  "pw.arch.noComponentsOnPage":   "Aucun composant sur cette page",
  "pw.arch.toggleComponents":     "Basculer les composants",
  "pw.arch.renamed":       "renommee",
  "pw.arch.hiddenNextBuild": "masquee a la prochaine construction",
  "pw.arch.hidden":        "masque",
  "pw.arch.saveName":      "Enregistrer le nom",
  "pw.arch.savePageName":  "Enregistrer le nom de la page",
  "pw.arch.renameThisPage": "Renommer cette page",
  "pw.arch.renamePage":    "Renommer la page",
  "pw.arch.hiddenRestore": "Masquee : retiree de l'application a la prochaine construction. Cliquez pour restaurer.",
  "pw.arch.hidePage":      "Masquer cette page pour que la prochaine construction la retire de l'application",
  "pw.arch.showPage":      "Afficher la page",
  "pw.arch.hidePageLabel": "Masquer la page",
  "pw.arch.lockedKeep":    "Verrouillee : les prochaines constructions conservent cette page telle quelle",
  "pw.arch.lockPage":      "Verrouiller cette page pour que les prochaines constructions ne la modifient pas",
  "pw.arch.unlockPage":    "Deverrouiller la page",
  "pw.arch.lockPageLabel": "Verrouiller la page",
  "pw.arch.compHiddenRestore": "Masque : retire a la prochaine construction. Cliquez pour restaurer.",
  "pw.arch.hideComponent": "Masquer ce composant de l'application",
  "pw.arch.showComponent": "Afficher le composant",
  "pw.arch.hideComponentLabel": "Masquer le composant",

  // Project workspace - requirements panel
  "pw.req.originalRequirement": "Exigence initiale",
  "pw.req.noOriginal":     "Aucune exigence initiale enregistree pour ce projet.",
  "pw.req.engineeringN":   "Demandes d'ingenierie ({n})",
  "pw.req.noRequests":     "Aucune demande de changement pour le moment. Soumettez-en une dans Demandes de construction.",
  "pw.req.showingRecent":  "Les {shown} plus recentes",
  "pw.req.constraintsN":   "Contraintes ({n})",
  "pw.req.decisionsN":     "Decisions ({n})",

  // Project workspace - environments panel
  "pw.env.staging":        "Preproduction",
  "pw.env.production":     "Production",
  "pw.env.deployed":       "Deploye",
  "pw.env.notDeployed":    "Non deploye",
  "pw.env.noUrl":          "Aucune URL",
  "pw.env.none":           "Aucun environnement pour le moment",
  "pw.env.noneHint":       "Les environnements apparaissent une fois votre premiere construction deployee.",
  "pw.env.build":          "Construction :",
  "pw.env.lastDeployment": "Dernier deploiement :",
  "pw.env.envVars":        "Variables d'environnement :",
  "pw.env.buildNone":      "aucune",
  "pw.env.never":          "jamais",

  // Project workspace - activity panel
  "pw.activity.none":      "Aucune activite pour le moment",
  "pw.activity.noneHint":  "L'historique des constructions apparaitra ici.",
  "pw.activity.buildFailed": "Construction echouee",

  // Project workspace - memory panel
  "pw.mem.brain.architecture": "Architecture",
  "pw.mem.brain.decision":     "Decisions",
  "pw.mem.brain.constraint":   "Contraintes",
  "pw.mem.brain.naming":       "Nommage",
  "pw.mem.brain.rationale":    "Justification",
  "pw.mem.brain.history":      "Historique",
  "pw.mem.title":          "Memoire du projet",
  "pw.mem.subtitle":       "Tout ce que Nuvra sait sur ce projet, accumule sur toutes les constructions.",
  "pw.mem.originalIntent": "Intention initiale",
  "pw.mem.noPrompt":       "Aucun prompt enregistre",
  "pw.mem.builds":         "Constructions",
  "pw.mem.pages":          "Pages",
  "pw.mem.applicationRoutes": "Routes de l'application",
  "pw.mem.detectedCapabilities": "Capacites detectees",
  "pw.mem.emptyBrain":     "Au fur et a mesure que vous construisez, Nuvra retient ici les decisions, l'architecture, le nommage et les contraintes de ce produit, et les transmet a chaque construction future.",

  // Project workspace - header and status
  "pw.header.clickToRename": "Cliquez pour renommer",
  "pw.header.projectName": "Nom du projet",
  "pw.identity.edit":               "Modifier l'identite du produit",
  "pw.identity.title":              "Identite du produit",
  "pw.identity.productName":        "Nom du produit",
  "pw.identity.saveName":           "Enregistrer",
  "pw.identity.regenerateLogo":     "Nouveau logo",
  "pw.identity.confirmedNote":      "Vous avez choisi ce nom. Nuvra ne le remplacera jamais.",
  "pw.identity.generatedNote":      "Genere depuis votre produit. Modifiez-le et il devient permanent.",
  "pw.identity.saveFailed":         "Impossible d'enregistrer l'identite du produit. Reessayez.",
  "pw.status.building":    "Construction...",
  "pw.status.buildFailed": "Construction echouee",
  "pw.status.built":       "Construit",
  "pw.status.latestFailed": "La derniere construction a echoue",
  "pw.status.draft":       "Brouillon",
  "pw.status.ready":       "Pret",
  "pw.projectMenu.open": "Ouvrir le menu du projet",
  "pw.projectMenu.project": "Espace de travail du projet",
  "pw.projectMenu.overview": "Vue d'ensemble",
  "pw.projectMenu.tools": "Outils du projet",
  "pw.projectMenu.rename": "Renommer le projet",
  "pw.projectMenu.allProjects": "Tous les projets",
  "pw.projectMenu.accountSettings": "Parametres du compte",
  "pw.header.openLiveApp": "Ouvrir l'application en direct",
  "pw.header.manage":      "Gerer",
  "pw.header.manageTitle": "Gerer ce produit",
  "pw.header.codeTitle":   "Parcourir et telecharger le code de votre projet",
  "pw.header.projectCode": "Code du projet",
  "pw.header.code":        "Code",
  "pw.header.manageLess":  "Moins",
  "pw.header.forkTitle":   "Bifurquer ce produit vers un nouveau projet",
  "pw.header.fork":        "Bifurquer",
  "pw.header.notesTitle":  "Notes d'ingenierie",
  "pw.header.notes":       "Notes",
  "pw.header.notesPreset.explain":  "Explique ce projet",
  "pw.header.notesPreset.next":     "Que devrais-je construire ensuite ?",
  "pw.header.notesPreset.fail":     "Pourquoi ma derniere construction a-t-elle echoue ?",
  "pw.header.notesPreset.analyze":  "Analyse mon architecture",
  "pw.header.notesPreset.missing":  "Quelles fonctionnalites me manque-t-il ?",
  "pw.header.askPlaceholder": "Posez une question sur l'architecture, les constructions ou l'etat de ce projet...",
  "pw.header.send":        "Envoyer",
  "pw.header.goLive":      "Publier",
  "pw.header.clearNotes":  "Effacer les notes d'ingenierie",

  // Project workspace - empty state and composer
  "pw.empty.title":        "Que construisons-nous ensuite ?",
  "pw.empty.body":         "Decrivez une fonctionnalite, une correction ou un changement de design. Nuvra l'integre a votre application.",
  "pw.mobile.readyTap":    "Votre produit est pret, touchez pour ouvrir",
  "pw.mobile.previousTap": "La derniere construction a echoue. Ouvrez la derniere version fonctionnelle",
  "pw.mobile.tapToOpen":   "Touchez pour ouvrir votre produit",
  "pw.mobile.draftTap":    "Ouvrir le brouillon, ajoutez du credit pour le construire vraiment",
  "pw.dup.notice":         "Vous avez deja construit ceci. Ouvrez la version existante ou construisez-la de nouveau.",
  "pw.dup.openExisting":   "Ouvrir l'existant",
  "pw.dup.buildAgain":     "Construire de nouveau",
  "pw.dup.dismiss":        "Ignorer",
  "pw.composer.placeholderComplete": "Demandez a Nuvra de construire, modifier ou ameliorer...",
  "pw.composer.placeholderBuilding": "Nuvra construit. Ecrivez votre prochain changement pendant ce temps...",
  "pw.composer.placeholderNew":      "Decrivez le produit a construire...",
  "pw.composer.sessionActive": "Construction",
  "pw.composer.ariaLabel": "Compositeur de messages de l'espace de travail",
  "pw.composer.dropToAttach": "Deposez le fichier pour joindre",
  "pw.composer.removeImage": "Retirer l'image",
  "pw.composer.hint":      "Entree pour envoyer",

  // Project workspace - preview panel
  "pw.preview.appDeployed": "Application deployee",
  "pw.preview.builtRunnableTitle": "Votre projet est pret",
  "pw.preview.builtRunnableBody": "Il s'agit d'une version jouable qui s'execute comme sa propre page. Deployez-la pour jouer en direct, ou ouvrez le code pour l'inspecter, le modifier et le telecharger.",
  "pw.preview.appDeployedBody": "Votre application est en direct. Ouvrez-la pour interagir, partager et demander des modifications.",
  "pw.preview.openLiveApp": "Ouvrir l'application en direct",
  "pw.preview.prevBuildTitle": "Version precedente",
  "pw.preview.prevBuildBody": "La derniere compilation a echoue. Ce lien ouvre votre derniere version reussie, pas celle qui a echoue.",
  "pw.preview.live":       "En direct",
  "pw.preview.previousVersion": "Version precedente operationnelle",
  "pw.preview.draft":      "Brouillon",
  "pw.preview.switchToMobile":  "Passer en vue mobile",
  "pw.preview.switchToDesktop": "Passer en vue bureau",
  "pw.preview.mobileView":  "Vue mobile",
  "pw.preview.desktopView": "Vue bureau",
  "pw.preview.copyShareable": "Copier le lien de partage",
  "pw.preview.copied":     "Copie",
  "pw.preview.share":      "Partager",
  "pw.preview.openProduct": "Ouvrez votre produit",
  "pw.preview.open":       "Ouvrir",
  "pw.preview.takeLive":   "Mettez votre produit en direct",
  "pw.preview.goLive":     "Publier",
  "pw.preview.reloadProduct": "Rechargez votre produit",
  "pw.preview.reload":     "Recharger",
  "pw.preview.close":      "Fermer",
  "pw.preview.lastBuildFailed": "Derniere compilation echouee. Affichage de la version precedente.",
  "pw.preview.rebuild":    "Recompiler",

  // Project workspace - status text and toasts
  "pw.build.understanding": "Comprehension de votre demande...",
  "pw.build.resumeRequest": "Continuez a construire ce projet depuis l'arret de la generation precedente.",
  "pw.build.sending":       "Envoi a Nuvra AI...",
  "pw.build.designing":     "Planification de la construction...",
  "pw.build.acuExhausted":  "Credits epuises.",
  "pw.build.acuTopUp":      "Vos credits gratuits sont epuises. Rechargez pour continuer a construire.",
  "pw.build.notAuthed":     "Non authentifie. Veuillez vous reconnecter.",
  "pw.build.notAuthedShort": "Non authentifie",
  "pw.build.productLive":   "Votre produit est en direct",
  "pw.build.productReady":  "Votre produit est pret",
  "pw.build.scaffoldReady":  "Un brouillon de depart est pret. Ajoutez du credit pour concevoir le vrai produit.",
  "pw.build.scaffoldTitle":  "Ceci est un brouillon, pas votre produit final",
  "pw.build.scaffoldBody":   "La generation reelle de votre demande necessite du credit IA. Ajoutez du credit et Nuvra concevra le modele de donnees, les pages et la logique complets de ce que vous avez decrit.",
  "pw.build.scaffoldCta":    "Ajoutez du credit pour construire vraiment",
  "pw.build.engineeredLive": "Rapport d'ingénierie prêt",
  "pw.build.engineered":    "Votre application est concue.",
  "bfr.failed": "Construction echouee",
  "bfr.creditBlocked": "Cette construction necessite plus de credits",
  "bfr.stalled": "Nuvra n'a pas pu confirmer le resultat final de la construction",
  "bfr.running": "Votre construction est toujours en cours",
  "bfr.completed": "Construction terminee",
  "bfr.retry": "Reessayer",
  "bfr.topUp": "Ajouter des credits",
  "bfr.refresh": "Verifier le statut",
  "bfr.refunded": "Les credits reserves ont ete rembourses pour cette tentative echouee.",
  "bfr.notUsed": "Aucun credit n'a ete utilise pour cette tentative.",
  "pw.build.failed":        "Construction echouee",
  "pw.build.stillBuilding": "Construction en cours. Votre construction se poursuit sur le serveur et apparaitra ici sous peu.",
  "pw.build.stalled": "Cette construction ne s'est pas terminee a temps.",
  "pw.build.stalledHint": "Nuvra n'a pas encore pu confirmer le resultat final. Votre travail est conserve. Verifiez le statut avant de lancer une autre construction.",
  "pw.ask.analyzing":       "Analyse...",
  "pw.ask.unableNow":       "Je ne peux pas repondre pour le moment. Veuillez reessayer.",
  "pw.ask.unableConn":      "Impossible de repondre. Verifiez votre connexion et reessayez.",

  /* Settings - two-factor authentication panel (TOTP via Supabase Auth) */
  "settings.twofactor.title":                  "Authentification a deux facteurs",
  "settings.twofactor.loading":                "Chargement",
  "settings.twofactor.scanInstructions":       "Scannez ce code QR avec une application d'authentification (Google Authenticator, 1Password, Authy), puis saisissez le code a 6 chiffres pour confirmer.",
  "settings.twofactor.qrAlt":                  "Code QR a deux facteurs",
  "settings.twofactor.manualKey":              "Ou saisissez cette cle manuellement",
  "settings.twofactor.codeLabel":              "Code a 6 chiffres",
  "settings.twofactor.verifying":              "Verification",
  "settings.twofactor.confirmOn":              "Confirmer et activer",
  "settings.twofactor.cancel":                 "Annuler",
  "settings.twofactor.onTitle":                "L'authentification a deux facteurs est active",
  "settings.twofactor.onDesc":                 "Un code de votre application d'authentification est requis a la connexion.",
  "settings.twofactor.disable":                "Desactiver",
  "settings.twofactor.addStep":                "Ajoutez une deuxieme etape a la connexion",
  "settings.twofactor.addStepDesc":            "Utilisez une application d'authentification pour generer des codes a usage unique.",
  "settings.twofactor.starting":               "Demarrage",
  "settings.twofactor.enable":                 "Activer",
  "settings.twofactor.toast.loadFailTitle":    "Impossible de charger l'etat de la 2FA",
  "settings.twofactor.toast.startFailTitle":   "Impossible de demarrer la configuration de la 2FA",
  "settings.twofactor.toast.tryAgain":         "Reessayez.",
  "settings.twofactor.toast.verifyFailTitle":  "Echec de la verification",
  "settings.twofactor.toast.incorrectTitle":   "Code incorrect",
  "settings.twofactor.toast.onTitle":          "Authentification a deux facteurs activee",
  "settings.twofactor.toast.onBody":           "Votre compte requiert desormais un code a la connexion.",
  "settings.twofactor.toast.offTitle":         "Authentification a deux facteurs desactivee",
  "settings.twofactor.toast.offBody":          "Les codes ne sont plus requis a la connexion.",
  "settings.twofactor.toast.disableFailTitle": "Impossible de desactiver la 2FA",
  "settings.twofactor.toast.secretCopiedTitle":"Cle copiee",
  "settings.twofactor.toast.secretCopiedBody": "Collez-la dans votre application d'authentification.",

  /* Project workspace - marketplace "recommended to complete this product" block */
  "pw.rec.title":             "Recommande pour completer ce produit",
  "pw.rec.subtitle":          "Lacunes que Nuvra a detectees dans ce que vous avez construit. Ce sont des suggestions, pas des changements.",
  "pw.rec.noMatches":         "Aucun actif correspondant pour le moment",
  "pw.rec.assetSingular":     "actif",
  "pw.rec.assetsPlural":      "actifs",
  "pw.rec.fromFree":          "gratuit",
  "pw.rec.fromPrice":         "a partir de ${price}",
  "pw.rec.findInMarketplace": "Trouver sur la place de marche",
  "pw.rec.free":              "Gratuit",
  "pw.rec.compatible":        "compatible",
  "pw.rec.checkCompat":       "verifier compat",

  /* Public preview pages (SharedPreview, SlugPreview) and the auth callback */
  "preview.notAvailable":   "Cette application n'est pas disponible en apercu public.",
  "preview.linkProblem":    "Le lien est peut-etre prive, expire ou incorrect.",
  "auth.completingSignIn":  "Finalisation de la connexion...",

  // Settings - Profile panel
  "settings.profile.infoTitle":      "Informations du profil",
  "settings.profile.avatarAlt":      "Avatar",
  "settings.profile.name":           "Nom",
  "settings.profile.email":          "E-mail",
  "settings.profile.saving":         "Enregistrement",
  "settings.profile.save":           "Enregistrer les modifications",
  "settings.profile.onboardingTitle":"Prise en main",
  "settings.profile.onboardingBody": "Rejouez la visite de bienvenue pour redecouvrir les fonctions cles ou explorer a nouveau la plateforme.",
  "settings.profile.restartTour":    "Relancer la visite de prise en main",
  "settings.profile.toast.errorTitle":   "Erreur",
  "settings.profile.toast.updatedTitle": "Profil mis a jour",
  "settings.profile.toast.updatedBody":  "Votre nom a ete enregistre.",

  // Settings - Security panel
  "settings.security.changeTitle":     "Changer le mot de passe",
  "settings.security.currentPassword": "Mot de passe actuel",
  "settings.security.newPassword":     "Nouveau mot de passe",
  "settings.security.confirm":         "Confirmer",
  "settings.security.mismatchInline":  "Les mots de passe ne correspondent pas.",
  "settings.security.updating":        "Mise a jour",
  "settings.security.update":          "Mettre a jour le mot de passe",
  "settings.security.toast.mismatchTitle": "Les mots de passe ne correspondent pas",
  "settings.security.toast.mismatchBody":  "Les nouveaux mots de passe ne correspondent pas.",
  "settings.security.toast.shortTitle":    "Trop court",
  "settings.security.toast.shortBody":     "Le mot de passe doit comporter au moins 8 caracteres.",
  "settings.security.toast.errorTitle":    "Erreur",
  "settings.security.toast.updatedTitle":  "Mot de passe mis a jour",
  "settings.security.toast.updatedBody":   "Votre mot de passe a ete change.",

  "settings.security.sessions.title":    "Sessions actives",
  "settings.security.sessions.empty":    "Aucune autre session active.",
  "settings.security.sessions.current":  "Session actuelle",
  "settings.security.sessions.revoke":   "Se deconnecter",
  "settings.security.sessions.revoking": "Deconnexion",
  "settings.security.sessions.browser":  "Navigateur",
  "settings.security.sessions.device":   "Appareil",
  "settings.security.sessions.lastSeen": "Derniere activite",
  "settings.security.sessions.location": "Emplacement",

  "settings.security.devices.title": "Appareils de confiance",
  "settings.passkeys.title": "Cles d'acces et biometrie",
  "settings.security.posture.title": "Posture de securite",
  "settings.security.posture.mfa": "Double facteur",
  "settings.security.posture.on": "Actif",
  "settings.security.posture.off": "Inactif",
  "settings.security.posture.passkeys": "Cles d'acces",
  "settings.security.posture.devices": "Appareils actifs",
  "settings.security.posture.signIns": "Connexions (30j)",
  "settings.security.posture.warnings": "Alertes (30j)",
  "settings.security.posture.window": "Agrege depuis vos evenements de connexion, appareils, cles d'acces et chronologie de securite des 30 derniers jours.",
  "settings.passkeys.intro": "Verification avec une cle d'acces stockee sur votre appareil. Face ID, Windows Hello et l'empreinte digitale s'inscrivent via la meme invite quand votre appareil le permet.",
  "settings.passkeys.unsupported": "Ce navigateur ne prend pas en charge les cles d'acces.",
  "settings.passkeys.namePlaceholder": "Nommez cette cle (ex. Mon telephone)",
  "settings.passkeys.add": "Ajouter une cle",
  "settings.passkeys.test": "Verifier maintenant",
  "settings.passkeys.added": "Cle d'acces ajoutee.",
  "settings.passkeys.verified": "Identite verifiee avec votre cle d'acces.",
  "settings.passkeys.failed": "La ceremonie de cle d'acces n'a pas abouti.",
  "settings.passkeys.empty": "Aucune cle d'acces pour le moment. Ajoutez-en une pour verifier les actions sensibles.",
  "settings.passkeys.unnamed": "Cle sans nom",
  "settings.passkeys.addedOn": "Ajoutee le",
  "settings.passkeys.lastUsed": "derniere utilisation",
  "settings.passkeys.remove": "Supprimer la cle",
  "settings.security.devices.intro": "Les appareils connectes a votre compte. Faites confiance aux votres; revoquez tout appareil inconnu.",
  "settings.security.devices.empty": "Aucun appareil enregistre pour le moment. Ils apparaitront apres votre prochaine connexion.",
  "settings.security.devices.trusted": "De confiance",
  "settings.security.devices.revoked": "Revoque",
  "settings.security.devices.lastSeen": "Vu pour la derniere fois",
  "settings.security.devices.trust": "Faire confiance",
  "settings.security.devices.untrust": "Retirer la confiance",
  "settings.security.devices.revoke": "Revoquer",
  "settings.security.timeline.title": "Evenements de securite",
  "settings.security.timeline.empty": "Aucun evenement de securite pour le moment.",

  "settings.security.strength.label":       "Force du mot de passe",
  "settings.security.strength.weak":        "Faible",
  "settings.security.strength.fair":        "Passable",
  "settings.security.strength.strong":      "Fort",
  "settings.security.strength.very_strong": "Tres fort",

  "settings.security.providers.title":      "Methodes de connexion",
  "settings.security.providers.desc":       "Gerez comment vous vous connectez. Au moins une methode doit rester active.",
  "settings.security.providers.linked":     "Lie",
  "settings.security.providers.unlink":     "Dissocier",
  "settings.security.providers.unlinking":  "Dissociation",
  "settings.security.providers.unlinkError": "Impossible de dissocier. Conservez au moins une methode de connexion active.",
  "settings.security.providers.linkedAt":   "Lie",
  "settings.security.providers.addGoogle":  "Connecter Google",
  "settings.security.providers.addGithub":  "Connecter GitHub",

  "settings.security.email.title":       "Adresse e-mail",
  "settings.security.email.verified":    "Verifiee",
  "settings.security.email.unverified":  "Non verifiee",
  "settings.security.email.resend":      "Renvoyer la verification",
  "settings.security.email.resent":      "E-mail de verification envoye.",
  "settings.security.email.resentError": "Impossible d'envoyer l'e-mail de verification.",

  "settings.security.recovery.title":       "E-mail de recuperation",
  "settings.security.recovery.desc":        "E-mail de secours pour recuperer votre compte si vous perdez acces a votre adresse principale.",
  "settings.security.recovery.placeholder": "secours@exemple.com",
  "settings.security.recovery.save":        "Enregistrer l'e-mail de recuperation",
  "settings.security.recovery.saving":      "Enregistrement",
  "settings.security.recovery.saved":       "E-mail de recuperation enregistre.",
  "settings.security.recovery.error":       "Impossible d'enregistrer l'e-mail de recuperation.",

  "settings.security.recs.title":    "Recommandations de securite",
  "settings.security.recs.mfa":      "Authentification a deux facteurs activee",
  "settings.security.recs.email":    "E-mail principal verifie",
  "settings.security.recs.recovery": "E-mail de recuperation configure",
  "settings.security.recs.allGood":  "Votre compte respecte toutes les recommandations de securite.",
  "settings.security.recs.fix":      "Corriger",

  "settings.notifications.important": "Important",
  "settings.notifications.summary.categories": "Catégories actives",
  "settings.notifications.summary.channels": "Canaux actifs",
  "settings.notifications.summary.behavior": "Mode de diffusion",
  "settings.notifications.summary.behaviorBody": "Les préférences s'appliquent à chaque type de notification et canal de diffusion. Les changements sont enregistrés sur votre compte immédiatement.",
  "settings.notifications.saveError": "Cette modification n'a pas pu être enregistrée.",
  "settings.notifications.dismiss": "Fermer",
  "language.summary.available": "Disponibles maintenant",
  "language.summary.note": "Couverture des traductions",
  "language.summary.noteBody": "Seules les langues disposant d'un catalogue Nuvra complet peuvent être sélectionnées. Cela évite qu'un changement apparent ne revienne silencieusement à l'anglais.",
  "language.unavailableTitle": "Autres langues de l'interface",
  "language.unavailableBody": "Ces langues sont définies dans Nuvra mais ne disposent pas encore d'un catalogue d'interface complet. Elles resteront indisponibles jusqu'à ce que leurs traductions soient prêtes.",
  // Settings - Notifications panel
  "settings.notifications.title":     "Préférences de notification",
  "settings.notifications.loading":   "Chargement",
  "settings.notifications.description": "Choisissez où Nuvra envoie chaque type de mise à jour de compte et de produit. Les changements sont enregistrés automatiquement.",
  "settings.notifications.colName":   "Notification",
  "settings.notifications.colInApp":  "Dans l'app",
  "settings.notifications.colEmail":  "E-mail",
  "settings.notifications.saving":    "Enregistrement",
  "settings.notifications.saved":     "Enregistré",
  "settings.notifications.recommended": "Recommandé",
  "settings.notifications.securityNote": "Les messages de sécurité et de facturation peuvent être importants pour protéger votre compte ou finaliser une transaction. Les préférences contrôlent les canaux de diffusion disponibles ; elles ne désactivent pas les opérations de compte requises.",
  "settings.notifications.type.product_updates":     "Mises à jour du produit",
  "settings.notifications.type.deployment_alerts":   "Alertes de déploiement",
  "settings.notifications.type.marketplace_updates": "Nouveautés du marketplace",
  "settings.notifications.type.security_alerts":     "Alertes de sécurité",
  "settings.notifications.type.weekly_digest":       "Résumé hebdomadaire",
  "settings.notifications.type.team_mentions":       "Mentions de l'équipe",
  "settings.notifications.type.support_alerts":       "Mises à jour du support",
  "settings.notifications.type.billing_alerts":       "Alertes de facturation",
  "settings.notifications.desc.product_updates":     "Nouveautés Nuvra, améliorations et annonces produit.",
  "settings.notifications.desc.deployment_alerts":   "Échecs de déploiement, confirmations de fin et événements de production.",
  "settings.notifications.desc.marketplace_updates": "Nouveautés du marketplace, modèles et changements de disponibilité.",
  "settings.notifications.desc.security_alerts":     "Connexions, authentification, récupération et autres événements de sécurité.",
  "settings.notifications.desc.weekly_digest":       "Un résumé périodique de l'activité importante et des mises à jour produit.",
  "settings.notifications.desc.team_mentions":       "Lorsque des membres de l'équipe vous mentionnent ou vous impliquent directement.",
  "settings.notifications.desc.support_alerts":       "Réponses et changements de statut du support Nuvra.",
  "settings.notifications.desc.billing_alerts":       "Paiements, abonnements, crédits et événements de facturation.",

  // Settings - API Keys panel
  "settings.apiKeys.createdBanner":  "Cle creee. Copiez-la maintenant, elle ne sera plus affichee.",
  "settings.apiKeys.dismiss":        "Ignorer",
  "settings.apiKeys.createTitle":    "Creer une cle d'API",
  "settings.apiKeys.keyName":        "Nom de la cle",
  "settings.apiKeys.keyNamePlaceholder": "p. ex. Pipeline CI/CD",
  "settings.apiKeys.scopes":         "Portees",
  "settings.apiKeys.creating":       "Creation",
  "settings.apiKeys.create":         "Creer la cle",
  "settings.apiKeys.activeTitle":    "Cles actives",
  "settings.apiKeys.loading":        "Chargement",
  "settings.apiKeys.empty":          "Aucune cle d'API pour l'instant. Creez-en une ci-dessus.",
  "settings.apiKeys.revoked":        "Revoquee",
  "settings.apiKeys.revoke":         "Revoquer",
  "settings.apiKeys.usingTitle":     "Utiliser vos cles",
  "settings.apiKeys.usingBodyBefore":"Envoyez la cle comme jeton Bearer ou comme en-tete",
  "settings.apiKeys.usingBodyAfter": ". Les cles sont en lecture seule et chaque appel est verifie par rapport aux portees de la cle.",
  "settings.apiKeys.usingNote":      "Les portees d'ecriture sont reservees. Les endpoints d'ecriture acceptent uniquement votre session connectee, donc une cle divulguee ne peut pas modifier un projet.",
  "settings.apiKeys.toast.createdTitle": "Cle d'API creee",
  "settings.apiKeys.toast.createdBody":  "Copiez-la maintenant, elle ne sera plus affichee.",
  "settings.apiKeys.toast.errorTitle":   "Erreur",
  "settings.apiKeys.toast.copiedTitle":  "Copiee",
  "settings.apiKeys.toast.copiedBody":   "Copiee dans le presse-papiers.",

  // Settings - Privacy panel
  "settings.privacy.title": "Confidentialite et conformite",

  // Settings - System Status panel
  "settings.systemStatus.title":       "Etat du systeme",
  "settings.systemStatus.checking":    "Verification des services",
  "settings.systemStatus.loadError":   "Impossible de charger l'etat du systeme.",
  "settings.systemStatus.allOperational": "Tous les systemes operationnels",
  "settings.systemStatus.noChecks":    "Aucune verification de service ne remonte pour le moment.",
  "settings.systemStatus.incidents":   "Incidents",
  "settings.systemStatus.noIncidents": "Aucun incident signale.",
  "settings.systemStatus.resolved":    "Resolu",
  "settings.systemStatus.startedAt":   "Demarre {time}",
  "settings.systemStatus.resolvedAt":  "Resolu {time}",
  "settings.systemStatus.state.operational": "Operationnel",
  "settings.systemStatus.state.degraded":    "Degrade",
  "settings.systemStatus.state.outage":      "Panne",

  // Settings - Memory panel
  "settings.memory.title":     "Gestion de la memoire",
  "settings.memory.clearAll":  "Tout effacer",
  "settings.memory.body":      "Ce que Nuvra retient entre vos sessions, par agent. Le contenu n'est jamais affiche ici, seulement ce qui est stocke et sa taille. Supprimer l'efface definitivement.",
  "settings.memory.loading":   "Chargement de la memoire",
  "settings.memory.loadError": "Impossible de charger la memoire.",
  "settings.memory.emptyTitle":"Aucune memoire stockee pour l'instant.",
  "settings.memory.emptyBody": "A mesure que vous travaillez avec les agents de Nuvra, ce qu'ils apprennent apparaitra ici.",
  "settings.memory.itemOne":   "{n} element",
  "settings.memory.itemOther": "{n} elements",
  "settings.memory.updated":   "mis a jour {date}",
  "settings.memory.toast.deletedTitle":   "Memoire supprimee",
  "settings.memory.toast.deleteFailTitle":"Echec de la suppression",
  "settings.memory.toast.clearedTitle":   "Memoire effacee",
  "settings.memory.toast.clearedOne":     "{n} element supprime.",
  "settings.memory.toast.clearedOther":   "{n} elements supprimes.",
  "settings.memory.toast.clearFailTitle": "Echec de l'effacement",

  // Settings - AI Preferences panel
  "settings.aiPreferences.title":        "Preferences d'IA",
  "settings.aiPreferences.loading":      "Chargement",
  "settings.aiPreferences.intro":        "Comment les agents de Nuvra travaillent pour vous. Ces reglages sont enregistres sur votre compte et s'appliquent a toutes les sessions.",
  "settings.aiPreferences.preferredModel":"Modele prefere",
  "settings.aiPreferences.automatic":    "Automatique (Nuvra choisit le meilleur modele par tache)",
  "settings.aiPreferences.reasoningDepth":"Profondeur de raisonnement",
  "settings.aiPreferences.creativity":   "Creativite",
  "settings.aiPreferences.responseStyle":"Style de reponse",
  "settings.aiPreferences.longTermMemory":"Utiliser la memoire a long terme",
  "settings.aiPreferences.longTermMemoryNote":"Permet aux agents de retenir le contexte entre les sessions. Gerez la memoire stockee sous Memoire.",
  "settings.aiPreferences.saving":       "Enregistrement",
  "settings.aiPreferences.save":         "Enregistrer les preferences",
  "settings.aiPreferences.depth.concise":  "Concis",
  "settings.aiPreferences.depth.balanced": "Equilibre",
  "settings.aiPreferences.depth.deep":     "Approfondi",
  "settings.aiPreferences.creativity.precise":  "Precis",
  "settings.aiPreferences.creativity.balanced": "Equilibre",
  "settings.aiPreferences.creativity.creative": "Creatif",
  "settings.aiPreferences.style.concise":  "Concis",
  "settings.aiPreferences.style.balanced": "Equilibre",
  "settings.aiPreferences.style.detailed": "Detaille",
  "settings.aiPreferences.toast.savedTitle":    "Preferences enregistrees",
  "settings.aiPreferences.toast.saveFailTitle": "Echec de l'enregistrement",


  // Settings - AI Behavior / Memory & Context enterprise surfaces
  "settings.aiBehavior.loadError":"Impossible de charger les reglages du comportement IA.","settings.aiBehavior.summaryTitle":"Nuvra AI","settings.aiBehavior.accountBadge":"Parametres du compte","settings.aiBehavior.summaryBody":"Ces parametres guident l'IA interactive de Nuvra; le routage propre a une tache et la gouvernance restent prioritaires.",
  "settings.aiBehavior.summary.routing":"Routage","settings.aiBehavior.summary.reasoning":"Raisonnement","settings.aiBehavior.summary.style":"Style","settings.aiBehavior.summary.memory":"Memoire","settings.aiBehavior.enabled":"Activee","settings.aiBehavior.disabled":"Desactivee",
  "settings.aiBehavior.routingTitle":"Routage des modeles","settings.aiBehavior.routingDescription":"Choisissez le modele pour l'IA interactive. Le routage automatique suit les regles serveur de disponibilite.","settings.aiBehavior.modelLabel":"Modele prefere","settings.aiBehavior.modelHelp":"Une preference, sans contournement de facturation ou de gouvernance.","settings.aiBehavior.automatic":"Automatique","settings.aiBehavior.modelPinnedNote":"Nuvra utilisera ce modele pour les requetes interactives eligibles tant qu'il reste disponible.","settings.aiBehavior.modelAutomaticNote":"Nuvra choisit le modele disponible adapte a chaque requete interactive.",
  "settings.aiBehavior.reasoningTitle":"Raisonnement","settings.aiBehavior.reasoningDescription":"Definissez le niveau de raisonnement prefere.","settings.aiBehavior.reasoning.concise.label":"Concis","settings.aiBehavior.reasoning.concise.description":"Raisonnement cible pour des reponses directes.","settings.aiBehavior.reasoning.balanced.label":"Equilibre","settings.aiBehavior.reasoning.balanced.description":"Equilibre entre profondeur, rapidite et ressources.","settings.aiBehavior.reasoning.deep.label":"Approfondi","settings.aiBehavior.reasoning.deep.description":"Raisonnement plus etendu pour les questions complexes.",
  "settings.aiBehavior.creativityTitle":"Creativite","settings.aiBehavior.creativityDescription":"Controlez le niveau de variation des reponses interactives.","settings.aiBehavior.creativity.precise.label":"Precis","settings.aiBehavior.creativity.precise.description":"Resultats previsibles et guides par les specifications.","settings.aiBehavior.creativity.balanced.label":"Equilibre","settings.aiBehavior.creativity.balanced.description":"Equilibre coherence et exploration utile.","settings.aiBehavior.creativity.creative.label":"Creatif","settings.aiBehavior.creativity.creative.description":"Davantage de variation pour explorer des idees.",
  "settings.aiBehavior.responseTitle":"Style de reponse","settings.aiBehavior.responseDescription":"Choisissez la quantite d'explications et de contexte.","settings.aiBehavior.style.concise.label":"Concis","settings.aiBehavior.style.concise.description":"Reponses directes avec peu d'explications.","settings.aiBehavior.style.balanced.label":"Equilibre","settings.aiBehavior.style.balanced.description":"Explications claires avec le contexte necessaire.","settings.aiBehavior.style.detailed.label":"Detaille","settings.aiBehavior.style.detailed.description":"Explications completes et contexte exploitable.","settings.aiBehavior.recommended":"Recommande",
  "settings.aiBehavior.memoryTitle":"Memoire a long terme","settings.aiBehavior.memoryDescription":"Autorisez les agents a conserver le contexte permis entre les sessions.","settings.aiBehavior.memoryToggle":"Activer la memoire a long terme","settings.aiBehavior.memoryOnTitle":"Memoire activee","settings.aiBehavior.memoryOnBody":"Nuvra peut utiliser la memoire autorisee pour maintenir la continuite.","settings.aiBehavior.memoryOffTitle":"Memoire desactivee","settings.aiBehavior.memoryOffBody":"Nuvra n'utilisera pas les operations de memoire a long terme.","settings.aiBehavior.manageMemory":"Gerer la memoire stockee",
  "settings.aiBehavior.saved":"Enregistre","settings.aiBehavior.unsaved":"Modifications non enregistrees","settings.aiBehavior.saveNote":"Les parametres du compte s'appliquent aux requetes IA interactives eligibles.","settings.aiBehavior.reset":"Reinitialiser","settings.aiBehavior.discard":"Ignorer","settings.aiBehavior.saving":"Enregistrement","settings.aiBehavior.save":"Enregistrer les modifications","settings.aiBehavior.toast.savedTitle":"Comportement IA enregistre","settings.aiBehavior.toast.saveFailTitle":"Impossible d'enregistrer le comportement IA",
  "settings.memory.managementTitle":"Memoire et contexte","settings.memory.managementDescription":"Consultez les metadonnees de memoire du compte. Le contenu stocke n'est jamais expose ici.","settings.memory.active":"Active","settings.memory.paused":"En pause","settings.memory.manageBehavior":"Comportement IA","settings.memory.stats.items":"Elements memoire","settings.memory.stats.storage":"Stockage utilise","settings.memory.stats.updated":"Derniere mise a jour","settings.memory.stats.expiring":"Avec expiration","settings.memory.privacyTitle":"Confidentialite et controle","settings.memory.longTermTitle":"Memoire a long terme","settings.memory.longTermOn":"Activee pour les operations eligibles.","settings.memory.longTermOff":"Desactivee par la preference du compte.","settings.memory.toggleManagedElsewhere":"Ce controle est gere dans Comportement IA afin de conserver une seule source de verite.",
  "settings.memory.recordsTitle":"Memoire stockee","settings.memory.recordsDescription":"Metadonnees uniquement: cle, agent, taille, dates et expiration.","settings.memory.searchPlaceholder":"Rechercher des cles ou agents","settings.memory.clearSearch":"Effacer la recherche","settings.memory.agentFilter":"Filtrer par agent","settings.memory.allAgents":"Tous les agents","settings.memory.expires":"Expire","settings.memory.delete":"Supprimer la memoire","settings.memory.noMatches":"Aucune memoire correspondante","settings.memory.noMatchesBody":"Essayez une autre cle ou un autre agent.","settings.memory.reviewBehavior":"Revoir le comportement IA","settings.memory.securityNote":"Nuvra conserve le contenu de la memoire derriere la frontiere serveur. Cette page expose uniquement des metadonnees et chaque operation est limitee au proprietaire authentifie.",
  "settings.memory.detailDescription":"Metadonnees de cette memoire. Le contenu sous-jacent n'est pas affiche.","settings.memory.detail.key":"Cle memoire","settings.memory.detail.agent":"Agent","settings.memory.detail.size":"Taille","settings.memory.detail.created":"Cree","settings.memory.detail.updated":"Mis a jour","settings.memory.detail.expiration":"Expiration","settings.memory.noExpiration":"Sans expiration","settings.memory.contentHidden":"Le contenu stocke n'est pas renvoye au client.","settings.memory.deleteConfirmTitle":"Supprimer cette memoire ?","settings.memory.deleteConfirmDescription":"Cette action supprime definitivement la memoire selectionnee.","settings.memory.clearConfirmTitle":"Effacer toute la memoire du compte ?","settings.memory.clearConfirmDescription":"Cette action supprime definitivement les enregistrements geres par cette surface. Le contexte de projet et d'organisation est gere separement.","settings.memory.clearing":"Effacement","settings.memory.cancel":"Annuler",

  "settings.aiBehavior.pageDescription": "Controlez le raisonnement, les reponses, le routage interactif et la memoire a long terme de Nuvra.",
  "settings.memory.pageDescription": "Consultez et controlez la memoire de compte stockee par Nuvra pour la continuite.",
  "settings.memory.scopeNote": "Cette surface gere les metadonnees de memoire du compte. Le contexte de projet et d organisation est gere par les surfaces memoire existantes de Nuvra.",
  // Settings - Billing panel
  "settings.billing.planTitle":      "Plan",
  "settings.billing.loading":        "Chargement",
  "settings.billing.loadError":      "Impossible de charger la facturation.",
  "settings.billing.free":           "Gratuit",
  "settings.billing.noSubscription": "Aucun abonnement payant actif.",
  "settings.billing.renews":         "se renouvelle {date}",
  "settings.billing.cancelNote":     "Votre plan est configure pour s'annuler a la fin de la periode actuelle.",
  "settings.billing.managePlan":     "Gerer le plan",
  "settings.billing.upgrade":        "Mettre a niveau",
  "settings.billing.creditsTitle":   "Credits",
  "settings.billing.actionsTitle":   "Cout des actions",
  "settings.billing.actionsNote":    "Les couts sont affiches avant l'execution d'une action. Les constructions complexes peuvent couter plus; les actions echouees sont remboursees.",
  "settings.billing.usageTitle":     "Utilisation recente",
  "settings.billing.usage.refund":   "Remboursement : {reason}",
  "settings.billing.usage.grant":    "Credits accordes",
  "settings.billing.usage.topup":    "Credits achetes",
  "settings.billing.usage.refunded": "rembourse",
  "settings.billing.noUsage":        "Aucune utilisation enregistree pour l'instant.",

  // Settings - Audit panel
  "settings.audit.title":        "Journaux d'audit",
  "settings.audit.intro":        "Un enregistrement immuable des actions effectuees sur votre compte et vos projets. Filtrez par type d'evenement ou recherchez par portee.",
  "settings.audit.allTypes":     "Tous les types d'evenement",
  "settings.audit.searchPlaceholder": "Rechercher une portee ou une table",
  "settings.audit.loading":      "Chargement du journal d'audit",
  "settings.audit.emptyTitle":   "Aucune action auditee pour l'instant.",
  "settings.audit.noMatch":      "Aucun evenement ne correspond a vos filtres.",
  "settings.audit.account":      "Compte",
  "settings.audit.project":      "Projet {id}",
  "settings.audit.scope.account":"compte",
  "settings.audit.scope.project":"projet",

  // Settings - Export center panel
  "settings.export.privateBadge": "Privé à votre compte",
  "settings.export.sourceGroups": "Groupes de données",
  "settings.export.format": "Format de téléchargement",
  "settings.export.rateLimit": "Téléchargements maximum",
  "settings.export.privacyTitle": "Limite de confidentialité",
  "settings.export.privacyBody": "L’export est limité aux données que Nuvra enregistre comme étant les vôtres. Les messages publics du support sont inclus ; les notes internes sont exclues.",
  "settings.export.successTitle": "Export prêt",
  "settings.export.successBody": "{records} enregistrements téléchargés dans {sources} groupes de données.",
  "settings.export.source.support_messages": "Conversations du support",
  "settings.audit.refresh": "Actualiser",
  "settings.audit.export": "Exporter le CSV filtré",
  "settings.audit.filters": "Filtres",
  "settings.audit.clear": "Effacer les filtres",
  "settings.audit.allScopes": "Tous les périmètres",
  "settings.audit.allTime": "Toute la période",
  "settings.audit.last24h": "Dernières 24 heures",
  "settings.audit.last7d": "7 derniers jours",
  "settings.audit.last30d": "30 derniers jours",
  "settings.audit.stat.events": "Événements enregistrés",
  "settings.audit.stat.account": "Événements du compte",
  "settings.audit.stat.projects": "Événements des projets",
  "settings.audit.col.event": "Événement",
  "settings.audit.col.scope": "Périmètre",
  "settings.audit.col.time": "Heure",
  "settings.audit.detail.action": "Action",
  "settings.audit.detail.time": "Enregistré",
  "settings.audit.detail.payload": "Détail enregistré",
  "settings.audit.retentionNote": "Cette vue affiche les enregistrements d’audit les plus récents renvoyés par Nuvra pour votre compte et vos projets. Les filtres et le CSV utilisent les enregistrements chargés.",
  "settings.export.title":       "Exportez vos donnees",
  "settings.export.body":        "Telechargez une copie JSON des donnees liees a votre compte, pour vos propres dossiers ou pour la conformite. L'export inclut uniquement vos propres donnees et est limite a 3 telechargements par heure.",
  "settings.export.included":    "Inclus dans votre export",
  "settings.export.preparing":   "Preparation de l'export",
  "settings.export.exportNow":   "Exporter mes donnees",
  "settings.export.generated":   "Genere {time}",
  "settings.export.toast.doneTitle": "Export telecharge",
  "settings.export.toast.doneBody":  "Vos donnees ont ete enregistrees sous nuvra-data-export.json.",
  "settings.export.toast.failTitle": "Echec de l'export",
  "settings.export.toast.failBody":  "Echec de l'export. Veuillez reessayer.",
  "settings.export.source.projects":            "Projets",
  "settings.export.source.builds":              "Historique des constructions",
  "settings.export.source.support_tickets":     "Tickets de support",
  "settings.export.source.creator_standing":    "Statut du createur",
  "settings.export.source.creator_violations":  "Infractions du createur",
  "settings.export.source.creator_appeals":     "Appels du createur",
  "settings.export.source.enforcement_actions": "Actions d'application",
  "settings.export.source.published_templates": "Modeles publies",
  "settings.export.source.template_installs":   "Installations de modeles",
  "settings.export.source.purchases":           "Achats",
  "settings.export.source.payments":            "Historique des paiements",
  "settings.export.source.data_audit":          "Journal d'audit du compte",

  /* Admin operator console - shell (tab bar, access gate, header) */
  "admin.shell.nav.overview":       "Apercu",
  "admin.shell.nav.status":         "Statut",
  "admin.shell.nav.trust":          "Confiance et securite",
  "admin.shell.nav.users":          "Utilisateurs",
  "admin.shell.nav.projects":       "Projets",
  "admin.shell.nav.builds":         "Builds IA",
  "admin.shell.nav.agent":             "Agent",
  "admin.shell.nav.workers":           "Workers",
  "admin.agent.loading":               "Chargement de l'activite de l'agent…",
  "admin.agent.summary":               "{sessions} sessions, {failed} en echec, {acu} ACU consommes",
  "admin.agent.filterStatus":          "Filtrer par statut",
  "admin.agent.allStatuses":           "Tous les statuts",
  "admin.agent.refresh":               "Actualiser",
  "admin.agent.none":                  "Aucune session d'agent enregistree.",
  "admin.agent.colWorkOrder":          "Ordre de travail",
  "admin.agent.colGoal":               "Objectif",
  "admin.agent.colUser":               "Utilisateur",
  "admin.agent.colStatus":             "Statut",
  "admin.agent.colTasks":              "Taches",
  "admin.agent.colAcu":                "ACU",
  "admin.agent.colStarted":            "Debut",
  "admin.agent.prev":                  "Precedent",
  "admin.agent.next":                  "Suivant",
  "admin.agent.page":                  "Page {page}",
  "admin.shell.nav.billing":        "Facturation et credits",
  "admin.shell.nav.executive":     "Direction",
  "admin.shell.nav.financials":     "Sante financiere",
  "admin.shell.nav.alerts":         "Alertes",
  "admin.shell.nav.communications": "Communications",
  "admin.shell.nav.security":       "Securite",
  "admin.shell.nav.audit":          "Journal d'audit",
  "admin.shell.nav.insights":       "Analyses",
  "admin.shell.nav.flags":          "Indicateurs de fonctionnalite",
  "admin.shell.nav.logs":           "Journaux",
  "admin.shell.nav.simulate":       "Simuler",
  "admin.shell.nav.database":       "Base de donnees",
  "admin.comms.intro":               "Les communications de plateforme atteignent tous les comptes en une fois et ne peuvent pas etre rappelees. Chacune est dedupliquee par personne, donc renvoyer la meme version ne previent personne deux fois.",
  "admin.comms.release.announce":    "Annoncer la version a tous les utilisateurs",
  "admin.comms.policy.announce":     "Annoncer la mise a jour du document a tous les utilisateurs",
  "admin.comms.sent":                "Envoye a {created} comptes sur {recipients}. {deduplicated} l'avaient deja.",
  "admin.comms.error.forbidden":     "Votre compte n'est pas administrateur de la plateforme.",
  "admin.comms.error.unknownRelease":"Cette version ne figure pas dans les notes de version publiees, elle ne peut donc pas etre annoncee.",
  "admin.comms.error.invalidRequest":"Un element obligatoire manque. Verifiez les champs et reessayez.",
  "admin.comms.error.unavailable":   "La communication n'a pas pu etre envoyee. Rien n'a ete distribue.",
  "admin.comms.release.title":       "Nouveautes",
  "admin.comms.release.body":        "Annoncez une version deja publiee dans les notes de version. Le texte provient de la version elle-meme, donc la notification et la page Nouveautes concordent toujours.",
  "admin.comms.release.version":     "Version",
  "admin.comms.policy.title":        "Confidentialite et Conditions",
  "admin.comms.policy.body":         "Informez tous les comptes qu'un document legal a change. La version est obligatoire, car un avis que personne ne peut relier a un document ne permet pas d'agir.",
  "admin.comms.policy.document":     "Document",
  "admin.comms.policy.privacy":      "Politique de confidentialite",
  "admin.comms.policy.terms":        "Conditions d'utilisation",
  "admin.comms.policy.version":      "Version",
  "admin.comms.policy.effective":    "Date d'entree en vigueur (facultatif)",
  "admin.comms.security.title":      "Avis de securite",
  "admin.comms.security.body":       "Le message le plus grave que la plateforme puisse envoyer, et celui que l'usage excessif abime le plus. Ne l'envoyez que pour un evenement de securite reel.",
  "admin.comms.security.reference":  "Reference",
  "admin.comms.security.referenceHint": "Un identifiant stable pour cet avis. Le reutiliser evite de prevenir deux fois de la meme chose.",
  "admin.comms.security.subject":    "Objet",
  "admin.comms.security.message":    "Message",
  "admin.comms.security.send":       "Envoyer l'avis de securite",
  "admin.shell.verifyingAccess":    "Verification de l'acces...",
  "admin.shell.deniedMissingTitle": "Outils d'admin non deployes",
  "admin.shell.deniedNotAdminTitle":"Acces admin requis",
  "admin.shell.deniedMissingBody":  "Les fonctions de base de donnees admin ne sont pas encore presentes dans cet environnement. Appliquez les migrations du panneau admin, puis rechargez.",
  "admin.shell.deniedNotAdminBody": "Votre compte n'a pas le role admin. Un admin existant doit l'accorder (user_roles.role = 'admin') avant que cette console soit disponible.",
  "admin.shell.deniedCacheTitle":   "Outils d'admin inaccessibles",
  "admin.shell.deniedCacheBody":    "L'API ne peut pas resoudre les fonctions admin. Soit elles n'ont pas ete appliquees a cette base de donnees, soit elles l'ont ete et le cache de schema n'a pas ete recharge. Verifiez qu'elles existent, puis rechargez le cache.",
  "admin.shell.deniedSchemaTitle":  "Schema admin incomplet",
  "admin.shell.deniedSchemaBody":   "Les fonctions admin sont deployees, mais une table ou une colonne qu'elles lisent manque dans cette base de donnees. Appliquez les migrations en attente, puis rechargez.",
  "admin.shell.deniedGrantTitle":   "Permission d'execution manquante",
  "admin.shell.deniedGrantBody":    "Les fonctions admin existent mais votre role ne peut pas les executer. Accordez EXECUTE sur les fonctions admin a authenticated, puis rechargez.",
  "admin.shell.deniedSignedOutTitle":"Session expiree",
  "admin.shell.deniedSignedOutBody":"Votre session n'est plus valide. Reconnectez-vous pour rouvrir la console.",
  "admin.shell.deniedUnknownTitle": "La console n'a pas pu s'ouvrir",
  "admin.shell.deniedUnknownBody":  "La verification d'acces a echoue pour une raison que Nuvra ne peut pas classer. La reponse de la base de donnees est affichee ci-dessous.",
  "admin.shell.backToDashboard":    "Retour au tableau de bord",
  "admin.shell.title":              "Centre de controle Nuvra",
  "admin.shell.subtitle":           "Tableau de bord des operations internes. Acces admin uniquement.",

  // Admin Overview tab
  "admin.overview.loading":               "Chargement des statistiques de la plateforme...",
  "admin.overview.autoRefresh":           "Actualisation toutes les 30s · Derniere:",
  "admin.overview.refresh":               "Actualiser",
  "admin.overview.usersHeading":          "Utilisateurs",
  "admin.overview.totalUsers":            "Utilisateurs totaux",
  "admin.overview.active30d":             "Actifs (30j)",
  "admin.overview.newThisWeek":           "Nouveaux cette semaine",
  "admin.overview.suspended":             "Suspendus",
  "admin.overview.platformActivity":      "Activite de la plateforme",
  "admin.overview.projects":              "Projets",
  "admin.overview.activeBuilds":          "Builds actifs",
  "admin.overview.failedBuilds7d":        "Builds echoues (7j)",
  "admin.overview.unreadAlerts":          "Alertes non lues",
  "admin.overview.successRatesTitle":     "Taux de reussite (7 jours)",
  "admin.overview.buildSuccessRate":      "Taux de reussite des builds",
  "admin.overview.deploymentSuccessRate": "Taux de reussite des deploiements",
  "admin.overview.revenueTitle":          "Revenus",
  "admin.overview.thisMonth":             "Ce mois-ci",
  "admin.overview.allTime":               "Depuis le debut",
  "admin.overview.acuCredits":            "Credits",
  "admin.overview.totalGranted":          "Total accorde",
  "admin.overview.totalConsumed":         "Total consomme",
  "admin.overview.inProfilesNow":         "Dans les profils maintenant",
  "admin.overview.allTimeSub":            "depuis le debut",
  "admin.overview.liveTotalSub":          "total en direct sur tous les utilisateurs",
  "admin.overview.sevenDayTrends":        "Tendances sur 7 jours",
  "admin.overview.buildActivityTitle":    "Activite des builds (7 jours)",
  "admin.overview.noBuildData":           "Aucune donnee de build sur les 7 derniers jours.",
  "admin.overview.newUsersTitle":         "Nouveaux utilisateurs (7 jours)",
  "admin.overview.noSignupData":          "Aucune donnee d'inscription sur les 7 derniers jours.",
  "admin.overview.seriesSuccess":         "Reussite",
  "admin.overview.seriesFailed":          "Echoues",
  "admin.overview.seriesNewUsers":        "Nouveaux utilisateurs",

  // Admin Trust & Safety tab
  "admin.trust.loading":             "Chargement de la console du fondateur",
  "admin.trust.intro":               "Confiance et securite: preparation, la file de revision, le flux de securite et l'application pour les createurs.",
  "admin.trust.refresh":             "Actualiser",
  "admin.trust.appealDecideError":   "Impossible de decider de l'appel.",
  "admin.trust.appealApproved":      "Appel approuve. La retenue a ete liberee.",
  "admin.trust.appealDenied":        "Appel refuse.",
  "admin.trust.replyPrompt":         "Repondre au createur (cela resout le ticket):",
  "admin.trust.replyError":          "Impossible d'envoyer la reponse.",
  "admin.trust.replySent":           "Reponse envoyee et ticket resolu.",
  "admin.trust.lookupError":         "Impossible de rechercher le createur.",
  "admin.trust.violationError":      "Impossible d'enregistrer l'infraction.",
  "admin.trust.violationRecorded":   "Infraction enregistree. Statut: {standing}{terminated}.",
  "admin.trust.terminatedSuffix":    "(resilie)",
  "admin.trust.resolveError":        "Impossible de resoudre le cas.",
  "admin.trust.resolveSuccess":      "Cas resolu et retenue de revenus liberee.",
  "admin.trust.verifyError":         "Impossible de demander la verification.",
  "admin.trust.verifyRequested":     "Verification {kind} demandee. Les paiements sont geles jusqu'a ce que le createur la termine.",
  "admin.trust.chargebackError":     "Impossible d'evaluer les retrofacturations.",
  "admin.trust.chargebackEnforced":  "Infraction de la regle 9 enregistree; retenue de paiement appliquee.",
  "admin.trust.chargebackNoThreshold": "Evalue. Aucun seuil de retenue atteint.",
  "admin.trust.readinessTitle":      "Preparation au lancement",
  "admin.trust.ready":               "Pret",
  "admin.trust.notReady":            "Pas pret",
  "admin.trust.readinessLoadError":  "Impossible de charger la preparation (acces admin requis).",
  "admin.trust.blockers":            "Bloqueurs:",
  "admin.trust.recommended":         "Recommande:",
  "admin.trust.schemaMissing":       "Schema manquant:",
  "admin.trust.optionalOff":         "Optionnel desactive:",
  "admin.trust.appealsQueue":        "File d'appels",
  "admin.trust.noAppeals":           "Aucun appel ouvert.",
  "admin.trust.creatorPrefix":       "createur",
  "admin.trust.approve":             "Approuver",
  "admin.trust.deny":                "Refuser",
  "admin.trust.supportQueue":        "File de support",
  "admin.trust.noTickets":           "Aucun ticket ouvert ou escalade.",
  "admin.trust.replyResolve":        "Repondre et resoudre",
  "admin.trust.creatorEnforcement":  "Application pour les createurs",
  "admin.trust.creatorIdPlaceholder":"id utilisateur du createur",
  "admin.trust.lookUp":              "Rechercher",
  "admin.trust.riskLabel":           "risque",
  "admin.trust.standingLabel":       "statut:",
  "admin.trust.strikesLabel":        "fautes:",
  "admin.trust.terminated":          "resilie",
  "admin.trust.ruleOption":          "Regle {number}: {title}",
  "admin.trust.recordViolation":     "Enregistrer l'infraction",
  "admin.trust.resolveCase":         "Resoudre le cas",
  "admin.trust.verificationLabel":   "Verification (Regle 11):",
  "admin.trust.requestFreezes":      "Demander (gele les paiements)",
  "admin.trust.chargebackTitle":     "Surveillance des retrofacturations",
  "admin.trust.sellerIdPlaceholder": "id de compte du vendeur",
  "admin.trust.evaluate":            "Evaluer",
  "admin.trust.enforce":             "Appliquer",
  "admin.trust.chargebackRate":      "taux de retrofacturation",
  "admin.trust.chargebackStats":     "{sales} ventes, {chargebacks} retrofacturations, {disputes} litiges",
  "admin.trust.securityFeedTitle":   "Flux de securite et d'abus",
  "admin.trust.noSecurityEvents":    "Aucun evenement de securite recent.",

  // Admin Users tab
  "admin.users.loading":                 "Chargement des utilisateurs...",
  "admin.users.amountMustBePositive":    "Le montant doit etre positif.",
  "admin.users.grantFailed":             "L'octroi a echoue.",
  "admin.users.grantSuccess":            "{amount} credits accordes avec succes.",
  "admin.users.amountZeroOrPositive":    "Le montant doit etre zero ou positif.",
  "admin.users.setFailed":               "Le reglage a echoue.",
  "admin.users.setSuccess":              "Solde regle a {amount} credits (etait {old}).",
  "admin.users.failed":                  "Echec.",
  "admin.users.userSuspended":           "Utilisateur suspendu.",
  "admin.users.userReactivated":         "Utilisateur reactive.",
  "admin.users.profileLoadFailed":       "Echec du chargement du profil.",
  "admin.users.linkGenFailed":           "Echec de la generation du lien.",
  "admin.users.totalUsers":              "Utilisateurs totaux",
  "admin.users.totalProfileAcu":         "Credits totaux",
  "admin.users.grantAcu":                "Accorder des credits",
  "admin.users.emailOrUserId":           "E-mail ou ID utilisateur",
  "admin.users.targetPlaceholder":       "user@example.com ou uuid",
  "admin.users.amountAcu":               "Montant (credits)",
  "admin.users.reason":                  "Motif",
  "admin.users.granting":                "Octroi...",
  "admin.users.setAcuBalance":           "Regler le solde de credits (exact)",
  "admin.users.setToAcu":                "Regler a (credits)",
  "admin.users.setting":                 "Reglage...",
  "admin.users.setBalance":              "Regler le solde",
  "admin.users.setBalanceHelp":          "Ecrase le solde exact (non additif). Admin uniquement; chaque changement est enregistre dans le registre.",
  "admin.users.usersTitle":              "Utilisateurs ({count})",
  "admin.users.planAll":                 "tous",
  "admin.users.searchPlaceholder":       "Rechercher...",
  "admin.users.colEmail":                "E-mail",
  "admin.users.colPlan":                 "Plan",
  "admin.users.colStatus":               "Statut",
  "admin.users.colProfileAcu":           "Credits",
  "admin.users.colRole":                 "Role",
  "admin.users.grantRole":             "Accorder {role}",
  "admin.users.revokeRole":            "Revoquer {role}",
  "admin.users.roleGranted":           "Role {role} accorde.",
  "admin.users.roleRevoked":           "Role {role} revoque.",
  "admin.users.roleUnchanged":         "Aucun changement : l'utilisateur avait deja cet etat {role}.",
  "admin.users.subscriptionHeading":     "Abonnement",
  "admin.users.subPlan":                 "Forfait",
  "admin.users.subStatus":               "Statut",
  "admin.users.subRenews":               "Renouvellement",
  "admin.users.subCancelling":           "Annulation en fin de periode",
  "admin.users.subNone":                 "Aucun abonnement enregistre.",
  "admin.users.deploymentsHeading":      "Deploiements ({count})",
  "admin.users.noDeployments":           "Aucun deploiement.",
  "admin.users.colJoined":               "Inscrit",
  "admin.users.colActions":              "Actions",
  "admin.users.noUsersFound":            "Aucun utilisateur trouve.",
  "admin.users.statusSuspended":         "Suspendu",
  "admin.users.statusActive":            "Actif",
  "admin.users.grant":                   "Accorder",
  "admin.users.view":                    "Voir",
  "admin.users.reactivate":              "Reactiver",
  "admin.users.suspend":                 "Suspendre",
  "admin.users.suspendConfirm":          "Suspendre {email}?\nMotif: {reason}",
  "admin.users.generateLoginLink":       "Generer un lien de connexion",
  "admin.users.magicLinkHelp":           "Ouvre un lien magique d'1 heure pour se connecter en tant que cet utilisateur (navigation privee)",
  "admin.users.copyLink":                "Copier le lien",
  "admin.users.loadingProfile":          "Chargement du profil...",
  "admin.users.riskProfile":             "Profil de risque",
  "admin.users.riskLevel":               "Niveau de risque:",
  "admin.users.blockedPrompts":          "Prompts bloques:",
  "admin.users.suspendedLabel":          "Suspendu:",
  "admin.users.yes":                     "Oui",
  "admin.users.no":                      "Non",
  "admin.users.reasonLabel":             "Motif:",
  "admin.users.projectsHeading":         "Projets ({count})",
  "admin.users.noProjects":              "Aucun projet.",
  "admin.users.recentBuildsHeading":     "Builds recents ({count})",
  "admin.users.noBuilds":                "Aucun build.",
  "admin.users.acuTransactionsHeading":  "Transactions de credits ({count})",
  "admin.users.noTransactions":          "Aucune transaction.",
  "admin.users.paymentsHeading":         "Paiements ({count})",
  "admin.users.noPayments":              "Aucun historique de paiement.",
  "admin.users.sessionsHeading":         "Sessions ({count})",
  "admin.users.noSessions":              "Aucune session.",
  "admin.users.sessionRevoked":          "Revoquee",
  "admin.users.sessionActive":           "Active",

  // Admin Projects tab
  "admin.projects.loading":          "Chargement des projets...",
  "admin.projects.flagConfirm":      "Signaler \"{name}\" pour revision admin?\nCela creera une alerte dans l'onglet Alertes.",
  "admin.projects.flagFailed":       "Le signalement a echoue.",
  "admin.projects.flagSuccess":      "\"{name}\" signale pour revision.",
  "admin.projects.title":            "Projets",
  "admin.projects.searchPlaceholder":"Rechercher un projet ou un e-mail...",
  "admin.projects.search":           "Rechercher",
  "admin.projects.colProject":       "Projet",
  "admin.projects.colOwner":         "Proprietaire",
  "admin.projects.colType":          "Type",
  "admin.projects.colVisibility":    "Visibilite",
  "admin.projects.colLatestBuild":   "Dernier build",
  "admin.projects.colLatestDeploy":  "Dernier deploiement",
  "admin.projects.colCreated":       "Cree",
  "admin.projects.colActions":       "Actions",
  "admin.projects.noProjects":       "Aucun projet trouve.",
  "admin.projects.openWorkspace":    "Ouvrir l'espace de travail",
  "admin.projects.open":             "Ouvrir",
  "admin.projects.flag":             "Signaler",
  "admin.projects.pageInfo":         "Page {page} - {count} projets affiches",
  "admin.projects.prev":             "Precedent",
  "admin.projects.next":             "Suivant",

  // Admin Builds tab
  "admin.builds.cancelConfirm":      "Annuler ce build? Le deploiement Railway en cours sera arrete.",
  "admin.builds.cancelFailed":       "L'annulation a echoue",
  "admin.builds.cancelled":          "Build annule.",
  "admin.builds.retryConfirm":       "Reessayer ce build? Railway redeploiera le dernier deploiement.",
  "admin.builds.retryFailed":        "Le reessai a echoue",
  "admin.builds.redeployTriggered":  "Redeploiement declenche.",
  "admin.builds.loading":            "Chargement des builds...",
  "admin.builds.title":              "Builds IA",
  "admin.builds.queuedCount":        "{count} en file",
  "admin.builds.runningCount":       "{count} en cours",
  "admin.builds.failedCount":        "{count} echoues",
  "admin.builds.filter.all":         "tous",
  "admin.builds.filter.running":     "en cours",
  "admin.builds.filter.done":        "termines",
  "admin.builds.filter.failed":      "echoues",
  "admin.builds.colBuildId":         "ID de build",
  "admin.builds.colProject":         "Projet",
  "admin.builds.colUser":            "Utilisateur",
  "admin.builds.colStatus":          "Statut",
  "admin.builds.colDuration":        "Duree",
  "admin.builds.colCreated":         "Cree",
  "admin.builds.colActions":         "Actions",
  "admin.builds.noBuilds":           "Aucun build trouve.",
  "admin.builds.cancel":             "Annuler",
  "admin.builds.retry":              "Reessayer",
  "admin.builds.errorLabel":         "Erreur",
  "admin.builds.pageInfo":           "Page {page} - {count} builds",
  "admin.builds.prev":               "Precedent",
  "admin.builds.next":               "Suivant",

  // Admin Billing tab
  "admin.billing.refundConfirm":   "Rembourser cette transaction de credits?",
  "admin.billing.refundFailed":    "Le remboursement a echoue.",
  "admin.billing.refundSuccess":   "Rembourse avec succes.",
  "admin.billing.loading":         "Chargement des transactions...",
  "admin.billing.title":           "Facturation et credits",
  "admin.billing.subAcu":          "Transactions de credits",
  "admin.billing.subPayments":     "Paiements",
  "admin.billing.colUser":         "Utilisateur",
  "admin.billing.colType":         "Type",
  "admin.billing.colAcu":          "Credits",
  "admin.billing.colDescription":  "Description",
  "admin.billing.colBuildId":      "ID de build",
  "admin.billing.colDate":         "Date",
  "admin.billing.colActions":      "Actions",
  "admin.billing.noTransactions":  "Aucune transaction.",
  "admin.billing.highChip":        "eleve",
  "admin.billing.refund":          "Rembourser",
  "admin.billing.colGateway":      "Passerelle",
  "admin.billing.colAmount":       "Montant",
  "admin.billing.colStatus":       "Statut",
  "admin.billing.noPayments":      "Aucun paiement.",
  "admin.billing.tariffTitle":     "Tarifs des actions (interne)",
  "admin.billing.tariffNote":      "Le prix en credits de chaque action mesuree, issu du meme registre que la chaine de build. Les utilisateurs voient les couts dans l'apercu avant execution et dans leur historique d'usage, pas dans ce tableau.",
  "admin.billing.pageInfo":        "Page {page} - {count} enregistrements",
  "admin.billing.prev":            "Precedent",
  "admin.billing.next":            "Suivant",

  // Admin Alerts tab
  "admin.alerts.loading":              "Chargement des alertes...",
  "admin.alerts.acknowledgeFailed":      "Impossible d'accuser reception de cette alerte.",
  "admin.alerts.resolveFailed":          "Impossible de resoudre cette alerte.",
  "admin.alerts.unacknowledgedCount":  "{count} non reconnues",
  "admin.alerts.unresolvedCount":      "{count} non resolues",
  "admin.alerts.criticalBadge":        "CRITIQUE",
  "admin.alerts.sev.all":              "toutes",
  "admin.alerts.sev.critical":         "critique",
  "admin.alerts.sev.high":             "elevee",
  "admin.alerts.sev.medium":           "moyenne",
  "admin.alerts.sev.low":              "faible",
  "admin.alerts.hideResolved":         "Masquer les resolues",
  "admin.alerts.showAll":              "Tout afficher",
  "admin.alerts.allClear":             "Aucune alerte. Tout est en ordre.",
  "admin.alerts.resolvedChip":         "Resolue",
  "admin.alerts.acknowledgedChip":     "Reconnue",
  "admin.alerts.acknowledge":          "Reconnaitre",
  "admin.alerts.resolve":              "Resoudre",

  // Admin Security tab
  "admin.security.forceLogoutConfirm": "Forcer la deconnexion de {email}?\nToutes ses sessions actives seront immediatement terminees.",
  "admin.security.forceLogoutFailed":  "La deconnexion forcee a echoue.",
  "admin.security.forceLogoutSuccess": "{email} a ete deconnecte de toutes les sessions.",
  "admin.security.suspendConfirm":     "Suspendre cet utilisateur?",
  "admin.security.failed":             "Echec.",
  "admin.security.userSuspended":      "Utilisateur suspendu.",
  "admin.security.userReactivated":    "Utilisateur reactive.",
  "admin.security.loading":            "Chargement des donnees de securite...",
  "admin.security.title":              "Securite",
  "admin.security.subSafetyEvents":    "Evenements de securite",
  "admin.security.subSessions":        "Sessions",
  "admin.security.subAbuseTracking":   "Suivi des abus",
  "admin.security.sev.all":            "toutes",
  "admin.security.sev.critical":       "critique",
  "admin.security.sev.high":           "elevee",
  "admin.security.sev.medium":         "moyenne",
  "admin.security.sev.low":            "faible",
  "admin.security.colUser":            "Utilisateur",
  "admin.security.colProvider":        "Fournisseur",
  "admin.security.colStatus":          "Statut",
  "admin.security.colSuspicious":      "Suspecte",
  "admin.security.colLastSeen":        "Vue derniere",
  "admin.security.colExpires":         "Expire",
  "admin.security.colActions":         "Actions",
  "admin.security.noSessions":         "Aucune session trouvee.",
  "admin.security.revoked":            "Revoquee",
  "admin.security.active":             "Active",
  "admin.security.forceLogout":        "Forcer la deconnexion",
  "admin.security.colType":            "Type",
  "admin.security.colSeverity":        "Severite",
  "admin.security.colDescription":     "Description",
  "admin.security.colDate":            "Date",
  "admin.security.noEvents":           "Aucun evenement de securite.",
  "admin.security.colRisk":            "Risque",
  "admin.security.colBlocked":         "Bloques",
  "admin.security.colFlagged":         "Signales",
  "admin.security.colViolations":      "Infractions",
  "admin.security.colLastViolation":   "Derniere infraction",
  "admin.security.noAbuse":            "Aucun enregistrement d'abus.",
  "admin.security.suspended":          "Suspendu",
  "admin.security.reactivate":         "Reactiver",
  "admin.security.suspend":            "Suspendre",
  "admin.security.pageInfo":           "Page {page}",
  "admin.security.prev":               "Precedent",
  "admin.security.next":               "Suivant",

  // Admin Audit tab
  "admin.audit.loading":               "Chargement du journal d'audit...",
  "admin.audit.targetType":            "Type de cible",
  "admin.audit.targetTypePlaceholder": "ex. user, acu_transaction...",
  "admin.audit.action":                "Action",
  "admin.audit.actionPlaceholder":     "ex. suspend, grant...",
  "admin.audit.filter":                "Filtrer",
  "admin.audit.clear":                 "Effacer",
  "admin.audit.title":                 "Journal d'audit",
  "admin.audit.immutable":             "(immuable)",
  "admin.audit.colActor":              "Acteur",
  "admin.audit.colAction":             "Action",
  "admin.audit.colTargetType":         "Type de cible",
  "admin.audit.colTargetId":           "ID de cible",
  "admin.audit.colIp":                 "IP",
  "admin.audit.colDate":               "Date",
  "admin.audit.noEntries":             "Aucune entree dans le journal d'audit.",
  "admin.audit.before":                "Avant",
  "admin.audit.after":                 "Apres",
  "admin.audit.pageInfo":              "Page {page} - {count} entrees",
  "admin.audit.prev":                  "Precedent",
  "admin.audit.next":                  "Suivant",

  // Admin Insights tab
  "admin.insights.loading":             "Chargement des analyses...",
  "admin.insights.subtitle":            "30 derniers jours. Actualisez pour les donnees les plus recentes.",
  "admin.insights.refresh":             "Actualiser",
  "admin.insights.funnelTitle":         "Entonnoir d'activation des utilisateurs",
  "admin.insights.funnelRegistered":    "Inscrits",
  "admin.insights.funnelCreatedProject":"Projet cree",
  "admin.insights.funnelRanFirstBuild": "Premier build execute",
  "admin.insights.funnelBuildSucceeded":"Build reussi",
  "admin.insights.funnelDeployed":      "Deploye",
  "admin.insights.fromPrev":            "({pct} depuis le precedent)",
  "admin.insights.signupToBuild":       "Inscription a build",
  "admin.insights.buildSuccessRate":    "Taux de reussite des builds",
  "admin.insights.signupToDeploy":      "Inscription a deploiement",
  "admin.insights.usersNoProject":      "Utilisateurs sans projet",
  "admin.insights.failurePatternsTitle":"Modeles d'echec de build",
  "admin.insights.last30d":             "(30j)",
  "admin.insights.noBuildFailures":     "Aucun echec de build sur les 30 derniers jours.",
  "admin.insights.safetyEventsTitle":   "Evenements de securite",
  "admin.insights.noSafetyEvents":      "Aucun evenement de securite sur les 30 derniers jours.",
  "admin.insights.colEventType":        "Type d'evenement",
  "admin.insights.colSeverity":         "Severite",
  "admin.insights.colCount":            "Nombre",
  "admin.insights.topFailingProjects":  "Projets avec le plus d'echecs",
  "admin.insights.noProjectFailures":   "Aucun echec de projet enregistre.",
  "admin.insights.colProject":          "Projet",
  "admin.insights.colOwner":            "Proprietaire",
  "admin.insights.colFailures":         "Echecs",
  "admin.insights.topFailingUsers":     "Utilisateurs avec le plus d'echecs",
  "admin.insights.noUserFailures":      "Aucun echec d'utilisateur enregistre.",
  "admin.insights.colUser":             "Utilisateur",
  "admin.insights.colFailedBuilds":     "Builds echoues",

  // Admin Feature Flags tab
  "admin.featureFlags.toggleFailed":          "Le basculement a echoue",
  "admin.featureFlags.deleteConfirm":         "Supprimer l'indicateur \"{key}\"?\nCela ne peut pas etre annule.",
  "admin.featureFlags.deleteFailed":          "La suppression a echoue",
  "admin.featureFlags.keyNameRequired":       "La cle et le nom sont obligatoires.",
  "admin.featureFlags.rolloutRange":          "Le deploiement doit etre 0-100.",
  "admin.featureFlags.saveFailed":            "L'enregistrement a echoue.",
  "admin.featureFlags.flagCreated":           "Indicateur cree.",
  "admin.featureFlags.loading":               "Chargement des indicateurs de fonctionnalite...",
  "admin.featureFlags.intro":                 "Activez les fonctionnalites globalement ou deployez-les a un pourcentage d'utilisateurs. Tous les changements sont enregistres dans le journal d'audit.",
  "admin.featureFlags.cancel":                "Annuler",
  "admin.featureFlags.newFlag":               "Nouvel indicateur",
  "admin.featureFlags.newFlagTitle":          "Nouvel indicateur de fonctionnalite",
  "admin.featureFlags.keyLabel":              "Cle",
  "admin.featureFlags.keyPlaceholder":        "ex. new_dashboard",
  "admin.featureFlags.keyHelp":               "Minuscules et traits de soulignement uniquement. Utilise dans le code.",
  "admin.featureFlags.displayNameLabel":      "Nom affiche",
  "admin.featureFlags.displayNamePlaceholder":"Nouveau tableau de bord",
  "admin.featureFlags.descriptionLabel":      "Description",
  "admin.featureFlags.descriptionPlaceholder":"Ce que controle cet indicateur",
  "admin.featureFlags.rolloutLabel":          "Deploiement % (0 = personne, 100 = tout le monde)",
  "admin.featureFlags.creating":              "Creation...",
  "admin.featureFlags.createFlag":            "Creer l'indicateur",
  "admin.featureFlags.tableTitle":            "Indicateurs de fonctionnalite",
  "admin.featureFlags.noFlags":               "Aucun indicateur de fonctionnalite pour le moment. Creez-en un ci-dessus.",
  "admin.featureFlags.colKey":                "Cle",
  "admin.featureFlags.colName":               "Nom",
  "admin.featureFlags.colDescription":        "Description",
  "admin.featureFlags.colEnabled":            "Active",
  "admin.featureFlags.colRollout":            "Deploiement %",
  "admin.featureFlags.colUpdated":            "Mis a jour",
  "admin.featureFlags.colActions":            "Actions",
  "admin.featureFlags.disable":               "Desactiver",
  "admin.featureFlags.enable":                "Activer",
  "admin.featureFlags.setRollout":            "Definir le pourcentage de deploiement",

  // Admin Logs tab
  "admin.logs.loading":            "Chargement des journaux...",
  "admin.logs.loadingShort":       "Chargement...",
  "admin.logs.filterAll":          "Tous",
  "admin.logs.filterBuildErrors":  "Erreurs de build",
  "admin.logs.filterSafety":       "Securite",
  "admin.logs.filterAudit":        "Audit",
  "admin.logs.filterTransactions": "Transactions",
  "admin.logs.typeBuildError":     "erreur de build",
  "admin.logs.typeSafety":         "securite",
  "admin.logs.typeAudit":          "audit",
  "admin.logs.typeTransaction":    "transaction",
  "admin.logs.filterByEmail":      "Filtrer par e-mail...",
  "admin.logs.search":             "Rechercher",
  "admin.logs.clear":              "Effacer",
  "admin.logs.title":              "Journal d'evenements unifie",
  "admin.logs.colDate":            "Date",
  "admin.logs.colType":            "Type",
  "admin.logs.colUser":            "Utilisateur",
  "admin.logs.colSeverity":        "Severite",
  "admin.logs.colSummary":         "Resume",
  "admin.logs.noEvents":           "Aucun evenement de journal trouve.",
  "admin.logs.pageInfo":           "Page {page} - {count} evenements",
  "admin.logs.filteredBy":         "· filtre par \"{email}\"",
  "admin.logs.prev":               "Precedent",
  "admin.logs.next":               "Suivant",

  // Admin Simulate tab
  "admin.simulate.notRun":              "Non execute",
  "admin.simulate.running":             "Execution...",
  "admin.simulate.linkFailed":          "Echec de la generation du lien.",
  "admin.simulate.grantStepFailed":     "L'etape d'octroi a echoue: {error}",
  "admin.simulate.unknownError":        "Erreur inconnue",
  "admin.simulate.acuRoundTripPassed":  "Aller-retour credits reussi: l'octroi de +1 credit a {email} a reussi, remboursement applique.",
  "admin.simulate.acuNoTxId":            "L'octroi a {email} n'a renvoye aucun id de transaction, donc rien n'a ete rembourse. Verifiez ce compte avant de relancer.",
  "admin.simulate.acuRefundFailed":      "1 ACU accorde a {email} mais le remboursement a echoue : {error}. Ce 1 ACU est toujours sur le compte.",
  "admin.simulate.title":               "Simulation et verification de l'etat",
  "admin.simulate.subtitle":            "Verifiez la connectivite et lancez des diagnostics. Le test ACU accorde puis rembourse 1 ACU reel sur le compte indique.",
  "admin.simulate.healthCheckTitle":    "Verification de l'etat du systeme",
  "admin.simulate.passing":             "{pass}/{total} reussis",
  "admin.simulate.runChecks":           "Lancer les verifications",
  "admin.simulate.runChecksHint":       "Cliquez sur \"Lancer les verifications\" pour tester toutes les RPC admin critiques et l'Edge Function.",
  "admin.simulate.journeyTitle":        "Simulation de parcours utilisateur",
  "admin.simulate.journeyHelp":         "Generez un lien de connexion d'1 heure pour tout utilisateur afin de simuler son experience dans une fenetre privee. Cela NE change PAS son compte.",
  "admin.simulate.userEmail":           "E-mail de l'utilisateur",
  "admin.simulate.generate":            "Generer",
  "admin.simulate.copyLink":            "Copier le lien",
  "admin.simulate.linkNote":            "Ouvrez en navigation privee. Expire dans 1 heure. L'utilisateur N'est PAS notifie.",
  "admin.simulate.acuTestTitle":        "Test d'octroi/remboursement d'credits",
  "admin.simulate.acuTestHelp":         "Accorde 1 credits de test a un utilisateur puis le rembourse immediatement. Verifie tout le pipeline de facturation de bout en bout sans changements permanents de solde.",
  "admin.simulate.targetUserEmail":     "E-mail de l'utilisateur cible",
  "admin.simulate.test":                "Tester",
  "admin.simulate.acuTestNote":         "Toutes les actions de test sont enregistrees dans le journal d'audit avec le motif \"system_test_grant\" / \"system_test_cleanup\".",

  // Admin Database tab
  "admin.database.loadError":      "Impossible de charger l'etat du schema.",
  "admin.database.applyError":     "Impossible d'appliquer le bootstrap.",
  "admin.database.title":          "Schema de base de donnees",
  "admin.database.allPresent":     "tout present",
  "admin.database.missingCount":   "{count} manquants",
  "admin.database.refresh":        "Actualiser",
  "admin.database.applyBootstrap": "Appliquer le bootstrap",
  "admin.database.noRows":         "Aucune ligne pour le moment. Elles apparaissent ici une fois que cette table contient des donnees.",
  "admin.database.present":        "present",
  "admin.database.missing":        "manquant",
  "admin.database.helpText":       "L'application est idempotente et additive (cree uniquement si absent). Elle ne supprime jamais une table, une colonne ou une ligne. Necessite DATABASE_URL sur le serveur; sinon, appliquez le SQL manuellement dans l'editeur SQL de Supabase.",

  /* Top bar (brand link, command palette) */
  "topbar.home":        "Accueil Nuvra",
  "topbar.searchAria":  "Rechercher des commandes",
  "topbar.searchTitle": "Rechercher des commandes (Ctrl/Cmd + K)",

  /* Sidebar projects group (live project navigation) */
  "sidebarProjects.nBuilding":        "{n} en cours",
  "sidebarProjects.newProject":       "Nouveau projet",
  "sidebarProjects.loading":          "Chargement",
  "sidebarProjects.empty":            "Aucun projet pour le moment. Lancez un build →",
  "sidebarProjects.status.building":  "En cours",
  "sidebarProjects.status.live":      "En ligne",
  "sidebarProjects.status.idle":      "Inactif",
  "sidebarProjects.moreOnDashboard":  "+{n} de plus sur le tableau de bord",

  /* Share project dialog (owner-facing share-link management) */
  "share.title":                 "Partager \"{name}\"",
  "share.description":           "Toute personne connectee qui ouvre un lien devient membre avec ce role. Revoquez un lien a tout moment.",
  "share.roleViewer":           "Lecteur",
  "share.roleEditor":           "Editeur",
  "share.createLink":           "Creer un lien de partage",
  "share.copyNow":              "Copiez ce lien maintenant. Pour des raisons de securite, il ne sera plus affiche.",
  "share.activeLinks":          "Liens actifs",
  "share.noActiveLinks":        "Aucun lien de partage actif.",
  "share.usedN":                "{n} utilisations",
  "share.unused":               "inutilise",
  "share.expiresSuffix":        " · expire {date}",
  "share.revokeLink":           "Revoquer le lien",
  "share.toast.createFailTitle":"Impossible de creer le lien",
  "share.toast.createFailBody": "Seul le proprietaire du projet peut le partager.",
  "share.toast.copyFailTitle":  "Echec de la copie",
  "share.toast.copyFailBody":   "Copiez le lien manuellement.",
  "share.toast.revokeFailTitle":"Impossible de revoquer",
  "share.toast.revokeFailBody": "Reessayez.",

  /* Command palette (global command bar, search/navigate/build) */
  "cmdk.section.navigate":      "Naviguer",
  "cmdk.section.actions":       "Actions",
  "cmdk.section.favorites":     "Favoris",
  "cmdk.section.suggested":     "Suggeres",
  "cmdk.section.recent":        "Recents",
  "cmdk.searchPlaceholder":     "Recherchez des commandes, naviguez ou decrivez ce que vous voulez construire...",
  "cmdk.voiceMode":             "Mode vocal",
  "cmdk.voiceInput":            "Entree vocale",
  "cmdk.undoLabel":             "Annuler : {label}",
  "cmdk.confirmLabel":          "Confirmer : {label} ?",
  "cmdk.confirm":               "Confirmer",
  "cmdk.cancel":                "Annuler",
  "cmdk.aiBuild":               "Construire avec l'IA",
  "cmdk.buildWithAI":           "Construire avec l'IA",
  "cmdk.macro":                 "Macro",
  "cmdk.stepsCount":            "{n} etapes",
  "cmdk.noMatch":               "Aucune commande ne correspond a \"{query}\"",
  "cmdk.noMatchHint":           "Decrivez ce que vous voulez construire ou utilisez \"/\" pour declencher une macro",
  "cmdk.emptyHint":             "Recherchez des commandes, naviguez ou decrivez quoi construire",
  "cmdk.hint.nav":              "Naviguer",
  "cmdk.hint.run":              "Executer",
  "cmdk.hint.undo":             "Annuler",
  "cmdk.hint.macro":            "Macro",
  "cmdk.voiceBadge":            "🎙 Voix",
  "cmdk.toast.build":           "→ Construire",
  "cmdk.toast.executing":       "Execution...",
  "cmdk.toast.undone":          "↩ Annule",
  "cmdk.toast.nothingToUndo":   "Rien a annuler",
  "cmdk.toast.addedFavorite":   "⭐ Ajoute aux favoris",
  "cmdk.toast.removedFavorite": "Retire des favoris",
  "cmdk.toast.listening":       "🎙 Ecoute...",
  "cmdk.toast.sayCommand":      "Dites une commande ou decrivez quoi construire",
  "cmdk.toast.voiceCaptured":   "🎙 Voix capturee",
  "cmdk.toast.processing":      "Traitement de la commande...",
  "cmdk.toast.macro":           "⚡ Macro : {name}",
  "cmdk.toast.executedSteps":   "{n} etapes executees",

  /* Site footer (marketing footer, navigation + legal) */
  "footer.section.platform":    "Plateforme",
  "footer.section.product":     "Produit",
  "footer.section.legal":       "Mentions legales",
  "footer.link.features":       "Fonctionnalites",
  "footer.link.howItWorks":     "Comment ca marche",
  "footer.link.useCases":       "Cas d'usage",
  "footer.link.liveDemo":       "Planificateur d'Architecture",
  "footer.link.dashboard":      "Tableau de bord",
  "footer.link.deploy":         "Deployer",
  "footer.link.billing":        "Facturation",
  "footer.link.privacyPolicy":  "Politique de confidentialite",
  "footer.link.termsOfService": "Conditions d'utilisation",
  "footer.link.acceptableUse":  "Usage acceptable",
  "footer.link.aiContentPolicy":"Politique de contenu IA",
  "footer.link.securityPolicy": "Politique de securite",
  "footer.social.community":    "Communaute",
  "footer.rightsReserved":      "Tous droits reserves.",
  "footer.bottom.privacy":      "Confidentialite",
  "footer.bottom.terms":        "Conditions",
  "footer.bottom.cookies":      "Cookies",

  /* Project backend menu (generated database viewer/editor popover) */
  "backendMenu.triggerTitle":   "Base de donnees et backend generes",
  "backendMenu.triggerAria":    "Base de donnees",
  "backendMenu.triggerLabel":   "Base de donnees",
  "backendMenu.heading":        "Backend genere",
  "backendMenu.tab.deploys":    "Deploiements",
  "backendMenu.tab.backups":    "Sauvegardes",
  "backendMenu.tab.audit":      "Audit",
  "backendMenu.tab.diagram":    "Schema",
  "backendMenu.status.liveDatabase": "Base de donnees active",
  "backendMenu.status.needsReview":  "A verifier",
  "backendMenu.status.schemaReady":  "Schema pret",
  "backendMenu.deployHistory":  "Historique des deploiements",
  "backendMenu.loading":        "Chargement",
  "backendMenu.noDeploys":      "Aucun deploiement pour le moment.",
  "backendMenu.open":           "ouvrir",
  "backendMenu.backupNow":      "Sauvegarder maintenant",
  "backendMenu.noBackups":      "Aucune sauvegarde pour le moment.",
  "backendMenu.confirm":        "confirmer",
  "backendMenu.cancel":         "annuler",
  "backendMenu.restore":        "restaurer",
  "backendMenu.restoreTitle":   "La restauration ecrase les lignes actuelles par cet instantane; les lignes ajoutees depuis sont conservees",
  "backendMenu.download":       "telecharger",
  "backendMenu.auditLog":       "Journal d'audit des donnees",
  "backendMenu.noChanges":      "Aucun changement enregistre pour le moment.",
  "backendMenu.deployed":       "Deploye",
  "backendMenu.deploying":      "Deploiement",
  "backendMenu.openLiveApp":    "Ouvrir l'app en direct",
  "backendMenu.loadingBackend": "Chargement du backend",
  "backendMenu.noTables":       "Aucune table pour le moment. Ajoutez-en une ci-dessous, ou Nuvra les cree quand il construit une app full-stack.",
  "backendMenu.pending":        "en attente",
  "backendMenu.column":         "colonne",
  "backendMenu.colTo":          "vers",
  "backendMenu.colUnique":      "unique",
  "backendMenu.restoreColTitle":"Restaurer (vos donnees ont ete conservees)",
  "backendMenu.removeColTitle": "Retirer de l'app (les donnees sont conservees, reversible)",
  "backendMenu.view":           "voir",
  "backendMenu.viewTitle":      "Parcourir les lignes (necessite une base de donnees materialisee)",
  "backendMenu.import":         "importer",
  "backendMenu.importTitle":    "Importer des lignes depuis un fichier CSV",
  "backendMenu.seed":           "remplir",
  "backendMenu.seedTitle":      "Inserer quelques lignes d'exemple (necessite une base de donnees materialisee)",
  "backendMenu.reference":      "reference",
  "backendMenu.required":       "obligatoire",
  "backendMenu.unique":         "unique",
  "backendMenu.indexed":        "indexe",
  "backendMenu.noReference":    "aucune reference",
  "backendMenu.referenceTitle": "Referencer une autre table",
  "backendMenu.add":            "Ajouter",
  "backendMenu.isolatedSchema": "Schema isole",
  "backendMenu.newTable":       "Nouvelle table",
  "backendMenu.field":          "champ",
  "backendMenu.createTable":    "Creer la table",
  "backendMenu.rebuildDeploy":  "Reconstruire et deployer",
  "backendMenu.rebuildTitle":   "Reconstruit l'app avec vos changements de schema et la deploie en direct (utilise des credits)",
  "backendMenu.ph.columnName":  "nom de colonne",
  "backendMenu.ph.enumValues":  "valeurs autorisees, separees par des virgules",
  "backendMenu.ph.min":         "min",
  "backendMenu.ph.minLength":   "longueur min",
  "backendMenu.ph.max":         "max",
  "backendMenu.ph.maxLength":   "longueur max",
  "backendMenu.ph.pattern":     "motif (regex, optionnel)",
  "backendMenu.ph.default":     "valeur par defaut (optionnel)",
  "backendMenu.ph.tableName":   "Nom de table (p. ex. Invoice)",
  "backendMenu.ph.fieldName":   "nom de champ",
  "backendMenu.unit.row":       "ligne",
  "backendMenu.unit.rows":      "lignes",
  "backendMenu.unit.table":     "table",
  "backendMenu.unit.tables":    "tables",
  "backendMenu.notice.tableCreated":    "Creee dans votre base de donnees.",
  "backendMenu.notice.savedNextBuild":  "Enregistre. S'applique a la prochaine construction.",
  "backendMenu.notice.columnAdded":     "Ajoutee a votre base de donnees.",
  "backendMenu.notice.columnRemoved":   "Retiree de l'app. Vos donnees sont conservees et le changement est reversible.",
  "backendMenu.notice.columnRestored":  "Colonne restauree.",
  "backendMenu.notice.relationshipAdded": "Relation ajoutee.",
  "backendMenu.notice.seededOne":       "{n} ligne d'exemple ajoutee dans {table}.",
  "backendMenu.notice.seededN":         "{n} lignes d'exemple ajoutees dans {table}.",
  "backendMenu.notice.backedUp":        "{rows} {rowLabel} sauvegardees sur {tables} {tableLabel}.",
  "backendMenu.notice.restored":        "{rows} {rowLabel} restaurees sur {tables} {tableLabel}.",
  "backendMenu.notice.imported":        "{imported} sur {total} {rowLabel} importees dans {table}.",
  "backendMenu.notice.importedSkipped": "{failed} ignorees.",
  "backendMenu.notice.rebuilding":      "Reconstruction et deploiement. Votre app en direct se met a jour a la fin.",
  "backendMenu.error.load":             "Impossible de charger le backend.",
  "backendMenu.error.addTable":         "Impossible d'ajouter la table.",
  "backendMenu.error.addColumn":        "Impossible d'ajouter la colonne.",
  "backendMenu.error.removeColumn":     "Impossible de retirer la colonne.",
  "backendMenu.error.restoreColumn":    "Impossible de restaurer la colonne.",
  "backendMenu.error.seed":             "Impossible de remplir la table.",
  "backendMenu.error.backup":           "Impossible de creer la sauvegarde.",
  "backendMenu.error.restoreBackup":    "Impossible de restaurer la sauvegarde.",
  "backendMenu.error.import":           "Impossible d'importer le CSV.",
  "backendMenu.error.insufficientCredits": "Credits insuffisants pour deployer.",
  "backendMenu.error.startDeploy":      "Impossible de demarrer le deploiement.",
  "backendMenu.audit.seed":             "a rempli",
  "backendMenu.audit.import":           "a importe",
  "backendMenu.audit.cellUpdate":       "a modifie une cellule",
  "backendMenu.audit.restore":          "a restaure une sauvegarde",
  "backendMenu.audit.backup":           "a sauvegarde",
  "backendMenu.audit.addTable":         "a ajoute une table",
  "backendMenu.audit.addColumn":        "a ajoute une colonne",
  "backendMenu.audit.removeColumn":     "a retire une colonne",
  "backendMenu.audit.restoreColumn":    "a restaure une colonne",
  "backendMenu.audit.addRelationship":  "a ajoute une relation",
  "backendMenu.audit.build":            "a construit l'app",
  "backendMenu.audit.buildFailed":      "echec de la construction",
  "backendMenu.audit.lockSection":      "a verrouille une page",
  "backendMenu.audit.unlockSection":    "a deverrouille une page",
  "backendMenu.audit.hidePage":         "a masque une page",
  "backendMenu.audit.showPage":         "a restaure une page",
  "backendMenu.audit.renamePage":       "a renomme une page",
  "backendMenu.audit.hideComponent":    "a masque un composant",
  "backendMenu.audit.showComponent":    "a restaure un composant",
  "backendMenu.audit.codeEdit":         "a modifie le code",
  "backendMenu.audit.codePush":         "a pousse vers GitHub",
  "backendMenu.audit.hostDeploy":       "a deploye",
  "backendMenu.audit.supabaseConnect":  "a connecte Supabase",

  /* Strategic memory panel (project reasoning artifacts) */
  "strategicMemory.loading":      "Chargement de la strategie",
  "strategicMemory.error":        "Impossible de charger la strategie de ce projet pour le moment. Elle apparaitra une fois accessible.",
  "strategicMemory.empty.title":  "Aucune strategie capturee pour le moment",
  "strategicMemory.empty.body":   "A mesure que Nuvra raisonne sur votre produit, son plan, sa decouverte et ses recommandations apparaissent ici et vous suivent sur tous vos appareils.",
  "strategicMemory.title":        "Strategie",
  "strategicMemory.subtitle":     "Ce que Nuvra comprend de ce produit, du concept aux recommandations.",
  "strategicMemory.confidence":   "{pct} de confiance",
  "strategicMemory.section.concept":         "Le concept",
  "strategicMemory.section.discovery":       "Decouverte",
  "strategicMemory.section.blueprint":       "Plan",
  "strategicMemory.section.implications":    "Implications",
  "strategicMemory.section.recommendations": "Recommandations",
  "strategicMemory.label.value":     "Valeur:",
  "strategicMemory.label.for":       "Pour:",
  "strategicMemory.label.distinct":  "Ce qui le distingue",
  "strategicMemory.label.problems":  "Problemes qu'il resout",
  "strategicMemory.label.domain":    "Domaine:",
  "strategicMemory.label.form":      "Forme:",
  "strategicMemory.label.readiness": "Maturite:",
  "strategicMemory.label.nextSteps": "Prochaines etapes",
  "strategicMemory.stat.modules":       "Modules",
  "strategicMemory.stat.pages":         "Pages",
  "strategicMemory.stat.models":        "Modeles",
  "strategicMemory.stat.roles":         "Roles",
  "strategicMemory.stat.workflows":     "Flux",
  "strategicMemory.stat.integrations":  "Integrations",
  "strategicMemory.stat.architectural": "Architecturales",
  "strategicMemory.stat.requiredCaps":  "Capacites requises",
  "strategicMemory.stat.risks":         "Risques",
  "strategicMemory.stat.dependencies":  "Dependances",

  /* Monetization menu (define plans end-users can buy) */
  "monetizationMenu.title":        "Monetisation",
  "monetizationMenu.monetize":     "Monetiser",
  "monetizationMenu.notOwner":     "Seul le proprietaire du projet peut configurer la monetisation.",
  "monetizationMenu.intro":        "Definissez ce que vos utilisateurs finaux peuvent acheter. Limitez les fonctions par cles de droits.",
  "monetizationMenu.enabled":      "Active",
  "monetizationMenu.featured":     "En vedette",
  "monetizationMenu.addPlan":      "Ajouter un plan",
  "monetizationMenu.save":         "Enregistrer",
  "monetizationMenu.saved":        "Enregistre.",
  "monetizationMenu.billingActive":"Facturation active pour {n} plan(s).",
  "monetizationMenu.addPriceId":   "Ajoutez un id de prix du fournisseur a un plan payant pour activer le paiement.",
  "monetizationMenu.error.save":   "Impossible d'enregistrer.",
  "monetizationMenu.interval.month": "/ mois",
  "monetizationMenu.interval.year":  "/ an",
  "monetizationMenu.interval.once":  "une fois",
  "monetizationMenu.ph.planName":     "Nom du plan",
  "monetizationMenu.ph.features":     "Fonctions (separees par des virgules)",
  "monetizationMenu.ph.entitlements": "Cles de droits (separees par des virgules)",
  "monetizationMenu.ph.priceId":      "Id de prix du fournisseur (pour le paiement)",

  /* Members menu (invite collaborators) */
  "membersMenu.title":        "Membres",
  "membersMenu.notOwner":     "Seul le proprietaire du projet peut gerer les membres.",
  "membersMenu.invite":       "Inviter",
  "membersMenu.invited":      "invite",
  "membersMenu.empty":        "Aucun collaborateur pour le moment. Invitez quelqu'un par e-mail.",
  "membersMenu.role.editor":  "Editeur",
  "membersMenu.role.viewer":  "Lecteur",
  "membersMenu.ph.email":     "collaborateur@email.com",
  "membersMenu.error.invite": "Impossible d'inviter",

  /* Marketplace menu (publish and clone templates) */
  "marketplaceMenu.title":         "Marketplace",
  "marketplaceMenu.tab.browse":    "Parcourir",
  "marketplaceMenu.tab.publish":   "Publier ce projet",
  "marketplaceMenu.search":        "Rechercher",
  "marketplaceMenu.empty":         "Aucun modele pour le moment. Publiez-en un depuis n'importe quel projet.",
  "marketplaceMenu.clones":        "{n} clones",
  "marketplaceMenu.clone":         "Cloner",
  "marketplaceMenu.publishIntro":  "Publiez la structure de ce projet comme modele reutilisable. Les secrets, donnees et domaines ne sont jamais inclus.",
  "marketplaceMenu.publish":       "Publier",
  "marketplaceMenu.published":     "Publie sur le marketplace.",
  "marketplaceMenu.titleTooShort": "Ajoutez un titre d'au moins 3 caracteres.",
  "marketplaceMenu.ph.search":      "Rechercher des modeles",
  "marketplaceMenu.ph.title":       "Titre du modele",
  "marketplaceMenu.ph.description":  "Que construit-il?",
  "marketplaceMenu.error.clone":   "Impossible de cloner.",
  "marketplaceMenu.error.publish": "Impossible de publier.",

  /* Observability menu (deployed app usage, errors, self-healing) */
  "observabilityMenu.triggerTitle": "Observabilite de l'app",
  "observabilityMenu.insights":     "Mesures",
  "observabilityMenu.appInsights":  "Mesures de l'app",
  "observabilityMenu.autoFix":      "Correction auto",
  "observabilityMenu.mode.fullAuto": "Tout auto",
  "observabilityMenu.mode.safe":     "Sur",
  "observabilityMenu.mode.manual":   "Manuel",
  "observabilityMenu.modeDesc.fullAuto": "Les corrections a risque faible et moyen s'appliquent automatiquement. Le risque eleve vous demande toujours d'abord.",
  "observabilityMenu.modeDesc.safe":     "Les corrections a risque faible s'appliquent automatiquement. Tout le reste est propose pour votre approbation.",
  "observabilityMenu.modeDesc.manual":   "Nuvra detecte les problemes et propose des corrections. Vous approuvez chaque correction.",
  "observabilityMenu.noIssues":     "Aucun probleme recurrent detecte. Le flux se met a jour a mesure que de vraies erreurs d'execution sont observees.",
  "observabilityMenu.risk":         "risque {risk}",
  "observabilityMenu.confidence":   "{n}% de confiance",
  "observabilityMenu.autoFixing":   "Correction en cours",
  "observabilityMenu.autoFixWhenKeyed":      "Correction auto quand l'IA est active",
  "observabilityMenu.autoFixWhenKeyedTitle": "Cette correction est eligible a l'application automatique; elle s'active une fois qu'une cle de fournisseur d'IA est configuree.",
  "observabilityMenu.approveFix":   "Approuver la correction",
  "observabilityMenu.dismiss":      "Ignorer",
  "observabilityMenu.noEvents":     "Aucun evenement pour le moment. Configurez {ingestVar} et {projectVar} dans votre app deployee pour signaler l'usage et les erreurs ici.",
  "observabilityMenu.stat.views":    "Vues",
  "observabilityMenu.stat.sessions": "Sessions",
  "observabilityMenu.stat.errors":   "Erreurs",
  "observabilityMenu.stat.logs":     "Journaux",
  "observabilityMenu.topPages":     "Pages principales",
  "observabilityMenu.errors":       "Erreurs",
  "observabilityMenu.fixThis":      "Corriger ceci",
  "observabilityMenu.recentLogs":   "Journaux recents",

  /* Build chat thread (work-order engineering log) */
  "bct.turn.you":              "Vous",
  "bct.turn.nuvra":            "Nuvra",
  "bct.turn.buildRequested":   "Compilation demandee",
  "bct.session.expand":             "Inspecter la session",
  "bct.wo.status.queued":           "En attente",
  "bct.wo.status.understanding":    "Analyse",
  "bct.wo.status.planning":         "Planification",
  "bct.wo.status.implementing":     "Implementation",
  "bct.wo.status.verifying":        "Verification",
  "bct.wo.status.completed":        "Termine",
  "bct.wo.status.blocked":          "Bloque",
  "bct.wo.status.failed":           "Echec",
  "bct.summary.files": "Fichiers",
  "bct.summary.validation": "Validation",
  "bct.summary.tests": "Tests",
  "bct.summary.preview": "Apercu",
  "bct.summary.passed": "reussie",
  "bct.summary.review": "a verifier",
  "bct.summary.available": "disponible",
  "bct.summary.none": "aucun",
  "bct.stage.artifactsGenerated":   "Fichiers du projet",
  "bct.stage.finalStatus":          "Resultat",
  "bct.stage.engineeringNote":      "Note",
  "bct.status.queued":              "En file",
  "bct.status.inProgress":          "En cours",
  "bct.status.failed":              "Echoue",
  "bct.status.cancelled":           "Annule",
  "bct.status.working":             "Nuvra travaille",
  "bct.status.planned":             "Planifie",
  "bct.turn.planRequested":         "Plan demande",
  "bct.plan.pages":                 "Pages",
  "bct.plan.dataModels":            "Modeles de donnees",
  "bct.plan.features":              "Fonctionnalites",
  "bct.stream.jumpToLatest":        "Aller au plus recent",
  "bct.status.completed":           "Termine",
  "bct.status.note":                "Note",
  "bct.blueprint.pages":            "Pages",
  "bct.blueprint.components":       "Composants",
  "bct.blueprint.models":           "Modeles",
  "bct.blueprint.routes":           "Routes",
  "bct.blueprint.auth":             "Authentification",
  "bct.blueprint.deploy":           "Deploiement",
  "bct.blueprint.fallbackTitle":    "Plan de l'application",
  "bct.blueprint.suggestedNext":    "Suggere ensuite",
  // Build credits used: the customer-facing record of what one build's credits went on.
  "bcu.title":                      "Credits utilises",
  "bcu.credits":                    "{count} credits",
  "bcu.note":                       "Repartis selon la part de travail de chaque etape de cette construction.",
  "bcu.noBreakdown":                "Aucun detail n'est disponible pour cette construction.",
  "bcu.zero":                       "Cette construction n'a utilise aucun credit.",
  "bcu.stage.understanding":        "Comprehension de votre demande",
  "bcu.stage.planning":             "Planification de la construction",
  "bcu.stage.writing":              "Ecriture de votre code",
  "bcu.stage.checking":             "Verification de votre application",
  "bcu.stage.fixing":               "Correction des problemes",
  "bcu.stage.other":                "Autres travaux",
  "bct.report.completed":           "Ingenierie terminee",
  "bct.report.completedWithBlockers": "Ingenierie terminee avec des blocages",
  "bct.report.blockersHint":        "La compilation est terminee, mais le deploiement est bloque jusqu a resolution.",
  "bct.report.scaffoldDraft":       "Brouillon genere (scaffold)",
  "bct.report.scaffoldDraftHint":   "Ceci est un brouillon deterministe, pas un logiciel genere par IA. Activez la generation par IA pour construire le vrai produit.",
  "bct.mode.aiGenerated":           "Genere par IA",
  "bct.mode.scaffoldDraft":         "Brouillon scaffold",
  "bct.report.openRuntime":         "Ouvrir le runtime",
  "bct.report.capabilities":        "Capacites",
  "bct.receipt.withBalance":        "Ce build a utilise {cost} credits. Il vous reste {balance} credits.",
  "bct.receipt.costOnly":           "Ce build a utilise {cost} credits.",
  "bct.security.passed":            "Securite verifiee : aucun probleme critique ({score}/100).",
  "bct.security.issues":            "Securite verifiee : {critical} critiques, {high} eleves signales.",
  "bct.report.decisions":           "Decisions prises par votre equipe",
  "bct.report.pages":               "Pages",
  "bct.report.blockers":            "Blocages",
  "bct.report.heading":             "Details du build",
  "bct.report.findings":          "Constats et reparations",
  "bct.report.verified":          "Verifie",
  "bct.report.artifact":          "Artefact",
  "bct.report.artifactVerified":  "Artefact verifie",
  "bct.report.remaining":         "Restant",
  "bct.report.next":              "Suite",
  "bct.report.implemented":         "Implemente",
  "bct.report.requested":      "Demande",
  "bct.report.changedFiles":   "Fichiers modifies",
  "bct.report.created":        "cree",
  "bct.report.modified":       "modifie",
  "bct.report.removed":        "supprime",
  "bct.report.andMoreFiles":   "et {n} de plus",
  "bct.report.filesWritten":        "{n} fichiers ecrits",
  "bct.report.details":             "Details",
  "bct.report.listen":              "Ecouter",
  "bct.report.stopListening":       "Arreter",
  "bct.report.nextInvite":          "Une suite a donner? Decrivez le changement et Nuvra construit sur cette application exacte.",
  "bct.report.hideDetails":         "Masquer les details",
  "bct.report.preview":             "Apercu",
  "bct.error.buildFailed":          "Echec du build",
  "bct.error.retry":                "Reessayer",
  "bct.error.rerun":                "Relancer l'ordre de travail",
  "bct.latestWorkOrder":            "Dernier ordre de travail",
  "bct.reuseRequestTitle":          "Reutiliser cette demande dans le compositeur",
  "bct.msg.edit":                   "Modifier",
  "bct.msg.copy":                   "Copier",
  "bct.msg.copied":                 "Copie",
  "bct.msg.share":                  "Partager",
  "bct.msg.like":                   "Bonne reponse",
  "bct.msg.dislike":                "Mauvaise reponse",
  "bct.history.loadEarlier":     "Charger l'historique anterieur",
  "bct.history.loadingEarlier":  "Chargement de l'historique...",
  "bct.msg.showMore":               "Voir plus",
  "bct.msg.showLess":               "Voir moins",
  "bct.msg.rerun":                  "Reutiliser cette demande",
  "bct.buildRequestFallback":       "Demande de build",
  "bct.cap.auth":                   "Authentification",
  "bct.cap.search":                 "Recherche",
  "bct.cap.payments":               "Paiements",
  "bct.cap.realtime":               "Temps reel",
  "bct.cap.notifications":          "Notifications",
  "bct.cap.upload":                 "Televersement de fichiers",
  "bct.cap.export":                 "Export de donnees",
  "bct.cap.analytics":              "Analytique",
  "bct.cap.email":                  "E-mail",

  /* Extensions page */
  "ext.title":                      "Extensions",
  "ext.subtitle":                   "Ajoutez des capacites a votre projet. Vous accordez chaque permission explicitement.",
  "ext.noProject":                  "Ouvrez cette page depuis un projet pour installer et gerer ses extensions. Vous pouvez parcourir le registre ci-dessous.",
  "ext.loading":                    "Chargement des extensions...",
  "ext.errorLoad":                  "Impossible de charger les extensions.",
  "ext.installed":                  "Installees",
  "ext.installedNone":              "Aucune extension installee dans ce projet pour l'instant.",
  "ext.permissionsNone":            "Aucune permission accordee.",
  "ext.suspend":                    "Suspendre",
  "ext.activate":                   "Activer",
  "ext.uninstall":                  "Desinstaller",
  "ext.registry":                   "Registre",
  "ext.searchPlaceholder":          "Rechercher des extensions",
  "ext.typeAll":                    "Tous les types",
  "ext.search":                     "Rechercher",
  "ext.registryNone":               "Aucune extension publiee pour l'instant.",
  "ext.installOpenProject":         "Ouvrez un projet pour installer",
  "ext.installIncompat":            "Incompatible avec cette plateforme",
  "ext.install":                    "Installer",
  "ext.permRequested.one":          "{n} permission demandee",
  "ext.permRequested.other":        "{n} permissions demandees",
  "ext.installCount.one":           "{n} installation",
  "ext.installCount.other":         "{n} installations",
  "ext.modalTitle":                 "Accorder des permissions",
  "ext.modalDesc":                  "{title} demande l'acces. N'accordez que ce en quoi vous avez confiance.",
  "ext.paidNotice":                 "Extension payante. Achetez-la d'abord sur la marketplace. Une fois acquise, l'installation ici se complete.",
  "ext.deprecatedNotice":           "Cette version est obsolete mais fonctionne encore. Envisagez une version plus recente quand elle sera disponible.",
  "ext.permissionsRequestedNone":   "Cette extension ne demande aucune permission.",
  "ext.optional":                   "optionnel",
  "ext.cancel":                     "Annuler",
  "ext.installWithPerm.one":        "Installer avec {n} permission",
  "ext.installWithPerm.other":      "Installer avec {n} permissions",
  "ext.badgeVerified":              "Verifie",
  "ext.badgeCommunity":             "Communaute",
  "ext.badgeExperimental":          "Experimental",
  "ext.badgeIncompat":              "Incompatible",
  "ext.badgeDeprecated":            "Obsolete",
  "ext.badgePaid":                  "Payant",
  "ext.toastInstalled":             "Extension installee",
  "ext.toastPurchaseRequired":      "Achat requis",
  "ext.toastPurchaseDesc":          "Cette extension est payante. Achetez-la sur la marketplace, puis installez-la.",
  "ext.toastInstallFailed":         "Installation echouee",
  "ext.toastTryAgain":              "Reessayez.",
  "ext.toastActionFailed":          "Action echouee",
  "ext.toastPermFailed":            "Impossible de mettre a jour la permission",

  /* Capabilities page */
  "cap.page.label":                   "Plateforme",
  "cap.page.title":                   "Capacites de la plateforme",
  "cap.page.desc":                    "Chaque capacite est operationnelle. Construisez des logiciels entreprise reels, securises et multi-utilisateurs.",
  "cap.page.allLive":                 "Tout en production",
  "cap.filter.all":                   "Tout",

  "cap.aiGen.label":                  "Generation IA",
  "cap.aiGen.headline":               "Orchestration multi-agents, routage intelligent, consensus et copilote adaptatif.",
  "cap.aiGen.multiAgent.name":        "Orchestration multi-agents",
  "cap.aiGen.multiAgent.desc":        "Coordonne plusieurs agents specialises en parallele pour produire des resultats de qualite superieure.",
  "cap.aiGen.multiAgent.badge":       "Production",
  "cap.aiGen.aiRouting.name":         "Routage intelligent",
  "cap.aiGen.aiRouting.desc":         "Selectionne automatiquement le fournisseur optimal selon le type de tache et le budget.",
  "cap.aiGen.aiRouting.badge":        "Production",
  "cap.aiGen.consensus.name":         "Consensus d'agents",
  "cap.aiGen.consensus.desc":         "Plusieurs agents votent sur la meilleure sortie pour reduire les hallucinations.",
  "cap.aiGen.consensus.badge":        "Production",
  "cap.aiGen.adaptive.name":          "Comportement adaptatif",
  "cap.aiGen.adaptive.desc":          "Les agents ajustent leur strategie en fonction des resultats en temps reel.",
  "cap.aiGen.adaptive.badge":         "Production",
  "cap.aiGen.arbitration.name":       "Arbitrage de conflit",
  "cap.aiGen.arbitration.desc":       "Resout les desaccords entre agents par un arbitre dedie.",
  "cap.aiGen.arbitration.badge":      "Production",
  "cap.aiGen.copilot.name":           "Copilote fondateur",
  "cap.aiGen.copilot.desc":           "Assistance contextuelle continue pendant la construction et le deploiement.",
  "cap.aiGen.copilot.badge":          "Production",
  "cap.aiGen.template.name":          "Bibliotheque de gabarits",
  "cap.aiGen.template.desc":          "Gabarits de domaines reels avec modelisation des donnees et logique metier.",
  "cap.aiGen.template.badge":         "Production",
  "cap.aiGen.marketplace.name":       "Marche de composants",
  "cap.aiGen.marketplace.desc":       "Composants verifies et reutilisables partages entre projets.",
  "cap.aiGen.marketplace.badge":      "Production",

  "cap.deploy.label":                 "Deploiement",
  "cap.deploy.headline":              "Deploiement full-stack, machine d'etat, auto-reparation et livraisons atomiques.",
  "cap.deploy.fullStack.name":        "Deploiement full-stack",
  "cap.deploy.fullStack.desc":        "Frontend, backend, base de donnees et services deployes ensemble en une operation.",
  "cap.deploy.fullStack.badge":       "Production",
  "cap.deploy.stateMachine.name":     "Machine d'etat de deploiement",
  "cap.deploy.stateMachine.desc":     "Chaque deploiement suit un graphe d'etat strict avec verification a chaque etape.",
  "cap.deploy.stateMachine.badge":    "Production",
  "cap.deploy.healing.name":          "Auto-reparation",
  "cap.deploy.healing.desc":          "Detecte et corrige automatiquement les defaillances courantes sans intervention humaine.",
  "cap.deploy.healing.badge":         "Production",
  "cap.deploy.repair.name":           "Planification de reparation",
  "cap.deploy.repair.desc":           "Genere un plan de correction structure pour chaque echec de build ou de deploiement.",
  "cap.deploy.repair.badge":          "Production",
  "cap.deploy.atomic.name":           "Livraisons atomiques",
  "cap.deploy.atomic.desc":           "Le deploiement reussit completement ou est annule: jamais d'etat partiel.",
  "cap.deploy.atomic.badge":          "Production",
  "cap.deploy.cdn.name":              "Distribution mondiale",
  "cap.deploy.cdn.desc":              "Assets diffuses depuis des points de presence proches des utilisateurs finaux.",
  "cap.deploy.cdn.badge":             "Production",
  "cap.deploy.governance.name":       "Gouvernance de deploiement",
  "cap.deploy.governance.desc":       "Historique complet, signatures et piste d'audit pour chaque publication.",
  "cap.deploy.governance.badge":      "Production",
  "cap.deploy.retryLedger.name":      "Registre de tentatives",
  "cap.deploy.retryLedger.desc":      "Chaque tentative est enregistree avec cause, resultat et horodatage.",
  "cap.deploy.retryLedger.badge":     "Production",
  "cap.deploy.garbage.name":          "Nettoyage des artefacts",
  "cap.deploy.garbage.desc":          "Supprime automatiquement les anciens artefacts de build pour limiter les couts de stockage.",
  "cap.deploy.garbage.badge":         "Production",

  "cap.mobile.label":                 "Mobile",
  "cap.mobile.headline":              "AAB, APK, iOS, PWA et packaging natif avec gouvernance de signature.",
  "cap.mobile.aab.name":              "Android App Bundle",
  "cap.mobile.aab.desc":              "Genere des AAB optimises prets pour la publication sur le Google Play Store.",
  "cap.mobile.aab.badge":             "Production",
  "cap.mobile.apk.name":              "APK de test",
  "cap.mobile.apk.desc":              "Produit des APK de debug et release pour tests directs sur appareil.",
  "cap.mobile.apk.badge":             "Production",
  "cap.mobile.ios.name":              "Packaging iOS",
  "cap.mobile.ios.desc":              "Genere des archives IPA avec configuration de provisionnement pour l'App Store.",
  "cap.mobile.ios.badge":             "Production",
  "cap.mobile.pwa.name":              "Application web progressive",
  "cap.mobile.pwa.desc":              "Manifeste, service worker, icones et precache pour une experience installable.",
  "cap.mobile.pwa.badge":             "Production",
  "cap.mobile.concurrent.name":       "Builds paralleles",
  "cap.mobile.concurrent.desc":       "Android et iOS sont construits simultanement pour reduire le temps d'attente.",
  "cap.mobile.concurrent.badge":      "Production",
  "cap.mobile.sdk.name":              "SDK embarque",
  "cap.mobile.sdk.desc":              "SDK natif integre permettant les mises a jour en direct et la collecte de telemetrie.",
  "cap.mobile.sdk.badge":             "Production",
  "cap.mobile.governor.name":         "Gouverneur de build",
  "cap.mobile.governor.desc":         "Limite les builds simultanes par projet pour eviter la surcharge des ressources.",
  "cap.mobile.governor.badge":        "Production",
  "cap.mobile.signing.name":          "Gestion des signatures",
  "cap.mobile.signing.desc":          "Keystores et certificats stockes de maniere chiffree avec rotation automatique.",
  "cap.mobile.signing.badge":         "Production",
  "cap.mobile.icons.name":            "Generation d'icones",
  "cap.mobile.icons.desc":            "Produit toutes les tailles d'icones requises pour Android et iOS a partir d'une source.",
  "cap.mobile.icons.badge":           "Production",
  "cap.mobile.manifest.name":         "Manifeste d'application",
  "cap.mobile.manifest.desc":         "Genere et valide les manifestes Android et iOS avec les metadonnees correctes.",
  "cap.mobile.manifest.badge":        "Production",
  "cap.mobile.telemetry.name":        "Telemetrie de build",
  "cap.mobile.telemetry.desc":        "Duree, taille, erreurs et metriques de chaque build mobile enregistrees.",
  "cap.mobile.telemetry.badge":       "Production",

  "cap.store.label":                  "Stores",
  "cap.store.headline":               "Publication sur Google Play et App Store avec gestion de cycle de vie complete.",
  "cap.store.google.name":            "Google Play",
  "cap.store.google.desc":            "Publie les AAB directement sur le Google Play Store via l'API officielle.",
  "cap.store.google.badge":           "Production",
  "cap.store.appstore.name":          "App Store d'Apple",
  "cap.store.appstore.desc":          "Soumet les IPA a l'App Store via App Store Connect avec gestion des metadonnees.",
  "cap.store.appstore.badge":         "Production",
  "cap.store.staged.name":            "Deploiement progressif",
  "cap.store.staged.desc":            "Publie a un pourcentage croissant d'utilisateurs avec suivi des metriques.",
  "cap.store.staged.badge":           "Production",
  "cap.store.vault.name":             "Coffre des secrets",
  "cap.store.vault.desc":             "Cles API et secrets de store stockes chiffres et jamais exposes en clair.",
  "cap.store.vault.badge":            "Production",
  "cap.store.release.name":           "Gestion des versions",
  "cap.store.release.desc":           "Historique complet des versions avec notes, dates et statuts de publication.",
  "cap.store.release.badge":          "Production",
  "cap.store.rollback.name":          "Retour arriere de version",
  "cap.store.rollback.desc":          "Repasse a une version anterieure en un clic si une regression est detectee.",
  "cap.store.rollback.badge":         "Production",
  "cap.store.keyGov.name":            "Gouvernance des cles",
  "cap.store.keyGov.desc":            "Rotation, audit et expiration automatiques des cles de signature de store.",
  "cap.store.keyGov.badge":           "Production",
  "cap.store.provenance.name":        "Provenance de publication",
  "cap.store.provenance.desc":        "Chaque version est liee au build source, au commit git et au fondateur qui a publie.",
  "cap.store.provenance.badge":       "Production",
  "cap.store.manifestZip.name":       "Archive de soumission",
  "cap.store.manifestZip.desc":       "Produit une archive complete et conforme aux exigences de chaque store.",
  "cap.store.manifestZip.badge":      "Production",

  "cap.exec.label":                   "Execution",
  "cap.exec.headline":                "Moteur d'execution avec isolation des defaillances, files asynchrones et planificateur de tentatives.",
  "cap.exec.renderer.name":           "Moteur de rendu",
  "cap.exec.renderer.desc":           "Rend le frontend en production avec hydratation et prise en charge SSR.",
  "cap.exec.renderer.badge":          "Production",
  "cap.exec.asyncQueue.name":         "File de taches asynchrones",
  "cap.exec.asyncQueue.desc":         "Les taches longues s'executent en arriere-plan sans bloquer l'interface utilisateur.",
  "cap.exec.asyncQueue.badge":        "Production",
  "cap.exec.aiWorker.name":           "Worker IA",
  "cap.exec.aiWorker.desc":           "Traite les appels de generation IA dans des workers isoles avec timeout et retry.",
  "cap.exec.aiWorker.badge":          "Production",
  "cap.exec.faultIsolation.name":     "Isolation des defaillances",
  "cap.exec.faultIsolation.desc":     "Chaque tache echoue de maniere isolee sans affecter les autres executions en cours.",
  "cap.exec.faultIsolation.badge":    "Production",
  "cap.exec.heartbeat.name":          "Surveillance par battement",
  "cap.exec.heartbeat.desc":          "Les workers envoient des signaux de vie periodiques; les workers silencieux sont redemarres.",
  "cap.exec.heartbeat.badge":         "Production",
  "cap.exec.retryScheduler.name":     "Planificateur de tentatives",
  "cap.exec.retryScheduler.desc":     "Replanifie les taches echouees avec backoff exponentiel et seuil d'echec.",
  "cap.exec.retryScheduler.badge":    "Production",
  "cap.exec.retryConsumer.name":      "Consommateur de tentatives",
  "cap.exec.retryConsumer.desc":      "Traite la file de tentatives en respectant les quotas et priorites.",
  "cap.exec.retryConsumer.badge":     "Production",
  "cap.exec.eventBridge.name":        "Bus d'evenements",
  "cap.exec.eventBridge.desc":        "Distribue les evenements systeme entre services avec garantie de livraison.",
  "cap.exec.eventBridge.badge":       "Production",

  "cap.observ.label":                 "Observabilite",
  "cap.observ.headline":              "Journaux de build, telemetrie de version, validateur de runtime et tableau de bord de statut.",
  "cap.observ.bootValidator.name":    "Validateur de demarrage",
  "cap.observ.bootValidator.desc":    "Verifie l'integrite complete de la configuration au demarrage avant de traiter les requetes.",
  "cap.observ.bootValidator.badge":   "Production",
  "cap.observ.releaseTelemetry.name": "Telemetrie de version",
  "cap.observ.releaseTelemetry.desc": "Collecte duree, taille et erreurs pour chaque version publiee.",
  "cap.observ.releaseTelemetry.badge":"Production",
  "cap.observ.auditLog.name":         "Journal d'audit",
  "cap.observ.auditLog.desc":         "Chaque action utilisateur et systeme est enregistree avec horodatage et identite.",
  "cap.observ.auditLog.badge":        "Production",
  "cap.observ.platformStatus.name":   "Statut de la plateforme",
  "cap.observ.platformStatus.desc":   "Tableau de bord en temps reel de l'etat de tous les sous-systemes.",
  "cap.observ.platformStatus.badge":  "Production",
  "cap.observ.buildLog.name":         "Journal de build",
  "cap.observ.buildLog.desc":         "Flux de logs en direct pour chaque build avec niveaux de severite et filtrage.",
  "cap.observ.buildLog.badge":        "Production",
  "cap.observ.runtimeValidator.name": "Validateur de runtime",
  "cap.observ.runtimeValidator.desc": "Verifie la conformite du schema et l'integrite des donnees en production.",
  "cap.observ.runtimeValidator.badge":"Production",
  "cap.observ.integrity.name":        "Verification d'integrite",
  "cap.observ.integrity.desc":        "Empreintes cryptographiques verifiees pour chaque artefact publie.",
  "cap.observ.integrity.badge":       "Production",
  "cap.observ.metrics.name":          "Metriques d'utilisation",
  "cap.observ.metrics.desc":          "Suivi des credits, du cout de generation et des tendances d'utilisation par projet.",
  "cap.observ.metrics.badge":         "Production",

  "cap.security.label":               "Securite",
  "cap.security.headline":            "Bac a sable, coffre de secrets, isolation par locataire et chiffrement de champs.",
  "cap.security.sandbox.name":        "Bac a sable d'execution",
  "cap.security.sandbox.desc":        "Le code genere s'execute dans un environnement isole sans acces au systeme hote.",
  "cap.security.sandbox.badge":       "Production",
  "cap.security.vault.name":          "Coffre de secrets",
  "cap.security.vault.desc":          "Tous les secrets sont stockes chiffres et jamais exposes en clair dans les logs.",
  "cap.security.vault.badge":         "Production",
  "cap.security.adminGate.name":      "Portail administrateur",
  "cap.security.adminGate.desc":      "Acces administrateur verifie avec multi-facteurs et journalisation de session.",
  "cap.security.adminGate.badge":     "Production",
  "cap.security.authConn.name":       "Connexion d'authentification",
  "cap.security.authConn.desc":       "Integre les fournisseurs d'identite externes avec verification stricte des jetons.",
  "cap.security.authConn.badge":      "Production",
  "cap.security.rateLimit.name":      "Limitation de debit",
  "cap.security.rateLimit.desc":      "Protege toutes les routes publiques contre les abus avec backoff progressif.",
  "cap.security.rateLimit.badge":     "Production",
  "cap.security.auditSvc.name":       "Service d'audit",
  "cap.security.auditSvc.desc":       "Enregistre et signe chaque evenement de securite pour les besoins de conformite.",
  "cap.security.auditSvc.badge":      "Production",
  "cap.security.fieldEnc.name":       "Chiffrement de champs",
  "cap.security.fieldEnc.desc":       "Les champs sensibles sont chiffres au repos et en transit, separement des donnees applicatives.",
  "cap.security.fieldEnc.badge":      "Production",
  "cap.security.roomAuth.name":       "Autorisation de salle",
  "cap.security.roomAuth.desc":       "Chaque espace collaboratif exige une autorisation explicite avant toute jointure.",
  "cap.security.roomAuth.badge":      "Production",

  "cap.realtime.label":               "Temps reel",
  "cap.realtime.headline":            "Serveur SSE, flux de logs, abonnements base de donnees et hooks d'evenements.",
  "cap.realtime.server.name":         "Serveur SSE",
  "cap.realtime.server.desc":         "Pousse les evenements serveur vers les clients en temps reel via une connexion persistante.",
  "cap.realtime.server.badge":        "Production",
  "cap.realtime.logStream.name":      "Flux de logs",
  "cap.realtime.logStream.desc":      "Diffuse les logs de build et de deploiement en direct dans l'interface utilisateur.",
  "cap.realtime.logStream.badge":     "Production",
  "cap.realtime.dbSubs.name":         "Abonnements base de donnees",
  "cap.realtime.dbSubs.desc":         "Les modifications de donnees declenchent des mises a jour en temps reel dans tous les clients connectes.",
  "cap.realtime.dbSubs.badge":        "Production",
  "cap.realtime.hook.name":           "Hooks d'evenements",
  "cap.realtime.hook.desc":           "Les evenements systeme declenchent des webhooks vers les services externes configures.",
  "cap.realtime.hook.badge":          "Production",

  "cap.game.label":                   "Jeu",
  "cap.game.headline":                "SDK de jeu, multijoueur, economie virtuelle, analyse comportementale et equilibrage IA.",
  "cap.game.gameSdk.name":            "SDK de jeu",
  "cap.game.gameSdk.desc":            "Bibliotheque client pour boucle de jeu, entrees, rendu et gestion d'etat.",
  "cap.game.gameSdk.badge":           "Production",
  "cap.game.multiplayer.name":        "Multijoueur",
  "cap.game.multiplayer.desc":        "Sessions en temps reel avec synchronisation d'etat et reconciliation de conflits.",
  "cap.game.multiplayer.badge":       "Production",
  "cap.game.economy.name":            "Economie virtuelle",
  "cap.game.economy.desc":            "Gestion des monnaies, inventaires et transactions in-app avec audit complet.",
  "cap.game.economy.badge":           "Production",
  "cap.game.analytics.name":          "Analyse comportementale",
  "cap.game.analytics.desc":          "Suit les parcours de joueurs, la retention et les evenements cles pour l'optimisation.",
  "cap.game.analytics.badge":         "Production",
  "cap.game.aiBalance.name":          "Equilibrage IA",
  "cap.game.aiBalance.desc":          "Ajuste dynamiquement les parametres de difficulte en fonction des donnees de joueurs.",
  "cap.game.aiBalance.badge":         "Production",
  "cap.game.scale.name":              "Montee en charge",
  "cap.game.scale.desc":              "Architecture auto-scalable pour supporter des pics de joueurs sans degradation.",
  "cap.game.scale.badge":             "Production",

  "cap.history.label":                "Historique",
  "cap.history.title":                "Historique des versions de la plateforme",
  "cap.history.desc":                 "Chaque mise en production verifiee et documentee.",

  "cap.release.storeRelease.title":   "Publication sur les stores d'applications",
  "cap.release.storeRelease.tag":     "Stores",
  "cap.release.storeRelease.desc":    "Publication directe sur Google Play et App Store avec gestion du cycle de vie complet.",
  "cap.release.mobileRelease.title":  "Builds mobiles natifs",
  "cap.release.mobileRelease.tag":    "Mobile",
  "cap.release.mobileRelease.desc":   "AAB, APK, IPA et PWA produits depuis un pipeline de build unique et unifie.",
  "cap.release.asyncRelease.title":   "Execution asynchrone",
  "cap.release.asyncRelease.tag":     "Execution",
  "cap.release.asyncRelease.desc":    "Files de taches, workers IA et planificateur de tentatives operationnels en production.",
  "cap.release.mediaRelease.title":   "Generation de contenu IA",
  "cap.release.mediaRelease.tag":     "IA",
  "cap.release.mediaRelease.desc":    "Images, sons et textes generes par IA integres au flux de build.",
  "cap.release.deployRelease.title":  "Deploiement avance",
  "cap.release.deployRelease.tag":    "Deploiement",
  "cap.release.deployRelease.desc":   "Deploiements atomiques avec auto-reparation, retour arriere et audit complet.",
  "cap.release.coreRelease.title":    "Fondations de la plateforme",
  "cap.release.coreRelease.tag":      "Coeur",
  "cap.release.coreRelease.desc":     "Authentification, facturation, RLS et generation de base mis en production.",

  "cap.footer.live":                  "Toutes les capacites sont actives en production",
  "cap.footer.status":                "Statut de la plateforme",

  /* LandingPage - Navbar */
  "landing.nav.features":         "Fonctionnalites",
  "landing.nav.howItWorks":       "Comment ca marche",
  "landing.nav.useCases":         "Cas d'usage",
  "landing.nav.tryIt":            "Planificateur",
  "landing.nav.architecture":     "Architecture",
  "landing.nav.pricing":     "Tarifs",
  "landing.nav.signIn":           "Se connecter",

  /* LandingPage - Hero */
  "landing.hero.badge":           "Plateforme Autonome d'Ingenierie Logicielle",
  "landing.hero.h1a":             "Decrivez-le.",
  "landing.hero.h1b":             "Nuvra le construit et le deploie.",
   "landing.hero.desc":            "Nuvra est une plateforme autonome d'ingenierie logicielle: elle concoit l'architecture, construit le code, valide le systeme et l'accompagne jusqu'au deploiement gouverne depuis une seule conversation.",
  "landing.hero.ctaPrimary":      "Commencer a Construire",
  "landing.hero.ctaSecondary":    "Voir en Direct",

  "landing.hero.pipelineLabel":  "Comment Nuvra exécute",
  "landing.hero.pipelineDesc":   "Une vue concise du pipeline logiciel autonome.",
  "landing.hero.pipelineStatus": "Pipeline actif",
  /* LandingPage - Product Explanation */
  "landing.explain.label":        "Ce que fait Nuvra",
  "landing.explain.h2":           "Pas d'assistance. De l'execution.",
  "landing.explain.desc":         "Decrivez ce que vous voulez construire. Nuvra genere le systeme complet et le deploie, sans etapes manuelles.",

  "landing.hero.proof.architecture": "Architecture d'abord",
  "landing.hero.proof.code":         "Code reel",
  "landing.hero.proof.validation":   "Validation automatisee",
  "landing.hero.proof.control":      "Controle de production",

  "landing.capabilities.label": "Au-dela du premier build",
  "landing.capabilities.h2": "La fabrique continue apres le prompt.",
  "landing.capabilities.desc": "Nuvra est plus qu'une generation. La plateforme accompagne le produit avec les builds mobiles, la publication, le deploiement, la memoire et le travail d'ingenierie connecte.",
  "landing.capability.mobile.title": "Builds mobiles",
  "landing.capability.mobile.desc": "Generez la sortie mobile avec le produit web, avec de vrais artefacts et des parcours de build.",
  "landing.capability.publishing.title": "Preparation pour les stores",
  "landing.capability.publishing.desc": "Preparez le produit pour la distribution avec metadonnees, documents de politique, captures et controles de preparation.",
  "landing.capability.externalDeploy.title": "Options de deploiement",
  "landing.capability.externalDeploy.desc": "Passez du preview aux cibles de deploiement prises en charge sans reconstruire le produit autour d'un autre stack.",
  "landing.capability.workspace.title": "Workspace d'ingenierie",
  "landing.capability.workspace.desc": "Exigences, architecture, work orders, builds, environnements, deploiements et integrations restent au meme endroit.",
  "landing.capability.memory.title": "Memoire et provenance",
  "landing.capability.memory.desc": "Le contexte du projet, le lignage, la provenance et l'historique d'ingenierie gardent le travail futur relie a ce qui precede.",
  "landing.capability.integrations.title": "Services connectes",
  "landing.capability.integrations.desc": "Utilisez des integrations gouvernees et des identifiants stockes pour relier la fabrique logicielle aux services dont votre produit depend.",

  /* LandingPage - Stats */
  "landing.live.builders":             "Createurs",
  "landing.live.projects":             "Projets crees",
  "landing.live.builds":               "Apps livrees",
  "landing.live.deployments":          "Deploiements",
  "landing.proof.label":                "Activite de la plateforme",
  "landing.proof.desc":                 "Mesuree a partir de la telemetrie publique de Nuvra.",
  "landing.lifecycle.label":            "Cycle de vie Nuvra",
  "landing.lifecycle.summary":          "Architecture → validation → deploiement",
  "landing.lifecycle.note":             "Flux produit presente a titre d'illustration. Le planificateur interactif ci-dessous est l'experience reelle connectee au serveur.",
  "landing.lifecycle.intent":            "Intention",
  "landing.lifecycle.architecture":      "Architecture",
  "landing.lifecycle.database":          "Base de donnees",
  "landing.lifecycle.backend":           "Backend",
  "landing.lifecycle.frontend":          "Frontend",
  "landing.lifecycle.validation":        "Validation",
  "landing.lifecycle.deployment":        "Deploiement",
  "landing.lifecycle.production":        "Production",
  "landing.stat.architecture.value": "Autonome",
  "landing.stat.architecture.label": "Architecture",
  "landing.stat.code.value":         "Genere",
  "landing.stat.code.label":         "Code",
  "landing.stat.deployment.value":   "Automatise",
  "landing.stat.deployment.label":   "Deploiement",
  "landing.stat.delivery.value":     "Production",
  "landing.stat.delivery.label":     "Livraison",

  /* LandingPage - Chips */
  "landing.chip.saas":            "SaaS",
  "landing.chip.fintech":         "Fintech",
  "landing.chip.market":          "Marketplace",
  "landing.chip.mobileApp":       "App Mobile",
  "landing.chip.enterprise":      "Entreprise",
  "landing.chip.aiTool":          "Outil IA",
  "landing.chip.gamePlatform":    "Plateforme de Jeux",
  "landing.chip.dashboard":       "Tableau de Bord",

  /* LandingPage - How It Works */
  "landing.howItWorks.label":     "Pipeline de Construction Autonome",
  "landing.howItWorks.h2":        "Une description. Systeme complet.",
  "landing.howItWorks.desc":      "Chaque etape s'execute sans vous. Architecture, code, validation et deploiement sont entierement automatises.",
  "landing.pipeline.sequential.label": "Execution sequentielle par etapes",
  "landing.pipeline.sequential.desc":  "Chaque etape valide avant que la suivante commence, pour qu'une construction n'avance jamais sur un travail non verifie.",
  "landing.pipeline.repair.label":     "Reparation autonome",
  "landing.pipeline.repair.desc":      "Les echecs de validation declenchent une auto-correction ciblee.",
  "landing.pipeline.zerotouch.label":  "Deploiement sans intervention",
  "landing.pipeline.zerotouch.desc":   "De l'apercu a la production en une seule operation orchestree.",

  /* LandingPage - Try It */
  "landing.tryIt.label":          "Planificateur d'Architecture",
  "lpp.panelLabel":               "Planificateur d'architecture Nuvra",
  "landing.tryIt.h2":             "Regardez Nuvra Planifier votre App",
  "landing.tryIt.desc":           "Saisissez une invite et Nuvra mappe la vraie architecture: entites, schema de base de donnees, APIs et controle d'acces. Inscrivez-vous pour generer la construction complete et fonctionnelle.",

  /* LandingPage - System Scale */
  "landing.scale.label":          "Generation Full-Stack",
  "landing.scale.h2":             "Chaque Couche. Generee de Facon Autonome.",
  "landing.scale.desc":           "Nuvra genere et deploie votre application sur une pile de plateforme complete: UI, conception de schema, tests et deploiement, tout automatise.",
  "landing.layer.frontend":       "Apps Frontend",
  "landing.layer.backend":        "Services Backend",
  "landing.layer.database":       "Bases de Donnees",
  "landing.layer.infra":          "Infrastructure",
  "landing.layer.analytics":      "Analytique",
  "landing.layer.automation":     "Automatisation",

  /* LandingPage - Use Cases */
  "landing.useCases.label":       "Cas d'Usage",
  "landing.useCases.h2":          "Construisez n'importe quoi",
  "landing.useCases.exploreTemplate": "Voir ce que Nuvra construit",
  "landing.useCase.saas.title":   "Plateformes SaaS",
  "landing.useCase.saas.desc":    "Apps multi-tenant avec auth, facturation et tableaux de bord",
  "landing.useCase.market.title": "Marketplaces",
  "landing.useCase.market.desc":  "Plateformes acheteur-vendeur avec paiements et recherche",
  "landing.useCase.fintech.title": "Apps Fintech",
  "landing.useCase.fintech.desc": "Outils financiers avec transactions securisees",
  "landing.useCase.health.title": "Sante",
  "landing.useCase.health.desc":  "Portails patients, rendez-vous et apps de gestion de sante",
  "landing.useCase.gov.title":    "Portails Gouvernementaux",
  "landing.useCase.gov.desc":     "Plateformes de service aux citoyens a grande echelle",
  "landing.useCase.mobile.title": "Apps Mobiles",
  "landing.useCase.mobile.desc":  "Backends mobiles multi-plateformes et APIs",
  "landing.useCase.game.title":   "Jeux 3D",
  "landing.useCase.game.desc":    "Mondes 3D jouables avec physique, scenes et progression sauvegardee",
  "landing.useCase.ai.title":     "Tableaux de Bord IA",
  "landing.useCase.ai.desc":      "Visualisation analytique et intelligente",

  /* LandingPage - Features */
  "landing.features.label":       "Capacites de l'Usine",
  "landing.features.h2":          "Comment l'Usine Fonctionne",
  "landing.features.learnMore":   "En savoir plus",
  "landing.feature.intent.title":  "Moteur d'Intention",
  "landing.feature.intent.desc":   "Decrivez-le en langage naturel. Nuvra resout votre intention en un plan de systeme complet avant que quoi que ce soit ne s'execute.",
  "landing.feature.pipeline.title": "Pipeline de Construction Autonome",
  "landing.feature.pipeline.desc":  "Chaque couche de votre app est construite de facon autonome via un pipeline sequentiel: analyse d'intention, generation de code, scan de securite, tests et deploiement.",
  "landing.feature.preview.title":  "Apercu en Direct",
  "landing.feature.preview.desc":   "Chaque construction obtient un environnement d'apercu isole. Voyez-le s'executer et interagissez avant de deployer.",
  "landing.feature.deploy.title":   "Deploiement en Un Clic",
  "landing.feature.deploy.desc":    "De la construction a une URL de production en direct en une seule operation. Pas de configuration, pas de CI requis.",
  "landing.feature.healing.title":  "Reparation Automatique",
  "landing.feature.healing.desc":   "Les pannes recurrentes sont detectees automatiquement et transformees en propositions de correction que vous approuvez en un clic. Rien n'est corrige dans votre dos.",
  "landing.feature.monitor.title":  "Surveillance des Constructions",
  "landing.feature.monitor.desc":   "Les taux de reussite, la latence et l'etat de deploiement sont suivis en temps reel pour que vous sachiez toujours ce qui a ete deploye.",

  /* LandingPage - Enterprise */
  "landing.enterprise.label":     "Pret pour l'Entreprise",
  "landing.enterprise.h2":        "Construit pour la Production",
  "landing.enterprise.hint":      "Cliquez pour explorer chaque capacite",

  /* LandingPage - System Architecture */
  "landing.arch.label":           "Profondeur de la Plateforme",
  "landing.arch.h2":              "Qualite production. Chaque couche.",
  "landing.arch.desc":            "Les capacites principales sont prets pour la production et actives.",
  "landing.arch.ctaAll":          "Explorer Toutes les Capacites",
  "landing.arch.ai.label":        "IA et Generation",
  "landing.arch.ai.desc":         "Resolution d'intention par IA, generation de code par etapes et auto-reparation autonome.",
  "landing.arch.deploy.label":    "Deploiement",
  "landing.arch.deploy.desc":     "Pipeline de construction automatise du code vers l'URL de production avec verification des sondes de sante.",
  "landing.arch.mobile.label":    "Constructions Mobiles",
  "landing.arch.mobile.desc":     "Packaging natif Android et iOS avec support de signature, pret pour les stores.",
  "landing.arch.store.label":     "Publication en Magasin",
  "landing.arch.store.desc":      "Listes prets pour les magasins: politique de confidentialite generee, conditions, descriptions, captures d'ecran et verifications de preparation.",
  "landing.arch.exec.label":      "Moteur d'Execution",
  "landing.arch.exec.desc":       "Execution de taches a haut debit avec planification prioritaire, files d'attente et metriques en direct.",
  "landing.arch.observ.label":    "Observabilite",
  "landing.arch.observ.desc":     "Telemetrie de construction, journaux d'audit, sante du systeme et suivi des incidents integres.",
  "landing.arch.security.label":  "Securite",
  "landing.arch.security.desc":   "Coffre de credentials chiffre, protection reseau, auth securisee et politique de contenu.",
  "landing.arch.realtime.label":  "Temps Reel",
  "landing.arch.realtime.desc":   "Streaming de constructions en direct, mises a jour d'etat evenementielles et surveillance du systeme en temps reel.",

  /* LandingPage - Final CTA */
  "landing.cta.h2a":              "Decrivez-le une fois.",
  "landing.cta.h2b":              "Deployez aujourd'hui.",
  "landing.cta.desc":             "Pas de code passe-partout. Pas de configuration. Pas de cablage manuel. Nuvra genere, orchestre et deploie votre systeme de bout en bout.",
  "landing.cta.buildWithNuvra":   "Construire avec Nuvra",

  /* LandingPage - Product-led proof, control plane, pricing and FAQ */
  "landing.nav.menu": "Ouvrir le menu",
  "landing.nav.close": "Fermer le menu",
  "landing.difference.label": "La difference Nuvra",
  "landing.howItWorks.liveH2": "Regardez le systeme evoluer",
  "landing.principles.label": "Principes d'ingenierie",
  "landing.principles.h2": "Concu comme un logiciel, pas comme une demo.",
  "landing.principles.desc": "Nuvra transforme l'intention en travail d'ingenierie structure et garde l'execution encadree, observable et verifiable.",
  "landing.principle.intent.title": "L'intention avant le code",
  "landing.principle.intent.desc": "Les exigences sont resolues en contrat systeme avant le debut de la generation.",
  "landing.principle.schema.title": "Execution basee sur les schemas",
  "landing.principle.schema.desc": "Architecture, donnees, services et interfaces sont planifies comme des sorties structurees, pas comme du code libre.",
  "landing.principle.verify.title": "Verification dans la boucle",
  "landing.principle.verify.desc": "Les etapes de build reposent sur la validation, la reparation et les preuves plutot que sur une seule generation.",
  "landing.principle.govern.title": "Autonomie gouvernee",
  "landing.principle.govern.desc": "Execution, permissions, actions d'infrastructure et changements de production restent dans des controles explicites.",
  "landing.control.label": "Le plan de controle Nuvra",
  "landing.control.h2": "Votre usine logicielle a un cockpit.",
  "landing.control.desc": "Exigences, architecture, ordres de travail, builds, environnements, deploiements, memoire et integrations vivent dans un seul espace d'ingenierie.",
  "landing.control.cta": "Ouvrir Nuvra",
  "landing.control.window": "Espace de travail d'ingenierie Nuvra",
  "landing.control.status": "Surface operationnelle",
  "landing.control.note": "Ce sont de vraies surfaces de l'espace de travail Nuvra, pas un tableau de bord decoratif.",
  "landing.control.surface.requirements.title": "Exigences",
  "landing.control.surface.requirements.desc": "Contrat produit",
  "landing.control.surface.architecture.title": "Architecture",
  "landing.control.surface.architecture.desc": "Conception du systeme",
  "landing.control.surface.workOrders.title": "Ordres de travail",
  "landing.control.surface.workOrders.desc": "Demandes d'ingenierie permanentes",
  "landing.control.surface.builds.title": "Builds",
  "landing.control.surface.builds.desc": "Historique d'execution",
  "landing.control.surface.environments.title": "Environnements",
  "landing.control.surface.environments.desc": "Cibles d'execution",
  "landing.control.surface.deployments.title": "Deploiements",
  "landing.control.surface.deployments.desc": "Etat des releases",
  "landing.control.surface.memory.title": "Memoire",
  "landing.control.surface.memory.desc": "Contexte du projet",
  "landing.control.surface.integrations.title": "Integrations",
  "landing.control.surface.integrations.desc": "Services connectes",
  "landing.pricing.label": "Forfaits",
  "landing.pricing.h2": "Commencez petit. Faites evoluer l'usine.",
  "landing.pricing.desc": "Choisissez le niveau de controle d'ingenierie adapte au logiciel que vous construisez. Consultez la matrice complete et les limites actuelles sur la page des tarifs.",
  "landing.pricing.popular": "Populaire",
  "landing.pricing.custom": "Sur mesure",
  "landing.pricing.month": "/ mois",
  "landing.pricing.noMonthly": "Unique / inclus",
  "landing.pricing.cta": "Voir les tarifs complets",
  "landing.faq.label": "Questions",
  "landing.faq.h2": "Avant de construire",
  "landing.faq.desc": "Les reponses importantes, sans brouillard marketing.",
  "landing.faq.what.q": "Nuvra est-il simplement un chatbot de codage IA ?",
  "landing.faq.what.a": "Non. Nuvra est concu comme une plateforme autonome d'ingenierie logicielle : l'intention et les exigences sont d'abord resolues, l'architecture est planifiee, l'execution est decoupee en etapes et la verification et le deploiement font partie du flux.",
  "landing.faq.real.q": "Le planificateur d'architecture est-il vraiment actif ?",
  "landing.faq.real.a": "Oui. Le planificateur de cette page appelle le service public de previsualisation d'architecture Nuvra et retourne une architecture reelle derivee de votre prompt. Aucun compte n'est requis pour la previsualisation.",
  "landing.faq.control.q": "Quel controle est-ce que je garde ?",
  "landing.faq.control.a": "Nuvra expose exigences, architecture, ordres de travail, builds, environnements, deploiements, memoire et integrations afin que vous puissiez inspecter et gouverner le processus d'ingenierie plutot que de le confier a une boite noire.",
  "landing.faq.deploy.q": "Nuvra peut-il deployer des logiciels ?",
  "landing.faq.deploy.a": "Nuvra dispose de capacites de deploiement en production et d'integrations fournisseurs. Ce qui peut etre deploye depend de l'architecture du projet, de l'infrastructure connectee, des permissions et du forfait.",
  "landing.faq.ownership.q": "Est-ce que je possede ce que Nuvra construit ?",
  "landing.faq.ownership.a": "Les forfaits publics de Nuvra incluent des capacites de propriete et d'export du code. Les options exactes dependent du forfait ; la page des tarifs affiche la matrice actuelle.",

  /* OrgAdminShell */
  "orgAdmin.noOrg":               "Aucune organisation specifiee.",
  "orgAdmin.verifying":           "Verification de l'acces...",
  "orgAdmin.accessDeniedTitle":   "Acces restreint",
  "orgAdmin.accessDeniedBody":    "Vous devez avoir un acces administrateur ou proprietaire de l'organisation pour voir cette page.",
  "orgAdmin.title":               "Administration de l'organisation",
  "orgAdmin.subtitle":            "Gerez la politique IA, les membres, SSO, la conformite, les comptes de service et la configuration de deploiement.",
  "orgAdmin.tab.overview":        "Apercu",
  "orgAdmin.tab.members":         "Membres",
  "orgAdmin.tab.aiPolicy":        "Politique IA",
  "orgAdmin.tab.audit":           "Journal d'audit",
  "orgAdmin.tab.whiteLabel":      "Marque blanche",
  "orgAdmin.tab.ssoCompliance":   "SSO et conformite",
  "orgAdmin.tab.serviceAccounts": "Comptes de service",
  "orgAdmin.tab.sharedCredentials": "Identifiants partages",
  "orgAdmin.tab.webhooks":        "Webhooks",
  "orgAdmin.tab.deployment":      "Deploiement",

  /* VoiceNoteButton */
  "voice.micUnavailable":           "Le microphone n'est pas disponible dans ce navigateur.",
  "voice.notConfigured":            "La transcription vocale n'est pas encore configuree.",
  "voice.noCredits":                "Credits insuffisants pour la transcription.",
  "voice.transcribeFailed":         "Impossible de transcrire la note.",
  "voice.micDenied":                "Permission microphone refusee.",
  "voice.stopRecording":            "Arreter l'enregistrement",
  "voice.transcribing":             "Transcription en cours",
  "voice.record":                   "Enregistrer une note vocale",

  /* NotificationPanel */
  "notif.title":                    "Notifications",
  "notif.markAllRead":              "Tout marquer comme lu",
  "notif.empty":                    "Aucune notification pour l'instant",
  "notif.enablePush":               "Activer sur cet appareil",
  "notif.pushOn":                   "Push active pour cet appareil",
  "notif.pushDenied":               "Notifications bloquees dans les parametres du navigateur",
  "notif.pushEnabling":             "Activation...",

  /* UpgradeModal */
  "upgrade.maybeLater":             "Peut-etre plus tard",
  "upgrade.feature.unlimitedBuilds":"Builds illimites",
  "upgrade.feature.aiAgents":       "Agents IA",
  "upgrade.feature.priorityQueue":  "File prioritaire",
  "upgrade.feature.customDomains":  "Domaines personnalises",

  /* CapabilityStrip */
  "capStrip.title":                 "Capacites de la plateforme",
  "capStrip.viewAll":               "Capacites de la plateforme",
  "capStrip.aiGen.label":           "Generation IA",
  "capStrip.aiGen.desc":            "Pipeline de generation IA",
  "capStrip.webDeploy.label":       "Deploiement web",
  "capStrip.webDeploy.desc":        "Apercu en direct et URL de production",
  "capStrip.mobile.label":          "Builds mobiles",
  "capStrip.mobile.desc":           "AAB, APK, IPA, TWA",
  "capStrip.store.label":           "Publication sur les stores",
  "capStrip.store.desc":            "Google Play et App Store",
  "capStrip.rollout.label":         "Deploiement progressif",
  "capStrip.rollout.desc":          "Deploiement par pourcentage controle",
  "capStrip.repair.label":          "Reparation IA",
  "capStrip.repair.desc":           "Recuperation autonome des builds",
  "capStrip.multiAi.label":         "Multi-IA",
  "capStrip.multiAi.desc":          "Cascade de 3 fournisseurs IA",
  "capStrip.integrity.label":       "Integrite des artefacts",
  "capStrip.integrity.desc":        "SHA-256 et detection de falsification",
  "capStrip.realtime.label":        "Evenements en temps reel",
  "capStrip.realtime.desc":         "Bus d'evenements et files de taches",
  "capStrip.rollback.label":        "Retour arriere",
  "capStrip.rollback.desc":         "Retour arriere verifie au build precedent",
  "capStrip.whatsNew.label":        "Nouveautes",
  "capStrip.whatsNew.desc":         "Dernieres versions de la plateforme",

  /* LiveSystemStatus */
  "sys.runtimeHealth":              "Sante du systeme",
  "sys.live":                       "En direct",
  "sys.status.operational":         "Operationnel",
  "sys.status.degraded":            "Degrade",
  "sys.status.outage":              "Panne",

  /* ConnectorCard */
  "connector.badge.enabled":        "Active",
  "connector.badge.new":            "Nouveau",
  "connector.badge.popular":        "Populaire",
  "connector.badge.required":       "Requis par votre application",
  "connector.configure":            "Configurer",
  "connector.connect":              "Connecter",

  /* FactoryProgressPanel */
  "factory.title":                  "Usine logicielle",
  "factory.stage.planning":         "Analyse du prompt",
  "factory.stage.architecture":     "Conception de l'architecture",
  "factory.stage.models":           "Generation des modeles de donnees",
  "factory.stage.workflows":        "Creation des flux de travail",
  "factory.stage.api":              "Configuration des routes API",
  "factory.stage.components":       "Generation des composants UI",
  "factory.stage.design":           "Creation du systeme de design",
  "factory.stage.deployment":       "Preparation du deploiement",
  "factory.stage.mobile":           "Preparation des builds mobiles",
  "factory.stage.materializing":    "Materialisation des fichiers",
  "factory.stage.complete":         "Termine",
  "factory.stage.failed":           "Echoue",

  /* InteractiveProjectGrid */
  "projects.grid.title":            "Vos projets",
  "projects.grid.loading":          "Chargement de votre travail d'ingenierie...",
  "projects.grid.startNew":         "Demarrer un nouveau projet",
  "projects.grid.emptyHint":        "Decrivez votre idee ci-dessus et votre equipe d'ingenierie commencera a la construire.",
  "projects.grid.inProgress":       "Ingenierie en cours...",
  "projects.grid.lastActivity":     "Derniere activite {t}",
  "projects.grid.notBuilt":         "Pas encore construit",
  "projects.grid.continueBuild":    "Continuer a construire",
  "projects.grid.runtime":          "Runtime",
  "projects.grid.openRuntime":      "Ouvrir le runtime en direct",
  "projects.grid.status.building":  "Ingenierie",
  "projects.grid.status.live":      "En ligne",
  "projects.grid.status.error":     "Necessite attention",
  "projects.grid.status.draft":     "Brouillon",

  /* ThreatTelemetryRail */
  "threat.title":                   "Telemetrie des menaces",
  "threat.noThreats":               "Aucune menace detectee",
  "threat.noThreatsHint":           "Les evenements de securite apparaissent ici en temps reel",
  "threat.blocked":                 "bloque",
  "threat.kind.threat":             "Menace",
  "threat.kind.quarantine":         "Quarantaine",
  "threat.kind.rate_limit":         "Limite de debit",
  "threat.kind.validation_fail":    "Op. invalide",
  "threat.kind.ai_threat":          "Menace IA",
  "threat.metric.assessed":         "Evalues",
  "threat.metric.blocked":          "Bloques",
  "threat.metric.blockPct":         "% bloques",

  /* WorkflowCanvas */
  "workflow.builder":               "Constructeur visuel",
  "workflow.emptyHint":             "Ajoutez des noeuds pour construire votre flux de travail",
  "workflow.node.trigger":          "Declencheur",
  "workflow.node.condition":        "Condition",
  "workflow.node.action":           "Action",
  "workflow.node.parallel":         "Parallele",
  "workflow.node.loop":             "Boucle",
  "workflow.node.wait":             "Attente/Delai",
  "workflow.node.approval":         "Approbation",

  /* AgentRunPanel */
  "agent.title":                    "Agent autonome",
  "agent.locked.title":             "Agent autonome",
  "agent.locked.body":              "L'agent d'ingenierie autonome (il lit, modifie, execute des commandes, compile, teste et repare votre projet) est inclus a partir du plan Pro. Passez a un plan superieur pour l'utiliser.",
  "agent.locked.cta":               "Voir les plans",
  "agent.placeholder":              "Decrivez ce que l'agent doit faire (lire, modifier, executer, construire, tester, valider)...",
  "agent.run":                      "Lancer l'agent",
  "agent.image":                    "Image",
  "agent.imageTitle":               "Joindre une image (capture d'ecran, esquisse)",
  "agent.stop":                     "Arreter",
  "agent.working":                  "En cours",
  "agent.error.noKey":              "La cle du fournisseur IA n'est pas encore configuree.",
  "agent.error.notEnabled":         "Le runtime de l'agent n'est pas active.",
  "agent.reconnect":                   "Reconnecter",
  "agent.resume":                      "Continuer",
  "agent.resume.note":                 "Continuer reprend au dernier point de sauvegarde; les etapes terminees ne sont ni reexecutees ni refacturees.",
  "agent.acuUsed":                     "ACU utilises",
  "agent.paused.approval":             "En pause: cette etape necessite une approbation avant de s'executer.",
  "agent.paused.budget":               "En pause: l'allocation de credit de cette session est epuisee.",
  "agent.cancelled":                   "Annule.",
  "agent.disconnected":                "Connexion perdue. L'execution continue; reconnectez-vous pour la suivre.",
  "agent.summary.pipeline":            "Pipeline",
  "agent.summary.stages":              "Etapes",
  "agent.summary.capabilities":        "Capacites terminees",

  // WorkspaceBusyState
  "workspace.busy.errorTitle":      "Le build n'a pas abouti",
  "workspace.busy.errorBody":       "Ce build s'est arrete avant la fin, et aucun credit n'a ete debite. Relancez-le pour continuer.",
  "workspace.busy.runAgain":        "Relancer",
  "workspace.busy.buildingTitle":   "Construction de votre produit",
  "workspace.busy.generating":      "Generation en cours sur les serveurs Nuvra. La progression en direct s'affiche ici.",

  // SyncConflictBanner
  "sync.conflict.ariaLabel":        "Ce projet a change sur un autre appareil",
  "sync.conflict.title":            "Ce projet a change sur un autre appareil",
  "sync.conflict.desc":             "Choisissez la version a conserver. Rien n'est ecrase avant votre decision.",
  "sync.conflict.keepMine":         "Garder la mienne",
  "sync.conflict.useTheirs":        "Utiliser la leur",
  "sync.conflict.diffCount":        "{n} differences",
  "sync.conflict.keepAllMine":      "Garder toutes les miennes",
  "sync.conflict.useAllTheirs":     "Utiliser toutes les leurs",

  // DataExplorer
  "data.explorer.selectTable":      "Selectionner une table",
  "data.explorer.searchPlaceholder":"Rechercher des enregistrements...",
  "data.explorer.insert":           "Inserer",
  "data.explorer.import":           "Importer",
  "data.explorer.export":           "Exporter",
  "data.explorer.actionsCol":       "Actions",
  "data.explorer.noRecords":        "Aucun enregistrement trouve",
  "data.explorer.recordCount":      "{n} enregistrements",
  "data.explorer.editHint":         "Double-cliquez sur une cellule pour modifier en ligne",

  // ArchitectureConfidencePanel
  "arch.confidence.title":          "Confiance dans l'architecture",
  "arch.confidence.score":          "{score}% de confiance",
  "arch.confidence.components":     "Composants",
  "arch.confidence.services":       "Services",
  "arch.confidence.tables":         "Tables",
  "arch.confidence.endpoints":      "Endpoints",
  "arch.confidence.estCredits":     "Credits est.",
  "arch.confidence.deployTarget":   "Cible de deploiement :",
  "arch.confidence.confirmBuild":   "Confirmer et compiler",

  // ExecutionLogs
  "exec.filter.all":                "Tous",
  "exec.filter.completed":          "Termines",
  "exec.filter.failed":             "Echoues",
  "exec.filter.running":            "En cours",
  "exec.logs.noExecutions":         "Aucune execution trouvee",

  // AccessControlPanel
  "access.rls.title":               "Securite au niveau des lignes",
  "access.rls.desc":                "Definissez des conditions RLS en langage naturel ou en expressions SQL",
  "access.rls.placeholder":         "ex. \"Les utilisateurs ne peuvent lire que leurs propres enregistrements\"",
  "access.rls.addRule":             "Ajouter une regle",

  // BuildActivityStream
  "build.activity.onTeam":          "fait partie de l'equipe",

  // PlanExplanationPanel
  "plan.explain.toggle":            "Pourquoi Nuvra a planifie ceci",
  "plan.explain.summary":           "Resume",
  "plan.explain.keyDecisions":      "Decisions cles",
  "plan.explain.assumptions":       "Hypotheses",
  "plan.explain.confidence":        "{pct}% de confiance",
  "plan.explain.pages":             "Pages ({n})",
  "plan.explain.capabilities":      "Capacites ({n})",
  "plan.explain.noData":            "Aucune donnee d'explication disponible pour ce plan.",

  // UseCasePreviewPanel
  "usecase.tab.architecture":       "Architecture",
  "usecase.tab.data":               "Modeles de donnees",
  "usecase.tab.auth":               "Authentification",
  "usecase.tab.deploy":             "Deploiement",
  "usecase.startBuilding":          "Commencer a construire",
  "usecase.close":                  "Fermer",

  // FeatureInteractionPanel
  "feature.howItWorks":             "Comment ca fonctionne",
  "feature.highlights":             "Points forts",
  "feature.try":                    "Essayer {title}",
  "feature.close":                  "Fermer",

  // SaveStatusIndicator
  "save.status.ready":              "Pret",
  "save.status.saving":             "Enregistrement...",
  "save.status.saved":              "Enregistre",
  "save.status.failed":             "Echec de l'enregistrement",

  // CommentsMenu
  "comments.title":                 "Commentaires",
  "comments.empty":                 "Pas encore de commentaires. Lancez la conversation.",
  "comments.placeholder":           "Ajouter un commentaire...",
  "comments.post":                  "Publier",

  // OAuthButtons
  "oauth.google":                   "Continuer avec Google",
  "oauth.github":                   "Continuer avec GitHub",
  "oauth.gitlab":                   "Continuer avec GitLab",
  "oauth.bitbucket":                "Continuer avec Bitbucket",
  "oauth.apple":                    "Continuer avec Apple",
  "oauth.microsoft":                "Continuer avec Microsoft",
  "oauth.signInFailed":             "Echec de la connexion",

  // WelcomeBanner
  "welcome.goodMorning":            "Bonjour",
  "welcome.goodAfternoon":          "Bon apres-midi",
  "welcome.goodEvening":            "Bonsoir",
  "welcome.factory":                "Ingénierie logicielle autonome",
  "welcome.credits":                "credits",

  // IntentChips
  "intent.chip.saas":              "SaaS",
  "intent.chip.api":               "API / Backend",
  "intent.chip.music":             "Musique",
  "intent.chip.books":             "Livres et ebooks",
  "intent.chip.video":             "Video",
  "intent.chip.ecommerce":         "E-commerce",
  "intent.chip.ai":                "Produit IA",
  "intent.chip.mobile":            "Application mobile",
  "intent.chip.game3d":         "Jeu 3D",

  // ProjectSwitcher
  "proj.switcher.select":           "Selectionner un projet",
  "proj.switcher.noProjects":       "Aucun projet pour l'instant",
  "proj.switcher.pinned":           "Epingles",
  "proj.switcher.recent":           "Recents",
  "proj.switcher.create":           "Creer un nouveau projet",
  "proj.switcher.justNow":          "a l'instant",
  "proj.switcher.mAgo":             "il y a {m}min",
  "proj.switcher.hAgo":             "il y a {h}h",
  "proj.switcher.dAgo":             "il y a {d}j",

  // ProjectsSheet
  "proj.sheet.title":               "Projets",
  "proj.sheet.newProject":          "Nouveau projet",
  "proj.sheet.noProjects":          "Aucun projet pour l'instant. Creez-en un pour commencer.",
  "proj.sheet.untitled":            "Projet sans titre",

  // BuilderEmptyState
  "builder.headline":               "Construisez votre prochaine entreprise logicielle.",
  "builder.subline":                "Decrivez votre produit et Nuvra l'ingenie de bout en bout.",
  "builder.step.intent":            "Intention",
  "builder.step.planning":          "Planification",
  "builder.step.engineering":       "Ingenierie",
  "builder.step.operations":        "Operations",
  "builder.step.growth":            "Croissance",
  "builder.tpl.saas":               "Tableau de bord SaaS",
  "builder.tpl.crm":                "CRM",
  "builder.tpl.marketplace":        "Marketplace",
  "builder.tpl.aiAgent":            "Plateforme d'agent IA",
  "builder.tpl.adminPortal":        "Portail d'administration",
  "builder.tpl.landingPage":        "Page d'accueil",
  "builder.tpl.ecommerce":          "Boutique e-commerce",
  "builder.tpl.internalTool":       "Outil interne",

  // ACUUsageNudges (ACUBalanceWarning)
  "acu.nudge.exhaustedTitle":       "Credits epuises",
  "acu.nudge.exhaustedBody":        "Vos credits de calcul sont epuises. Rechargez pour continuer a construire.",
  "acu.nudge.topUp":                "Recharger",
  "acu.nudge.dismiss":              "Ignorer",
  "acu.nudge.almostTitle":          "Presque plus de credits",
  "acu.nudge.remaining":            "{n} credits restants.",
  "acu.nudge.approachingTitle":     "Approche de la limite de credits",
  "acu.nudge.buildingTitle":        "Prise d'elan",
  "acu.nudge.buildingBody":         "Vous etes sur la bonne lancee. Continuez.",

  // FirstRunOnboarding
  "onboarding.a11yTitle":           "Configurez votre profil de createur Nuvra",
  "onboarding.stepOf":              "Etape {n} sur {total}",
  "onboarding.maybeLater":          "Peut-etre plus tard",
  "onboarding.back":                "Retour",
  "onboarding.continue":            "Continuer",
  "onboarding.saveFailTitle":       "Vos preferences n'ont pas ete enregistrees",
  "onboarding.saveFailBody":        "Nuvra n'a pas pu joindre le serveur. Vous pouvez continuer; vos reponses seront enregistrees a la prochaine etape.",
  "onboarding.sessionExpiredTitle": "Votre session a expire",
  "onboarding.sessionExpiredBody":  "Reconnectez-vous pour enregistrer vos preferences. Vous pouvez continuer a construire dans tous les cas.",
  "onboarding.profile.title":       "Comment utiliserez-vous Nuvra?",
  "onboarding.profile.body":        "Parlez-nous un peu de votre facon de construire. Nuvra adapte votre experience en consequence.",
  "onboarding.profile.founder":     "Fondateur / startup",
  "onboarding.profile.founderDesc": "Transformez vos idees en vrais produits et lancez plus vite.",
  "onboarding.profile.product_manager":     "Product manager",
  "onboarding.profile.product_managerDesc": "Transformez des exigences produit en logiciel fonctionnel.",
  "onboarding.profile.developer":       "Developpeur / ingenieur",
  "onboarding.profile.developerDesc":   "Construisez, deboguez et livrez du logiciel de production.",
  "onboarding.profile.business_owner":      "Chef d'entreprise",
  "onboarding.profile.business_ownerDesc":  "Creez les outils et systemes qui font tourner votre entreprise.",
  "onboarding.profile.freelancer":      "Freelance / agence",
  "onboarding.profile.freelancerDesc":  "Construisez des produits et des experiences pour vos clients et equipes.",
  "onboarding.profile.marketer":        "Marketeur / createur",
  "onboarding.profile.marketerDesc":    "Creez des sites web, des experiences numeriques et des produits.",
  "onboarding.profile.student":         "Etudiant / enseignant",
  "onboarding.profile.studentDesc":     "Apprenez, experimentez et transformez vos idees en projets reels.",
  "onboarding.profile.personal":        "Projets personnels",
  "onboarding.profile.personalDesc":    "Construisez quelque chose d'utile juste pour vous.",
  "onboarding.intent.title":        "Que voulez-vous creer en premier?",
  "onboarding.intent.body":         "Choisissez un point de depart. Vous pourrez tout changer ensuite.",
  "onboarding.intent.production_app":  "Application de production",
  "onboarding.intent.saas":            "Produit SaaS",
  "onboarding.intent.website":         "Site web / experience web",
  "onboarding.intent.internal_tool":   "Outil interne d'entreprise",
  "onboarding.intent.mobile_app":      "Application mobile",
  "onboarding.intent.api_backend":     "API / systeme backend",
  "onboarding.intent.mvp":             "MVP / prototype",
  "onboarding.intent.ecommerce":       "Produit e-commerce",
  "onboarding.intent.client_project":  "Projet client",
  "onboarding.intent.something_else":  "Autre chose",
  "onboarding.intent.ownIdea":      "J'ai une autre idee",
  "onboarding.priorities.title":    "Qu'est-ce qui compte le plus pour vous?",
  "onboarding.priorities.body":     "Choisissez jusqu'a 3 priorites. Nuvra les utilisera pour mieux comprendre comment vous voulez construire.",
  "onboarding.priorities.count":    "{n} sur {max} selectionnees",
  "onboarding.priority.production_readiness": "Pret pour la production",
  "onboarding.priority.ui_ux":            "UI/UX exceptionnelle",
  "onboarding.priority.speed":            "Vitesse jusqu'a la premiere version",
  "onboarding.priority.architecture":     "Architecture solide",
  "onboarding.priority.business_logic":   "Logique metier",
  "onboarding.priority.scalability":      "Scalabilite",
  "onboarding.priority.integrations":     "Integrations",
  "onboarding.priority.security":         "Securite",
  "onboarding.priority.mobile":           "Experience mobile",
  "onboarding.priority.cost":             "Efficacite des couts",
  "onboarding.ready.headline":      "Que construisons-nous?",
  "onboarding.ready.body":          "Decrivez ce que vous voulez construire. Nuvra transforme votre idee en un projet d'ingenierie que vous pouvez construire, tester et livrer.",
  "onboarding.ready.placeholder":   "Decrivez votre produit: ce qu'il fait, qui l'utilise et ce qu'il doit gerer.",
  "onboarding.ready.examplesLabel": "Exemples",
  "onboarding.ready.build":         "Construire avec Nuvra",
  "onboarding.ready.workspace":     "Continuer vers l'espace de travail",
  "onboarding.example.store":            "Construisez une boutique en ligne avec catalogue produits, paiement et suivi des commandes",
  "onboarding.example.clientPortal":     "Construisez un portail client avec authentification, factures, documents et messagerie",
  "onboarding.example.fitnessPlatform":  "Construisez une plateforme de fitness avec abonnements, plans d'entrainement, paiements et suivi des progres",

  // ACUTopUpGrid
  "topup.title":                    "Recharger les credits",
  "topup.body":                     "Bientot a court? Ajoutez des credits a tout moment sans changer votre plan.",
  "topup.info":                     "L'achat de credits ne modifie pas votre abonnement. Les credits n'expirent jamais. Maximum en attente: {n} credits.",
  "topup.creditsUnit":              "credits",
  "topup.bestValue":                "Meilleur rapport",
  "topup.processing":               "Traitement en cours...",
  "topup.buy":                      "Acheter {label}",
  "topup.footer":                   "Les credits de recharge n'expirent jamais et se cumulent a chaque cycle de facturation.",
  "topup.upgrade":                  "Besoin de beaucoup plus? Mettez a niveau votre plan",
  "topup.viewPlans":                "Voir les plans",

  // BillingUsagePanel
  "usage.loading":                  "Chargement des details d'utilisation...",
  "usage.noData":                   "Aucune donnee d'utilisation.",
  "usage.month":                    "Ce mois ({period})",
  "usage.spentSoFar":               "Depense jusqu'ici",
  "usage.projected":                "Projection en fin de mois",
  "usage.dayOf":                    "jour {elapsed} sur {total}",
  "usage.planLimits":               "Limites du plan ({tier})",
  "usage.unlimited":                "Illimite sur ce plan.",
  "usage.notInPlan":                "Non inclus dans votre offre",
  "usage.unlockUpgrade":            "Debloquez-les avec une mise a niveau",
  "usage.breakdown":                "Repartition de l'utilisation",
  "usage.byProvider":               "Par fournisseur IA",
  "usage.byActivity":               "Par activite",
  "usage.noActivity":               "Aucune utilisation enregistree ce mois.",
  "usage.recentLimits":             "Limites recemment atteintes",
  "usage.rateLimit":                "limite de debit",
  "usage.planLimit":                "limite de plan",

  // SSOComplianceTab
  "sso.loading":                    "Chargement de la configuration SSO et conformite...",
  "sso.noConfig":                   "Aucune configuration chargee",
  "sso.title":                      "Authentification unique",
  "sso.saved":                      "Enregistre",
  "sso.saving":                     "Enregistrement...",
  "sso.saveSso":                    "Enregistrer le SSO",
  "sso.enabled":                    "SSO active",
  "sso.type":                       "Type de SSO",
  "sso.issuerUrl":                  "URL de l'emetteur",
  "sso.clientId":                   "ID client",
  "sso.scope":                      "Portee",
  "sso.scopeHint":                  "Portees OIDC separees par des espaces.",
  "sso.entityId":                   "ID d'entite",
  "sso.ssoUrl":                     "URL SSO",
  "sso.certificate":                "Certificat X.509",
  "sso.defaultRole":                "Role de membre par defaut",
  "sso.defaultRoleHint":            "Role attribue aux nouveaux membres provisionnes via SSO.",
  "sso.scim":                       "Provisionnement SCIM",
  "sso.scimDesc":                   "Active le endpoint /api/org/{orgId}/scim/v2/Users pour le provisionnement IdP.",
  "sso.scimToken":                  "Token SCIM",
  "sso.scimTokenHint":              "Conservez-le maintenant. Il ne sera plus affiche apres avoir quitte cette page.",
  "sso.copied":                     "Copie",
  "sso.complianceTitle":            "Residency des donnees et conformite",
  "sso.saveRegion":                 "Enregistrer la politique de region",
  "sso.dataClass":                  "Classification des donnees",
  "sso.piiRedaction":               "Redaction PII requise",
  "sso.piiDesc":                    "Signale les noms de champs PII dans le README d'export. Ne redacte pas le contenu automatiquement.",
  "sso.legalHold":                  "Conservation legale",
  "sso.legalHoldActive":            "ACTIF: toutes les exportations de projets sont bloquees.",
  "sso.legalHoldDesc":              "Quand actif: les exportations de projets sont bloquees.",
  "sso.frameworks":                 "Referentiels de conformite",
  "sso.dataRegions":                "Regions de donnees autorisees",
  "sso.dataRegionsHint":            "Codes de region separes par des virgules. Vide = toute region autorisee.",
  "sso.aiRegions":                  "Regions du fournisseur IA",
  "sso.aiRegionsHint":              "Separes par des virgules. Vide = toute region du fournisseur autorisee.",

  // AIGovernanceTab
  "ai.gov.loading":                 "Chargement de la politique IA...",
  "ai.gov.noPolicy":                "Aucune politique chargee",
  "ai.gov.title":                   "Politique de gouvernance IA",
  "ai.gov.saved":                   "Enregistre",
  "ai.gov.saving":                  "Enregistrement...",
  "ai.gov.save":                    "Enregistrer",
  "ai.gov.approvedModels":          "Modeles approuves",
  "ai.gov.approvedModelsHint":      "Liste vide = tout modele autorise. Selectionnez pour restreindre.",
  "ai.gov.restrictionActive":       "Restriction active: seuls les modeles selectionnes sont autorises pour cette org.",
  "ai.gov.redactionPatterns":       "Patterns de redaction",
  "ai.gov.redactionHint":           "Une expression reguliere par ligne. Appliquee aux prompts avant qu'ils atteignent un fournisseur.",
  "ai.gov.retention":               "Retention des prompts (jours)",
  "ai.gov.retentionHint":           "0 = ne pas conserver",
  "ai.gov.costCap":                 "Plafond de cout mensuel (centimes)",
  "ai.gov.noCap":                   "Aucun plafond",
  "ai.gov.month":                   "mois",
  "ai.gov.logPrompts":              "Journaliser les prompts",
  "ai.gov.logPromptsHint":          "Quand desactive, le contenu du prompt n'est jamais stocke (seulement les metadonnees).",
  "ai.gov.trainingOptOut":          "Exclusion de l'entrainement",
  "ai.gov.trainingOptOutHint":      "Signale aux fournisseurs IA que les donnees de l'org ne doivent pas etre utilisees pour l'entrainement.",

  // WhiteLabelTab
  "wl.loading":                     "Chargement de la configuration marque blanche...",
  "wl.noConfig":                    "Aucune configuration chargee",
  "wl.title":                       "Configuration marque blanche",
  "wl.saved":                       "Enregistre",
  "wl.saving":                      "Enregistrement...",
  "wl.save":                        "Enregistrer",
  "wl.domain":                      "Domaine personnalise",
  "wl.domainHint":                  "Le domaine sur lequel votre instance Nuvra en marque blanche est servie.",
  "wl.platformName":                "Nom de la plateforme",
  "wl.platformNameHint":            "Remplace 'Nuvra' partout dans l'interface.",
  "wl.primaryColor":                "Couleur principale",
  "wl.logoUrl":                     "URL du logo",
  "wl.faviconUrl":                  "URL du favicon",
  "wl.supportEmail":                "Email de support",
  "wl.footerText":                  "Texte du pied de page",
  "wl.hidePoweredBy":               "Masquer 'Powered by Nuvra'",
  "wl.customCss":                   "CSS personnalise",
  "wl.customCssHint":               "Porte sur votre tenant. Les patterns dangereux sont supprimes cote serveur (pas de @import, javascript:, expressions). Max 8 Ko.",

  // MembersTab
  "members.loading":                "Chargement des membres...",
  "members.title":                  "Membres",
  "members.total":                  "{n} au total",
  "members.empty":                  "Aucun membre trouve",
  "members.col.member":             "Membre",
  "members.col.role":               "Role",
  "members.col.joined":             "Rejoint",
  "members.col.lastActive":         "Derniere activite",
  "members.never":                  "Jamais",
  "members.remove":                 "Retirer le membre",

  // OrgDashboardTab
  "org.loading":                    "Chargement du tableau de bord...",
  "org.health":                     "Sante de l'organisation",
  "org.refresh":                    "Actualiser",
  "org.metric.totalInferences":     "Total des inferences",
  "org.metric.denied":              "Refusees",
  "org.metric.redacted":            "Retraitees",
  "org.metric.policyChanges":       "Changements de politique (24h)",
  "org.alerts":                     "Alertes actives ({n})",
  "org.noAlerts":                   "Aucune alerte active",
  "org.members":                    "Membres ({n})",
  "org.inactive":                   "inactif",
  "org.noMembers":                  "Aucun membre trouve",

  // AuditLogTab
  "audit.tab.prompts":              "Journal des prompts",
  "audit.tab.policy":               "Changements de politique",
  "audit.loading":                  "Chargement...",
  "audit.col.time":                 "Heure",
  "audit.col.model":                "Modele",
  "audit.col.outcome":              "Resultat",
  "audit.col.redacted":             "Retraite",
  "audit.col.action":               "Action",
  "audit.col.changedBy":            "Modifie par",
  "audit.unknown":                  "inconnu",
  "audit.field":                    "champ",
  "audit.fields":                   "champs",
  "audit.noRedaction":              "Non",
  "audit.blockReason":              "Raison du blocage : {reason}",
  "audit.noPromptEntries":          "Aucune entree d'audit de prompts",
  "audit.system":                   "systeme",
  "audit.noPolicyEntries":          "Aucune entree de changement de politique",

  // WatermarkModal
  "wm.close":                       "Fermer",
  "wm.logoAlt":                     "Logo Nuvra",
  "wm.title":                       "Cree avec Nuvra",
  "wm.tagline":                     "La facon la plus rapide de lancer des apps avec l'IA.",
  "wm.descBefore":                  "Cette app a ete creee avec",
  "wm.descAfter":                   "une plateforme native IA pour lancer des apps de production en minutes, pas en mois.",
  "wm.buildMyApp":                  "Creer mon app",
  "wm.exploreTemplates":            "Explorer les modeles",
  "wm.viewPlatform":                "Voir la plateforme",
  "wm.joinLine":                    "Rejoignez des milliers qui construisent avec Nuvra",

  // ProjectCodeView
  "code.title":                     "Code du projet",
  "code.edited":                    "edite",
  "code.editedTooltip":             "Edite",
  "code.pushGithub":                "Pousser vers GitHub",
  "code.mobileApp":                 "App mobile",
  "code.mobileAppTitle":            "Telecharger l'app mobile native (Expo / React Native)",
  "code.downloadZip":               "Telecharger ZIP",
  "code.buildCi":                   "Compiler l'app (CI)",
  "code.buildCiTitle":              "Compiler un AAB/IPA signe en CI (runners avec Android SDK / macOS + Xcode)",
  "code.loading":                   "Chargement",
  "code.empty":                     "Aucun code genere. Construisez le projet d'abord.",
  "code.edit":                      "Editer",
  "code.reset":                     "Reinitialiser",
  "code.save":                      "Enregistrer",
  "code.cancel":                    "Annuler",
  "code.flash.downloadStarted":     "Telechargement lance.",
  "code.flash.noCredits":           "Credits insuffisants pour exporter (necessite {acu} credits).",
  "code.flash.more":                "plus",
  "code.flash.exportFailed":        "Echec de l'export (HTTP {status}).",
  "code.flash.needGithub":          "Connectez votre GitHub d'abord, puis compilez en CI.",
  "code.flash.ciFailed":            "Echec de l'envoi de compilation (HTTP {status}).",
  "code.flash.pushed":              "Pousse. Ajoutez les secrets de signature puis lancez le workflow.",
  "code.flash.ciReady":             "Remise de compilation preparee.",
  "code.flash.saved":               "Enregistre.",
  "code.flash.reset":               "Reinitialise a l'original genere.",
  "code.flash.filesTo":             "{n} fichiers pousses vers {branch}.",

  // NuvraActionsMenu
  "actions.button":                 "Actions",
  "actions.title":                  "Actions Nuvra",
  "actions.templates":              "Modeles",
  "actions.connect":                "Connecter",
  "actions.manageConnectors":       "Gerer les connecteurs",
  "actions.cat.saas":               "SaaS / Startup",
  "actions.cat.marketplace":        "Marketplace / E-Commerce",
  "actions.cat.internal":           "Outils internes",
  "actions.cat.ai_agents":          "IA et Agents",
  "actions.cat.marketing":          "Marketing",
  "actions.cat.other":              "Autre",
  "actions.tpl.analytics_dash":     "Tableau de bord analytique",
  "actions.tpl.sub_billing":        "App de facturation par abonnement",
  "actions.tpl.two_sided":          "Marketplace a deux cotes",
  "actions.tpl.ecommerce":          "Boutique e-commerce",
  "actions.tpl.admin_portal":       "Portail d'administration",
  "actions.tpl.ops_tool":           "Outil d'operations",
  "actions.tpl.ai_platform":        "Plateforme d'agents IA",
  "actions.tpl.ai_chat":            "App de chat IA",
  "actions.tpl.landing":            "Landing page SaaS",
  "actions.tpl.crm":                "CRM",
  "actions.tpl.community":          "Plateforme communautaire",
  "actions.tpl.project_mgmt":       "Outil de gestion de projet",

  // BuildHistory
  "bh.title":                       "Historique des compilations",
  "bh.count":                       "{n} compilations",
  "bh.noBuilds":                    "Aucune compilation ne correspond aux filtres.",
  "bh.filter.all":                  "Toutes",
  "bh.filter.success":              "Reussies",
  "bh.filter.failed":               "Echouees",
  "bh.filter.inProgress":           "En cours",
  "bh.filter.allCost":              "Tous couts",
  "bh.filter.highCost":             "Cout eleve",
  "bh.used":                        "Utilises : {n} credits",
  "bh.reserved":                    "Reserves : {n} credits",
  "bh.refunded":                    "Rembourses : {n} credits",
  "bh.logsTitle":                   "Journaux - {id}",
  "bh.logsExported":                "Journaux exportes",
  "bh.downloading":                 "Telechargement {target} - {mb} Mo",
  "bh.shareCopied":                 "Lien de partage copie",
  "bh.idCopied":                    "ID de compilation copie",

  // MobileGovernanceDashboard
  "mgov.loading":                   "Chargement des donnees de gouvernance mobile",
  "mgov.loadError":                 "Impossible de charger les donnees de gouvernance mobile.",
  "mgov.loadAllFailed":             "Impossible de charger la sante des builds de ce projet. C'est generalement temporaire.",
  "mgov.loadPartial":               "Une partie des donnees de sante n'a pas ete chargee. Reessayez pour completer le tableau.",
  "mgov.retry":                     "Reessayer",
  "mgov.profileSaved":              "Profil par defaut enregistre. Les nouvelles compilations l'utiliseront sauf derogation.",
  "mgov.profileError":              "Impossible d'enregistrer le profil par defaut.",
  "mgov.observability":             "Observabilite des compilations",
  "mgov.stat.builds":               "Compilations",
  "mgov.stat.successRate":          "Taux de reussite",
  "mgov.stat.p50":                  "Duree p50",
  "mgov.stat.p95":                  "Duree p95",
  "mgov.stat.completed":            "Terminees",
  "mgov.stat.failed":               "Echouees",
  "mgov.stat.running":              "En cours",
  "mgov.stat.queued":               "En attente",
  "mgov.failuresByCause":           "Echecs par cause",
  "mgov.of":                        "sur",
  "mgov.noBuilds":                  "Aucune compilation enregistree pour ce projet.",
  "mgov.versionHistory":            "Historique des versions",
  "mgov.storeReady":                "pret-pour-store",
  "mgov.caps":                      "caps",
  "mgov.noVersions":                "Aucune compilation versionnee encore.",
  "mgov.profiles":                  "Profils de gouvernance",
  "mgov.profilesExplainer":         "Le profil selectionne execute ses controles de securite, chiffrement, audit et residence des donnees avant chaque build mobile. Un controle en echec bloque le build.",
  "mgov.projectDefault":            "Defaut du projet",
  "mgov.selectDefault":             "Selectionner un defaut",
  "mgov.default":                   "defaut",
  "mgov.tag.encryption":            "chiffrement",
  "mgov.tag.audit":                 "audit",
  "mgov.tag.residency":             "residence : {r}",
  "mgov.tag.security":              "securite : {s}",
  "mgov.tag.blocks":                "bloque {n}",

  // HostDeploySection
  "host.title":                     "Deployer sur votre host",
  "host.subtitle":                  "Publier la compilation actuelle sur votre compte Vercel ou Netlify.",
  "host.connectBtn":                "Connecter un host",
  "host.open":                      "Ouvrir",
  "host.deploy":                    "Deployer",
  "host.disconnect":                "Deconnecter",
  "host.noConnection":              "Aucun host connecte. Connectez Vercel ou Netlify pour deployer sur votre compte.",
  "host.tokenRequired":             "Token et cible requis.",
  "host.connectError":              "Connexion impossible.",
  "host.noCredits":                 "Credits insuffisants pour le deploiement (necessite {acu} credits).",
  "host.more":                      "plus",
  "host.deployFailed":              "Deploiement echoue (HTTP {status}).",
  "host.tokenPlaceholder":          "Token d'acces {provider}",
  "host.vercelProjectId":           "ID de projet Vercel",
  "host.netlifySiteId":             "ID de site Netlify",
  "host.vercelTeamId":              "ID d'equipe Vercel (optionnel)",
  "host.connectProvider":           "Connecter {provider}",

  // StoreSubmitSection
  "store.google":                   "Google Play Store",
  "store.apple":                    "Apple App Store",
  "store.ciRun":                    "Execution CI",
  "store.checking":                 "Verification de la compilation",
  "store.needGithub":               "Connectez GitHub et lancez une compilation {target} pour soumettre.",
  "store.noBuild":                  "Aucune compilation {target}. Lancez-en une depuis Compilations mobiles.",
  "store.buildRunning":             "Compilation {status}. La soumission se debloque a la fin.",
  "store.buildFailed":              "La derniere compilation a echoue. Corrigez et recompilez avant de soumettre.",
  "store.noArtifact":               "La compilation a reussi mais n'a produit aucun artefact a soumettre.",
  "store.packagePlaceholder":       "ID d'application Android (ex. com.entreprise.app)",
  "store.submit":                   "Soumettre",
  "store.submitted":                "Soumis ({id})",
  "store.release":                  "lancement",
  "store.submitFailed":             "Soumission echouee",
  "store.statusError":              "Impossible de lire l'etat de la compilation",

  // batch 9 - ver.*
  "ver.title":          "Historique des versions",
  "ver.titleBtn":       "Historique des versions",
  "ver.btnLabel":       "Versions",
  "ver.loading":        "Chargement des versions...",
  "ver.loadError":      "Impossible de charger l'historique des versions.",
  "ver.empty":          "Aucune compilation terminee pour l'instant.",
  "ver.untitled":       "Compilation sans titre",
  "ver.name":           "Nommer cette version",
  "ver.rename":         "Renommer cette version",
  "ver.namePlaceholder": "Nommez cette version (ex. Avant les paiements)",
  "ver.labelError":     "Impossible d'enregistrer le nom. Reessayez.",
  "ver.current":        "Actuelle",
  "ver.confirm":        "Confirmer",
  "ver.cancel":         "Annuler",
  "ver.revert":         "Revenir",
  "ver.revertFailed":   "Impossible de revenir a cette version.",

  // batch 9 - domain.*
  "domain.status.live":      "Actif",
  "domain.status.progress":  "En cours",
  "domain.status.failed":    "Echoue",
  "domain.status.pending":   "En attente",
  "domain.title":            "Domaines personnalises",
  "domain.subtitle":         "Connectez votre propre domaine a ce projet.",
  "domain.add":              "Ajouter un domaine",
  "domain.dnsInstructions":  "Ajoutez ces enregistrements DNS chez votre registraire de domaine :",
  "domain.copy":             "Copier",
  "domain.verify":           "Verifier",
  "domain.remove":           "Supprimer",
  "domain.registerError":    "Impossible d'enregistrer le domaine.",
  "domain.dnsError":         "Impossible de recuperer les instructions DNS.",
  "domain.verifyFailed":     "La verification a echoue.",
  "domain.record.host":      "Hote",
  "domain.record.value":     "Valeur",

  // batch 9 - sb.*
  "sb.title":                    "Connecter Supabase",
  "sb.subtitle":                 "Liez votre projet Supabase pour la persistance cloud et l'authentification reelle.",
  "sb.anonOnly":                 "Cle anonyme uniquement",
  "sb.anonService":              "Cle anonyme et cle de role de service",
  "sb.connectBtn":               "Connecter",
  "sb.disconnect":               "Deconnecter",
  "sb.urlRequired":              "L'URL du projet est obligatoire.",
  "sb.connectError":             "Connexion impossible. Verifiez vos identifiants.",
  "sb.connected":                "Supabase connecte.",
  "sb.anonPlaceholder":          "Cle API publique (anon)",
  "sb.serviceRolePlaceholder":   "Cle de role de service (facultatif)",

  // batch 9 - hdm.*
  "hdm.title":             "Deployer",
  "hdm.connectTitle":      "Connecter un fournisseur d'hebergement",
  "hdm.deploy":            "Deployer",
  "hdm.open":              "Ouvrir le site",
  "hdm.disconnect":        "Deconnecter",
  "hdm.tokenRequired":     "Un token API est requis.",
  "hdm.connectError":      "Impossible de se connecter au fournisseur.",
  "hdm.deployFailed":      "Le deploiement a echoue (HTTP {status}).",
  "hdm.deployFailed2":     "Le deploiement a echoue.",
  "hdm.noCredits":         "{acu} credits sont necessaires pour deployer.",
  "hdm.more":              "plus",
  "hdm.connected":         "Connecte a {provider}.",
  "hdm.liveon":            "En ligne sur {provider}.",
  "hdm.tokenPlaceholder":  "Jeton d'acces {provider}",
  "hdm.vercelProjectId":   "ID du projet Vercel",
  "hdm.netlifySiteId":     "ID du site Netlify",
  "hdm.vercelTeamId":      "ID de l'equipe Vercel (facultatif)",
  "hdm.connect":           "Connecter {provider}",

  // batch 9 - imp.*
  "imp.title":            "Importer un depot",
  "imp.subtitle":         "Importez un projet GitHub existant dans Nuvra.",
  "imp.btnLabel":         "Importer un depot",
  "imp.preview":          "Apercu",
  "imp.import":           "Importer",
  "imp.tokenPlaceholder": "Token d'acces personnel GitHub",
  "imp.previewError":     "Impossible d'obtenir l'apercu du depot.",
  "imp.importError":      "L'importation a echoue.",
  "imp.models":           "modeles",
  "imp.routes":           "routes",
  "imp.done":             "Importation terminee : {files} fichiers, {models} modeles, {pages} pages.",

  // batch 9 - sr.*
  "sr.docsTitle":       "Preparation pour la boutique",
  "sr.dataDisclosure":  "Donnees collectees : {data}.",
  "sr.minimalData":     "aucune",
  "sr.privacy":         "Politique de confidentialite",
  "sr.terms":           "Conditions d'utilisation",
  "sr.listing":         "Description de la boutique",
  "sr.screenshots":     "Captures d'ecran",
  "sr.genError":        "Impossible de generer le contenu de la boutique.",
  "sr.shotsError":      "Impossible de generer les captures d'ecran.",
  "sr.shotsError2":     "Impossible de telecharger la capture d'ecran.",

  // batch 9 - erd.*
  "erd.title":       "Diagramme de la base de donnees",
  "erd.fromTable":   "Depuis la table",
  "erd.references":  "reference",
  "erd.colName":     "nom de colonne",
  "erd.addRel":      "Relation",
  "erd.noTables":    "Aucune table a afficher pour l'instant.",
  "erd.pending":     "en attente",
  "erd.relError":    "Impossible d'ajouter la relation.",

  // batch 9 - tdv.*
  "tdv.search":        "Rechercher des lignes...",
  "tdv.loading":       "Chargement...",
  "tdv.noRows":        "Aucune ligne dans cette table.",
  "tdv.noRowsSearch":  "Aucune ligne ne correspond a la recherche.",
  "tdv.clickEdit":     "Cliquer pour modifier",
  "tdv.loadError":     "Impossible de charger les donnees.",
  "tdv.saveError":     "Impossible d'enregistrer la modification.",

  // batch 9 - wft.*
  "wft.testTitle":    "Tester {name}",
  "wft.dryRun":       "Simulation",
  "wft.payloadLabel": "Charge utile de test (JSON)",
  "wft.invalidJson":  "JSON invalide. Corrigez avant de continuer.",
  "wft.executing":    "Execution...",
  "wft.runTest":      "Lancer le test",
  "wft.liveBadge":    "En direct",
  "wft.steps":        "{n} etapes",

  // batch 9 - wfv.*
  "wfv.saveTitle":      "Enregistrer une version",
  "wfv.currentBadge":   "v{n}",
  "wfv.placeholder":    "Message de version",
  "wfv.save":           "Enregistrer",
  "wfv.historyTitle":   "Versions enregistrees",
  "wfv.empty":          "Aucune version enregistree pour l'instant.",
  "wfv.restore":        "Restaurer",

  // batch 10 - env.*
  "env.title":          "Environnements",
  "env.notReleased":    "non publie",
  "env.build":          "compilation {id}",
  "env.promoteStaging": "Promouvoir le staging",
  "env.rollBack":       "Revenir en arriere",
  "env.rollbackTitle":  "Revenir a la compilation {id}",
  "env.config":         "Config",
  "env.add":            "Ajouter",
  "env.actionFailed":   "L'action a echoue",
  "env.description":    "Les nouvelles compilations sont publiees en staging. Promouvez en production quand vous etes pret. La configuration est pour les parametres non secrets ; gardez les secrets dans votre plateforme de deploiement.",

  "assets.open":        "Ressources",
  "assets.title":       "Ressources",
  "assets.subtitle":    "Importez des medias et placez-les dans votre app",
  "assets.empty":       "Aucune ressource. Importez une image, un audio ou une video.",
  "assets.drop":        "Glissez des fichiers ici, ou cliquez pour importer",
  "assets.uploading":   "Import {pct}%",
  "assets.retry":       "Reessayer",
  "assets.delete":      "Supprimer",
  "assets.replace":     "Remplacer",
  "assets.selected":    "{count} selectionnes",
  "assets.placeHint":   "Dites ou le placer, par exemple : mets ceci dans le hero",
  "assets.place":       "Placer dans l'app",
  "assets.placing":     "Placement",
  "assets.placed":      "Place dans {target}",
  "assets.inUse":       "Utilise",
  "assets.close":       "Fermer",
  "assets.failed":      "Un probleme est survenu. Reessayez.",
  "assets.loadFailed":  "Impossible de charger les ressources.",
  "assets.removed":     "Supprime",

  // batch 10 - msm.*
  "msm.title":          "Generer des medias",
  "msm.label":          "Medias",
  "msm.placeholder":    "Decrivez une image ou collez du texte pour le convertir en voix...",
  "msm.image":          "Image",
  "msm.speech":         "Voix",
  "msm.music":          "Musique",
  "msm.download":       "Telecharger",
  "msm.imageAlt":       "Genere",
  "msm.imageGen":       "Generation d'image",
  "msm.speechGen":      "Generation de voix",
  "msm.musicGen":       "Generation de musique",
  "msm.notConfigured":  "{what} n'est pas encore configure.",
  "msm.noCredits":      "Credits insuffisants (il en faut {acu} credits).",
  "msm.more":           "plus",
  "msm.failedHttp":     "{what} a echoue (HTTP {status}).",
  "msm.imgFailed":      "La generation d'image a echoue.",
  "msm.speechFailed":   "La generation de voix a echoue.",
  "msm.musicFailed":    "La generation de musique a echoue.",
  "msm.length":         "Duree",
  "msm.instrumental":   "Instrumental",
  "msm.library":        "Vos morceaux",
  "msm.titleField":     "Titre (optionnel)",
  "msm.genreField":     "Style (optionnel)",
  "msm.lyricsField":    "Paroles (optionnel)",
  "msm.musicTooLong":   "Le fournisseur de musique configure atteint {max} minutes.",
  "msm.musicStillRunning": "Generation en cours. Le morceau apparaitra dans votre bibliotheque une fois termine.",
  "msm.musicCancelled": "Cette generation n'est plus en cours.",
  "msm.mfail.unsupported": "Cette combinaison n'est pas disponible. Essayez d'autres reglages.",
  "msm.mfail.unavailable": "La musique n'est pas disponible pour le moment. Vous n'avez pas ete facture.",
  "msm.mfail.timeout": "Cela a pris trop de temps et a ete arrete. Vous n'avez pas ete facture.",
  "msm.mfail.validation": "La piste recue ne correspondait pas a votre demande, elle a ete ecartee. Vous n'avez pas ete facture.",
  "msm.mfail.storage": "La piste n'a pas pu etre enregistree. Vous n'avez pas ete facture.",
  "msm.mfail.provider": "Le service de musique n'a pas termine celle-ci. Vous n'avez pas ete facture.",
  "msm.cancel":         "Annuler",
  "msm.stage.queued":     "En attente",
  "msm.stage.running":    "Generation",
  "msm.stage.generating": "Generation",
  "msm.stage.validating": "Verification de l'audio",
  "msm.stage.illustrating": "Creation de la pochette",
  "msm.coverUnavailable": "Morceau genere. La pochette n'est pas disponible pour ce titre.",
  "msm.play":           "Lire",
  "msm.downloadAudio":  "Telecharger l'audio",
  "msm.downloadCover":  "Telecharger la pochette",
  "msm.coverAlt":       "Pochette de {title}",
  "msm.noCover":        "Pas de pochette",
  "msm.stage.saving":     "Enregistrement",
  "msm.video":          "Video",
  "msm.videoGen":       "Generation video",
  "msm.videoFailed":    "La generation video a echoue.",
  "msm.videoRefused":   "Le fournisseur video a refuse cette demande.",
  "msm.videoLength":    "Clip",
  "msm.videoTier":         "Qualite",
  "msm.videoTier.fast":    "Rapide",
  "msm.videoTier.quality": "Qualite maximale",
  "msm.videoAudio":     "Avec son",
  "msm.vfail.unsupported": "Cette combinaison n'est pas disponible. Essayez une autre duree, taille ou qualite.",
  "msm.vfail.unavailable": "La video n'est pas disponible pour le moment. Vous n'avez pas ete facture.",
  "msm.vfail.timeout":     "Cela a pris trop de temps et a ete arrete. Vous n'avez pas ete facture.",
  "msm.vfail.validation":  "Le clip recu ne correspondait pas a votre demande, il a ete ecarte. Vous n'avez pas ete facture.",
  "msm.vfail.storage":     "Le clip n'a pas pu etre enregistre. Vous n'avez pas ete facture.",
  "msm.vfail.provider":    "Le service video n'a pas termine celui-ci. Vous n'avez pas ete facture.",
  "msm.videoLibrary":   "Vos clips",
  "msm.videoHasAudio":  "avec son",
  "msm.videoOption":    "{seconds}s ({acu} credits)",
  "msm.videoLengths":   "Ce fournisseur genere des clips de {lengths} secondes.",
  "msm.videoStillRunning": "Generation en cours. Le clip apparaitra dans votre bibliotheque une fois termine.",
  "msm.videoCancelled": "Cette generation n'est plus en cours.",
  "msm.vstage.submitting": "Envoi de votre demande",
  "msm.vstage.queued":     "En file d'attente",
  "msm.vstage.generating": "Generation",
  "msm.vstage.retrieving": "Recuperation du fichier",
  "msm.vstage.validating": "Verification de la video",
  "msm.vstage.saving":     "Enregistrement",

  // batch 10 - wb.*
  "wb.dismiss":         "Fermer le resume",
  "wb.teamHere":        "Votre equipe est la",
  "wb.welcomeBack":     "Bon retour sur {name}.",
  "wb.lastWorked":      "Nous avons travaille dessus {ago}.",
  "wb.productStands":   "Voici l'etat actuel de votre produit.",
  "wb.buildingToward":  "Construction vers",
  "wb.whatNext":        "Ce que je ferais ensuite",
  "wb.buildThis":       "Construire",

  // batch 10 - wcm.*
  "wcm.title":          "Connecter GitHub, votre base de donnees et vos integrations",
  "wcm.ariaLabel":      "Connecter les integrations",
  "wcm.connectLabel":   "Connecter",
  "wcm.sectionTitle":   "Connexions",
  "wcm.database":       "Base de donnees et donnees",
  "wcm.integrations":   "Toutes les integrations",

  // batch 10 - erec.*
  "erec.title":         "Ce que votre equipe d'ingenierie recommande ensuite",
  "erec.subtitle":      "Prochaines etapes reelles de l'etat actuel de votre projet. Un tap pour construire.",
  "erec.buildThis":     "Construire ceci",

  // batch 10 - bal.*
  // batch 11 - bst.* (durable build execution timeline)
  "bst.heading":        "Journal d'ingenierie",
  "bst.historical":     "Plus tot dans cette construction",
  "bst.live":           "En cours maintenant",
  "bst.interrupted":    "Non termine",
  "bst.running":        "En cours",
  "bst.completed":      "Termine",
  "bst.failed":         "Echec",
  "bst.attempts":       "Tente {count} fois",
  "bst.unavailable":    "Aucun journal d'ingenierie n'a ete trouve pour cette construction.",
  "bst.unreachable":    "Nuvra n'a pas pu acceder au journal d'ingenierie. Cela ne signifie pas que la construction a echoue.",
  "bst.forbidden":      "Cette construction ne vous est pas accessible.",
  "bst.loading":        "Lecture du journal d'ingenierie",
  "bst.noPhases":       "Cette construction n'a enregistre aucune phase.",
  "bst.retry":          "Reessayer",
  "bst.startedAt":      "Demarre {time}",
  "bst.duration":       "A dure {duration}",

  "bal.builtProduct":    "Votre produit a ete construit",
  "bal.buildingProduct": "Construction de votre produit",
  "bal.builtData":       "Construit votre {nouns}",
  "bal.assembling":      "Assemblage de votre produit",
  "bal.openInFiles":     "Ouvrir dans Fichiers",

  // batch 10 - pp.*
  "pp.title":           "Pulse d'orchestration",
  "pp.releaseEvents":   "Evenements de publication",
  "pp.mobileBuilds":    "Compilations mobiles",
  "pp.lastEvent":       "Dernier evenement",
  "pp.standingBy":      "Pipeline en attente - aucun evenement pour l'instant.",
  "pp.viewFull":        "Voir l'intelligence de plateforme complete",
  "pp.justNow":         "A l'instant",
  "pp.minsAgo":         "il y a {n}m",
  "pp.hoursAgo":        "il y a {n}h",
  "pp.daysAgo":         "il y a {n}j",

  // batch 10 - wqa.*
  "wqa.heading":        "Construire et lancer",
  "wqa.generateApp":    "Generer une app",
  "wqa.shipBuild":      "Lancer la compilation",
  "wqa.myProjects":     "Mes projets",
  "wqa.buyCredits":     "Acheter des credits",
  "wqa.credits":        "{n} credits",

  // batch 10 - eaf.*
  "eaf.earlierStep":    "{n} etape precedente",
  "eaf.earlierSteps":   "{n} etapes precedentes",
  "eaf.now":            "maintenant",

  // batch 10 - etl.*
  "etl.now":            "maintenant",

  // batch 11 - dt.*
  "dt.title":           "Pipeline de deploiement",
  "dt.loading":         "Chargement de l'historique de compilation...",
  "dt.empty":           "Aucune compilation. Lancez votre premiere compilation depuis le Tableau de bord.",
  "dt.preview":         "Apercu",
  "dt.statusLive":      "En ligne",
  "dt.statusFailed":    "Echoue",
  "dt.statusGenerating": "Generation",
  "dt.justNow":         "A l'instant",
  "dt.minsAgo":         "il y a {n}m",
  "dt.hoursAgo":        "il y a {n}h",
  "dt.daysAgo":         "il y a {n}j",

  // batch 11 - iaf.*
  "iaf.title":          "Evenements d'orchestration",
  "iaf.loading":        "Chargement de l'activite...",
  "iaf.empty":          "Aucune compilation",
  "iaf.emptyDesc":      "Les agents IA sont prets. Decrivez ce que vous voulez construire.",
  "iaf.shipped":        "Application deployee",
  "iaf.failed":         "Generation echouee",
  "iaf.generating":     "Agents IA en cours de generation...",
  "iaf.inQueue":        "En attente",
  "iaf.justNow":        "A l'instant",
  "iaf.minsAgo":        "il y a {n}m",
  "iaf.hoursAgo":       "il y a {n}h",
  "iaf.daysAgo":        "il y a {n}j",

  // batch 11 - aqa.*
  "aqa.platformStatus": "Etat de la plateforme",
  "aqa.platformDesc":   "Voir l'orchestration, les sorties et l'infrastructure de compilation en temps reel",
  "aqa.debugAi":        "Debogage avec IA",
  "aqa.debugDesc":      "Ouvrez un espace projet pour diagnostiquer un echec de compilation ou de deploiement",

  // batch 11 - uqa.*
  "uqa.generateApp":    "Generer une app",
  "uqa.deploy":         "Deployer",
  "uqa.mobileBuild":    "Compilation mobile",
  "uqa.platform":       "Plateforme",
  "uqa.buyCredits":     "Acheter des credits",
  "uqa.credits":        "{n} credits",

  // batch 11 - iap.*
  "iap.title":          "Alertes",
  "iap.healthy":        "Tous les systemes sont operationnels. Aucune alerte active.",
  "iap.resolve":        "Resoudre l'incident",
  "iap.justNow":        "a l'instant",
  "iap.minsAgo":        "il y a {n}m",
  "iap.hoursAgo":       "il y a {n}h",

  // batch 11 - cgb.*
  "cgb.disconnect":     "Deconnecter",
  "cgb.installApp":     "Installer l'app GitHub",
  "cgb.connect":        "Connecter GitHub",
  "cgb.notConfigured":  "GitHub n'est pas configure sur le serveur",

  // batch 11 - rcp.*
  "rcp.running":        "Execution de votre code",
  "rcp.rerunTitle":     "Relancer avec le dernier code et les modifications",
  "rcp.rerun":          "Relancer",
  "rcp.loadError":      "Impossible d'executer le code.",
  "rcp.noCode":         "Aucun code executable. Construisez d'abord le projet.",

  // batch 11 - bih.*
  "bih.notSignedIn":    "Non connecte",
  "bih.notSignedInDesc": "Connectez-vous et reessayez.",
  "bih.noCredits":      "Plus de credits",
  "bih.noCreditsDesc":  "Ajoutez des credits ou mettez a niveau votre plan pour continuer avec Nuvra.",
  "bih.createError":    "Impossible de creer le projet",
  "bih.docCreateError": "Impossible de creer le document",
  "bih.docCreated":     "Document cree",
  "bih.docCreatedFallback": "Votre document",
  "bih.docPages":       "pages",
  "bih.unexpectedError": "Erreur inattendue. Veuillez reessayer.",
  "bih.permissionError": "Votre compte n'a peut-etre pas la permission ou assez de credits. Verifiez votre facturation.",
  "bih.wentWrong":      "Quelque chose s'est mal passe",
  "bih.tryAgain":       "Veuillez reessayer.",
  "composerFail.engineUnavailable": "Le service d'ingenierie de Nuvra est indisponible pour le moment, rien n'a ete construit et aucun credit n'a ete utilise. Reessayez dans quelques instants.",
  "composerFail.needCredits":       "Vous n'avez pas assez de credits pour cette construction. Aucun credit n'a ete utilise.",
  "composerFail.needPlan":          "Ce type de produit n'est pas inclus dans votre offre actuelle.",
  "composerFail.describeProduct":   "Decrivez le produit : ce qu'il doit faire et qui l'utilise.",
  "composerFail.signedOut":         "Votre session a expire. Connectez-vous et reessayez.",
  "composerFail.notAllowed":        "Votre compte n'a pas acces a cela.",
  "composerFail.busy":              "Nuvra traite trop de constructions en ce moment. Reessayez dans un instant.",
  "composerFail.failed":            "Impossible de creer cela. Rien n'a ete construit et aucun credit n'a ete utilise. Veuillez reessayer.",
  "bih.placeholder":    "Decrivez le produit a developper...",
  "bih.ariaLabel":      "Decrivez votre idee d'app",
  "bih.hint":           "Entree pour envoyer",
  "bih.attachImageInWorkspace":     "Les images s'ajoutent dans l'espace projet. Lancez le build et ajoutez-les la-bas.",
  "bih.ex.saasMultiTenant":       "Creez une plateforme SaaS multi-locataires avec authentification, abonnements, equipes et acces par roles",
  "bih.ex.inventory":             "Creez une plateforme de stock et de ventes avec suivi des stocks, commandes et rapports",
  "bih.ex.customerPortal":        "Creez un portail client avec authentification, paiements, documents et messagerie",
  "bih.ex.marketplace":           "Creez une marketplace avec vendeurs, fiches produits, commandes, paiements et avis",
  "bih.ex.aiProduct":             "Creez une plateforme de productivite augmentee par l'IA avec espaces, projets et flux intelligents",
  "bih.ex.booking":               "Creez une plateforme de reservation avec comptes clients, disponibilites, paiements et notifications",
  "bih.ex.internalOps":           "Creez une plateforme d'operations internes avec validations, rapports, roles et historique d'audit",
  "bih.ex.learning":              "Creez une plateforme de formation avec cours, comptes eleves, suivi de progression et abonnements",
  "bih.ex.ecommerce":             "Creez une boutique en ligne avec catalogue, panier, paiement et gestion des commandes",
  "bih.ex.apiBackend":            "Creez une API REST avec authentification, limitation de debit, webhooks et documentation",
  "bih.ex.dashboard":             "Creez un tableau de bord analytique avec donnees connectees, graphiques, filtres et rapports programmes",
  "bih.ex.mobileApp":             "Creez une application mobile avec comptes, donnees hors ligne, notifications push et paiements integres",
  "bih.ex.game3d":               "Creez un jeu d'aventure 3D avec physique, camera a la troisieme personne, ennemis, objets a collecter et progression sauvegardee",

  // batch 12 - rv.*
  "rv.title":           "Recu",
  "rv.subtitle":        "Telechargez ou ouvrez votre recu PDF.",
  "rv.downloadPdf":     "Telecharger le PDF",
  "rv.openTab":         "Ouvrir dans un nouvel onglet",

  // batch 12 - th.*
  "th.loading":         "Chargement des transactions...",
  "th.empty":           "Aucune transaction",
  "th.creditsAdded":    "{n} credits ajoutes",
  "th.view":            "Voir",
  "th.download":        "Telecharger",

  // batch 12 - rm.*
  "rm.title":           "Recu",
  "rm.creditsAdded":    "{n} credits ajoutes",
  "rm.reference":       "Reference",
  "rm.date":            "Date",
  "rm.plan":            "Plan",
  "rm.secureNote":      "Cette transaction a ete traitee de facon securisee.",
  "rm.print":           "Imprimer",
  "rm.copyRef":         "Copier la ref.",
  "rm.refCopied":       "Reference copiee",

  // batch 12 - acu.*
  "acu.title":          "Utilisation de calcul",
  "acu.creditsUsed":    "Credits utilises",
  "acu.credits":        "{used} / {total} credits",
  "acu.percentUsed":    "{n}% utilise",
  "acu.remaining":      "{n} restants",
  "acu.daysRemaining":  "Jours restants",
  "acu.dailyRate":      "credits / jour moy.",
  "acu.recommendations": "Recommandations",
  "acu.byProject":      "Utilisation par projet",
  "acu.general":        "General",
  "acu.high":           "Eleve",

  // batch 12 - scc.*
  "scc.heading":        "Paiements Stripe",
  "scc.enabled":        "Actif",
  "scc.connected":      "Votre compte Stripe est connecte et pret a recevoir des paiements.",
  "scc.description":    "Connectez un compte Stripe pour recevoir des paiements. Vous completerez une configuration breve et securisee sur Stripe.",
  "scc.notEnabled":     "Stripe n'est pas encore actif.",
  "scc.startFailed":    "Impossible de demarrer l'integration.",
  "scc.continueSetup":  "Continuer la configuration Stripe",
  "scc.connect":        "Connecter Stripe",

  // batch 12 - cd.*
  "cd.loading":         "Chargement des revenus...",
  "cd.empty":           "Aucun revenu de la marketplace",
  "cd.payoutHold":      "Paiement en attente",
  "cd.loadError":       "Erreur de chargement des revenus",
  "cd.totalEarned":     "Total gagne",
  "cd.pending":         "En attente (retenu)",
  "cd.available":       "Disponible",
  "cd.nextPayout":      "Prochain paiement : {date}. {notice}",

  // batch 12 - pmf.*
  "pmf.heading":        "Methode de paiement",
  "pmf.update":         "Mettre a jour",
  "pmf.accountEnding":  "Compte se terminant par {last4}",
  "pmf.bankCodeDisplay": "Code bancaire {code}",
  "pmf.saved":          "Enregistre.",
  "pmf.description":    "Les revenus liquides sont envoyes ici automatiquement a chaque date de paiement. Les details sont chiffres.",
  "pmf.accountNumber":  "Numero de compte",
  "pmf.bankCodeLabel":  "Code bancaire",
  "pmf.beneficiaryName": "Nom du beneficiaire",
  "pmf.country":        "Pays (code 2 lettres, optionnel)",
  "pmf.saving":         "Enregistrement...",
  "pmf.saveMethod":     "Enregistrer la methode de paiement",
  "pmf.cancel":         "Annuler",
  "pmf.signInError":    "Connectez-vous pour enregistrer une methode de paiement",
  "pmf.saveError":      "Impossible d'enregistrer la methode de paiement",

  // batch 12 - mp.*
  "mp.applied":         "Appliquees",
  "mp.pending":         "En attente",
  "mp.runPending":      "Appliquer les en attente",
  "mp.dryRun":          "Execution test",
  "mp.up":              "HAUT",
  "mp.down":            "BAS",
  "mp.apply":           "Appliquer",
  "mp.rollback":        "Revenir",

  // batch 12 - svp.*
  "svp.createSnapshot": "Creer un instantane",
  "svp.description":    "Description de l'instantane",
  "svp.dev":            "Dev",
  "svp.staging":        "Staging",
  "svp.prod":           "Prod",
  "svp.save":           "Enregistrer",
  "svp.modelsInSnapshot": "{n} modeles dans l'instantane",
  "svp.restore":        "Restaurer",
  "svp.viewDiff":       "Voir les differences",

  // batch 12 - qb.*
  "qb.visual":          "Visuel",
  "qb.sql":             "SQL",
  "qb.sortBy":          "Trier par",
  "qb.noSort":          "Sans tri",
  "qb.asc":             "ASC",
  "qb.desc":            "DESC",
  "qb.filters":         "Filtres",
  "qb.addFilter":       "Ajouter un filtre",
  "qb.value":           "Valeur",
  "qb.execute":         "Executer",
  "qb.results":         "Resultats",
  "qb.rows":            "{n} lignes",
  "qb.sqlPlaceholder":  "SELECT * FROM...",

  // batch 13 - ddt.*
  "ddt.loading":               "Chargement de la declaration de deploiement...",
  "ddt.loadError":             "Echec du chargement",
  "ddt.saveError":             "Echec de l'enregistrement",
  "ddt.noDecl":                "Aucune declaration chargee",
  "ddt.title":                 "Declaration de deploiement",
  "ddt.lastUpdated":           "Derniere mise a jour {date}",
  "ddt.saved":                 "Enregistre",
  "ddt.saving":                "Enregistrement...",
  "ddt.save":                  "Enregistrer",
  "ddt.airGappedWarning":      "Deploiement isole: les exportations de projets sont bloquees. Contactez votre equipe d'infrastructure avant d'activer.",
  "ddt.deploymentTarget":      "Cible de deploiement",
  "ddt.deploymentTargetHint":  "Controle les exigences d'infrastructure et les politiques d'exportation applicables.",
  "ddt.keyMgmt":               "Gestion des cles",
  "ddt.keyMgmtHint":           "Comment les cles de chiffrement sont gerees pour les donnees de cette organisation.",
  "ddt.hsmProvider":           "Fournisseur HSM",
  "ddt.hsmProviderHint":       "Nom ou identifiant de votre fournisseur HSM.",
  "ddt.strictResidency":       "Residence stricte des donnees",
  "ddt.strictResidencyHint":   "Garantit que toutes les donnees restent dans la region primaire declaree.",
  "ddt.primaryRegion":         "Region primaire",
  "ddt.primaryRegionHint":     "Code de region ISO ou toutes les donnees de l'organisation doivent resider.",
  "ddt.complianceFrameworks":  "Cadres de conformite",
  "ddt.infraTitle":            "Exigences d'infrastructure",
  "ddt.sovereignBadge":        "Contexte souverain",
  "ddt.airGappedBadge":        "Isole",
  "ddt.hsmBadge":              "HSM requis",
  "ddt.byokBadge":             "BYOK",
  "ddt.strictResidencyBadge":  "Residence stricte",
  "ddt.networkIsolation":      "Isolation reseau",
  "ddt.onPremInfra":           "Infra locale",
  "ddt.provisioning":          "Provisionnement requis avant le lancement:",
  "ddt.opNotes":               "Notes operationnelles:",
  "ddt.targetNuvraCloud":      "Nuvra Cloud",
  "ddt.targetPrivateCloud":    "Nuage prive",
  "ddt.targetOnPremise":       "Sur site",
  "ddt.targetSovereign":       "Souverain",
  "ddt.targetAirGapped":       "Isole",
  "ddt.kmPlatform":            "Gere par la plateforme",
  "ddt.kmCustomerManaged":     "Gere par le client (BYOK)",
  "ddt.kmHsm":                 "Module de securite materiel",

  // batch 13 - sat.*
  "sat.loading":         "Chargement des comptes de service...",
  "sat.loadError":       "Echec du chargement",
  "sat.createError":     "Echec de la creation",
  "sat.revokeError":     "Echec de la revocation",
  "sat.title":           "Comptes de service",
  "sat.newAccount":      "Nouveau compte",
  "sat.keyCreated":      "Compte de service cree. Conservez cette cle maintenant.",
  "sat.keyOnce":         "Elle ne sera plus affichee.",
  "sat.copied":          "Copie",
  "sat.dismiss":         "Fermer",
  "sat.newAccountTitle": "Nouveau compte de service",
  "sat.name":            "Nom",
  "sat.scopes":          "Permissions",
  "sat.expiresAt":       "Expire le (facultatif)",
  "sat.cancel":          "Annuler",
  "sat.creating":        "Creation...",
  "sat.create":          "Creer",
  "sat.empty":           "Aucun compte de service pour l'instant",
  "sat.revoked":         "revoque",
  "sat.created":         "Cree le {date}",
  "sat.lastUsed":        "Derniere utilisation {date}",
  "sat.expires":         "Expire le {date}",
  "sat.revokeTitle":     "Revoquer",

  // batch 13 - swt.*
  "swt.loading":         "Chargement des webhooks...",
  "swt.loadError":       "Echec du chargement",
  "swt.createError":     "Echec de la creation",
  "swt.deleteError":     "Echec de la suppression",
  "swt.title":           "Webhooks vendeur",
  "swt.addEndpoint":     "Ajouter un endpoint",
  "swt.webhookCreated":  "Webhook cree. Conservez le secret de signature maintenant.",
  "swt.secretOnce":      "Il ne sera plus affiche.",
  "swt.dismiss":         "Fermer",
  "swt.newEndpoint":     "Nouvel endpoint webhook",
  "swt.url":             "URL",
  "swt.events":          "Evenements",
  "swt.allEventsHint":   "Vide = recevoir tous les evenements.",
  "swt.cancel":          "Annuler",
  "swt.creating":        "Creation...",
  "swt.create":          "Creer",
  "swt.empty":           "Aucun endpoint webhook pour l'instant",
  "swt.disabled":        "desactive",
  "swt.receivingAll":    "Reception de tous les evenements",
  "swt.created":         "Cree le {date}",
  "swt.lastCalled":      "Dernier appel {date}",
  "swt.testTitle":       "Envoyer un evenement de test",
  "swt.deleteTitle":     "Supprimer",

  // batch 13 - eep.*
  "eep.prodArchTitle":         "Architecture prete pour la production",
  "eep.prodArch1":             "Livraison d'actifs statiques via CDN Cloudflare Pages",
  "eep.prodArch2":             "Integrite par somme de controle SHA-256 sur chaque artefact deploye",
  "eep.prodArch3":             "L'analyse de securite controle chaque compilation. Les resultats critiques bloquent le deploiement",
  "eep.prodArch4":             "Controle d'admission: le score de validation et l'analyse de securite doivent reussir avant le deploiement",
  "eep.prodArch5":             "Certification de compilation: certificat signe emis pour chaque deploiement reussi",
  "eep.secureInfraTitle":      "Infrastructure securisee",
  "eep.secureInfra1":          "Chiffrement de bout en bout pour les donnees en transit et au repos",
  "eep.secureInfra2":          "Heberge sur une infrastructure certifiee SOC 2 Type II (Cloudflare Pages + Supabase)",
  "eep.secureInfra3":          "Analyse de securite statique a chaque compilation: injection SQL, XSS, eval() et detection de credentials codees en dur",
  "eep.secureInfra4":          "Politiques de securite au niveau des lignes sur tous les acces aux donnees",
  "eep.secureInfra5":          "Gestion des secrets avec stockage en coffre-fort chiffre",
  "eep.enterpriseDeployTitle": "Deploiement de niveau entreprise",
  "eep.enterpriseDeploy1":     "La sonde de sante HTTP avec nouvelles tentatives valide chaque deploiement avant de le marquer actif",
  "eep.enterpriseDeploy2":     "Retour arriere automatique en cas d'echec des controles de sante",
  "eep.enterpriseDeploy3":     "Controle de gouvernance du deploiement: les echecs consecutifs bloquent automatiquement les nouveaux deploiements",
  "eep.enterpriseDeploy4":     "Verification d'empreinte en direct: somme de controle deployee comparee au manifeste de compilation",
  "eep.enterpriseDeploy5":     "Stockage des artefacts de compilation sur CDN mondial avec URLs de previsualisation partageables",
  "eep.aiAutomationTitle":     "Automatisation de l'ingenierie par IA",
  "eep.aiAutomation1":         "L'IA genere et valide tout le code de l'application dans le pipeline de compilation",
  "eep.aiAutomation2":         "Generation automatisee de tests pour la couverture UI, API, donnees et flux de travail",
  "eep.aiAutomation3":         "Moteur de reparation autonome: les echecs de validation declenchent une reparation ciblee du code avant le deploiement",
  "eep.aiAutomation4":         "Classification intelligente des erreurs et analyse des causes profondes",

  // batch 13 - lpp.*
  "lpp.describe":         "Decrivez ce que vous souhaitez creer",
  "lpp.generate":         "Generer",
  "lpp.analyzing":        "Analyse de votre description et planification de l'architecture...",
  "lpp.secStandard":      "Standard",
  "lpp.secElevated":      "Elevee",
  "lpp.secCritical":      "Critique",
  "lpp.securityBadge":    "Securite: {level}",
  "lpp.confidenceMatch":  "{n}% de correspondance",
  "lpp.coreWorkflows":    "Flux principaux:",
  "lpp.buildApp":         "Creer cette app",
  "lpp.tryAnother":       "Essayer une autre",
  "lpp.footer":           "Ceci est le planificateur d'architecture reel de Nuvra. Inscrivez-vous pour generer l'application complete et fonctionnelle.",
  "lpp.timeout":          "Ca a pris trop de temps. Reessayez.",
  "lpp.previewError":     "Impossible de creer un apercu.",
  "lpp.prompt1":          "Creer un SaaS de gestion de projets avec collaboration d'equipe",
  "lpp.prompt2":          "Creer un tableau de bord fintech avec analyses en temps reel",
  "lpp.prompt3":          "Concevoir un marketplace e-commerce avec des outils pour vendeurs",

  // batch 13 - alp.*
  "alp.title":   "Journal d'audit",
  "alp.entries": "{n} entrees",
  "alp.by":      "par {user}",

  // batch 14 - auth.*
  "auth.preparing": "Preparation de votre experience...",
  "auth.checking":  "Verification de l'authentification...",

  // batch 14 - wm.*
  "wm.ariaLabel": "Cree avec Nuvra - ouvrir les details",
  "wm.builtWith": "Cree avec",

  // batch 14 - agp.*
  "agp.endpoints":    "{n} points de terminaison",
  "agp.graphqlSchema": "Schema GraphQL",

  // batch 14 - sfd.*
  "sfd.empty": "Soumettez un prompt pour generer des flux systeme",

  // batch 14 - prc.*
  "prc.loadError":  "Impossible de charger l'etat des rails de paiement. Rechargez pour reessayer.",
  "prc.noRail":     "Aucun canal de paiement configure. Ajoutez FLUTTERWAVE_SECRET_KEY ou une cle Stripe.",
  "prc.webhookUrl": "URL du webhook Flutterwave",
  "prc.title":         "Canaux de paiement",
  "prc.statusActive":  "Actif",
  "prc.statusReady":   "Configure",
  "prc.statusMissing": "Non configure",
  "prc.webhookHint":   "Pointez le webhook de votre tableau de bord Flutterwave ici. Il gere a la fois les paiements et le reglement des transferts (versements).",
  "prc.copyTitle":  "Copier",

  // batch 14 - rn.*
  "rn.aiCredits":    "+{n} credits IA",
  "rn.upgradeTitle": "Passez a un forfait pour continuer a construire",
  "rn.viewPlans":    "Voir les plans payants",
  "rn.maybeLater":   "Peut-etre plus tard",

  // batch 14 - sd.*
  "sd.creditsRemaining": "credits restants",
  "sd.trial":            "Essai",
  "sd.creditsUsed":      "Credits d'essai utilises",
  "sd.upgrade":          "Passez a la version superieure pour continuer",
  "sd.creditsCompact":   "{n} credits",

  // batch 14 - psb.*
  "psb.dismiss": "Fermer",

  // batch 14 - dep.*
  "dep.title":         "Systeme IA global",
  "dep.ai":            "IA",
  "dep.fixing":        "Reparation...",
  "dep.forceFix":      "Forcer la reparation",
  "dep.runtimeActive": "Environnement actif",

  // batch 14 - ip.*
  "ip.search":    "Rechercher des integrations...",
  "ip.configure": "Configurer",
  "ip.connect":   "Connecter",

  // batch 14 - wap.*
  "wap.totalRuns":       "Executions totales",
  "wap.avgSuccess":      "Succes moyen",
  "wap.avgDuration":     "Duree moyenne",
  "wap.activeWorkflows": "Flux actifs",
  "wap.runs":            "executions",
  "wap.success":         "succes",
  "wap.avgMs":           "Moy. {n}ms",
  "wap.fail":            "echec",
  "wap.last":            "Dernier: {date}",

  // batch 15 - acp.*
  "acp.heading":           "Contexte du projet IA",
  "acp.routes":            "Routes",
  "acp.footer":            "Ce contexte est envoye avec chaque requete IA pour que l'assistant comprenne votre projet.",
  "acp.labelPages":        "Pages",
  "acp.labelComponents":   "Composants",
  "acp.labelDataModels":   "Modeles de donnees",
  "acp.labelWorkflows":    "Flux de travail",
  "acp.labelDesignTokens": "Tokens de design",

  // batch 15 - aip.*
  "aip.analyzing":        "Analyse en cours...",
  "aip.score":            "Score d'architecture",
  "aip.systems":          "Systemes ({n}/{m})",
  "aip.issues":           "Problemes ({n})",
  "aip.recommendations":  "Recommandations ({n})",
  "aip.applyFix":         "Appliquer la correction",
  "aip.applied":          "Recommandation appliquee",
  "aip.failedApply":      "Echec de l'application",
  "aip.autoFixFailed":    "Correction automatique echouee",

  // batch 15 - cpp.*
  "cpp.heading":      "Apercu du code",
  "cpp.generate":     "Generer",
  "cpp.materialize":  "Materialiser",
  "cpp.emptyHeading": "Aucun code genere",
  "cpp.emptyDesc":    "Cliquez sur Generer pour du code contextuel ou Materialiser pour des fichiers d'architecture.",

  // batch 15 - ssp.*
  "ssp.platformStatus":  "Statut de la plateforme",
  "ssp.projectHealth":   "Sante du projet",
  "ssp.backgroundTasks": "Taches en arriere-plan",
  "ssp.recentLogs":      "Journaux recents",
  "ssp.checkingHealth":  "Verification de la sante du service...",
  "ssp.noIssues":        "Aucun probleme detecte",
  "ssp.noLogs":          "Aucun journal pour l'instant",
  "ssp.outage":          "Panne",
  "ssp.degraded":        "Degrade",
  "ssp.operational":     "Operationnel",

  // batch 15 - vhp.*
  "vhp.noProject":   "Aucun projet charge",
  "vhp.heading":     "Historique des versions ({n})",
  "vhp.noVersions":  "Aucune version pour l'instant",
  "vhp.restored":    "Version restauree",
  "vhp.description": "{n} blocs · {m} pages",
  "vhp.scope":       "Enregistre uniquement dans ce navigateur. Efface avec les donnees du site ou en changeant d'appareil.",

  // batch 15 - ce.*
  "ce.loading": "Chargement de l'editeur...",

  // batch 15 - aop.*
  "aop.idle":        "En attente",
  "aop.thinking":    "Analyse",
  "aop.editing":     "Edition",
  "aop.reviewing":   "Revision",
  "aop.waiting":     "En attente d'entree",
  "aop.heading":     "Orchestration IA",
  "aop.emptyTitle":  "Aucun agent IA actif",
  "aop.emptyDesc":   "Les agents IA apparaissent ici lors de taches de generation ou de reparation",
  "aop.active":      "{n} actifs",
  "aop.inSession":   "{n} agents en session",
  "aop.idleSection": "En attente",

  // batch 15 - rnp.*
  "rnp.heading":     "Historique des versions",
  "rnp.loading":     "Chargement de l'historique...",
  "rnp.empty":       "Aucun evenement de version",
  "rnp.failedLoad":  "Echec du chargement des notes de version",
  "rnp.events":      "{n} evenements",
  "rnp.deployment":  "deploiement",
  "rnp.deployments": "deploiements",
  "rnp.build":       "compilation",
  "rnp.builds":      "compilations",
  "rnp.submission":  "soumission",
  "rnp.submissions": "soumissions",

  // batch 15 - sr.*
  "sr.title":      "Preparation pour la boutique",
  "sr.button":     "Boutique",
  "sr.ready":      "Pret",
  "sr.partial":    "Partiel",
  "sr.notReady":   "Non pret",
  "sr.submit":     "Soumettre",
  "sr.optimize":   "Optimiser",
  "sr.optimizing": "Optimisation...",
  "sr.highRisk":   "Risque eleve: corrigez les problemes avant de soumettre",
  "sr.fixIssues":  "Corrigez les problemes avant de soumettre",

  // batch 15 - cm.*
  "cm.credentials":   "Identifiants",
  "cm.noCredentials": "Ce connecteur ne requiert pas d'identifiants.",
  "cm.required":      "requis",
  "cm.alreadySet":    "deja configure",
  "cm.viewDocs":      "Voir la documentation",
  "cm.update":        "Mettre a jour",
  "cm.saveEnable":    "Enregistrer et activer",
  "cm.test":          "Tester",
  "cm.disconnect":    "Deconnecter",

  // batch 15 - ldp.*
  "ldp.heading":           "Documents juridiques",
  "ldp.privacyPolicy":     "Politique de confidentialite",
  "ldp.termsOfService":    "Conditions d'utilisation",
  "ldp.generatingPrivacy": "Generation de la politique de confidentialite...",
  "ldp.generatingTerms":   "Generation des conditions d'utilisation...",
  "ldp.dataCollected":     "Donnees collectees",
  "ldp.thirdParty":        "Services tiers",
  "ldp.governing":         "Capacites regissantes",

  // batch 15 - lav.*
  "lav.phaseIdle":         "En attente",
  "lav.phaseAnalyzing":    "Analyse de la demande",
  "lav.phaseArchitecting": "Conception de l'architecture",
  "lav.phaseGenerating":   "Generation des composants",
  "lav.phaseConnecting":   "Etablissement des connexions",
  "lav.phaseDeploying":    "Preparation du deploiement",
  "lav.phaseComplete":     "Architecture prete",
  "lav.configuration":     "Configuration",
  "lav.endpoints":         "Points d'acces",
  "lav.tables":            "Tables",
  "lav.files":             "Fichiers",
  "lav.building":          "construction...",
  "lav.tableCount":        "{n} tables",
  "lav.routeCount":        "{n} routes",

  // batch 15 - awg.*
  "awg.heading":          "Generateur de flux IA",
  "awg.placeholder":      "Decrivez votre flux de travail en langage naturel...",
  "awg.generating":       "Generation...",
  "awg.generateWorkflow": "Generer le flux",
  "awg.quickPrompts":     "Suggestions rapides",
  "awg.generated":        "Genere: {name}",
  "awg.nodesCreated":     "{n} noeuds crees",
  "awg.suggestion1":      "Envoyer un email de bienvenue apres l'inscription",
  "awg.suggestion2":      "Traiter le paiement et generer une facture",
  "awg.suggestion3":      "Rapport analytique quotidien pour l'equipe admin",
  "awg.suggestion4":      "Notifier Slack en cas d'echec de deploiement",
  "awg.suggestion5":      "Flux d'approbation utilisateur pour la publication de contenu",

  // batch 16 - aarp.*
  "aarp.heading":        "Agents IA",
  "aarp.scan":           "Scanner",
  "aarp.scanning":       "Scan en cours...",
  "aarp.scanFailed":     "Echec du scan",
  "aarp.approved":       "Approuve",
  "aarp.rejected":       "Rejete",
  "aarp.executed":       "Execute",
  "aarp.failed":         "Echec",
  "aarp.emptyTitle":     "Aucun resultat de scan",
  "aarp.emptyDesc":      "Lancez un scan pour obtenir des suggestions IA de refactorisation, bugs, optimisation, conformite et migration.",
  "aarp.runFullScan":    "Lancer le scan complet",
  "aarp.scanningAgents": "Scan avec 5 agents...",

  /* Pre-run credit estimate: what an action costs, what you have, what would remain. */
  "acp.upgrade": "Ameliorer",
  "acp.cost": "Ceci utilise {n} credits.",
  "acp.balance": "Vous avez {n}.",
  "acp.remaining": "Il vous resterait {n}.",
  "acp.short": "Ceci necessite {n} credits et vous en avez {have}. Rechargez ou passez a un plan superieur pour l'executer.",
  "acp.unknown": "Impossible de verifier votre solde de credits pour le moment.",
  "acp.action.full_app_gen": "Creer ou modifier une app",
  "acp.action.rebuild": "Reconstruction ciblee",
  "acp.action.page_gen": "Generer une page",
  "acp.action.component_gen": "Generer un composant",
  "acp.action.targeted_fix_build": "Appliquer un correctif cible",
  "acp.action.auto_fix": "Corriger un probleme",
  "acp.action.documentation": "Generer la documentation",
  "acp.action.ai_completion": "Poser une question sur votre projet",
  "acp.action.agent_execution": "Executer un agent autonome",
  "acp.action.image_generation": "Generer une image",
  "acp.action.music_generation": "Generer de la musique",
  "acp.action.speech_generation": "Generer une voix",
  "acp.action.voice_transcription": "Transcrire une note vocale",
  "acp.action.deployment": "Deployer vers un hebergeur",
  "acp.action.project_export": "Exporter le projet",
  "acp.action.aab": "Compilation Android App Bundle (AAB)",
  "acp.action.twa": "Compilation Android TWA",
  "acp.action.ipa": "Compilation iOS (IPA)",
  "acp.action.refactor": "Refactoriser un composant",
  "acp.action.build_plan_preview": "Apercu du plan de construction",
  "acp.action.document_generated": "Generer un document (longueur standard)",
  "acp.action.document_chapter": "Generer un chapitre",
  "acp.action.document_render": "Rendre un document en PDF",
  "acp.action.document_image": "Generer une illustration du document",
  "acp.action.document_export": "Exporter un document (EPUB/DOCX/ZIP)",
  "aarp.creditCost":     "Credits estimes :",
  "aarp.breakingChange": "Changement rupture",
  "aarp.approve":        "Approuver",
  "aarp.reject":         "Rejeter",
  "aarp.execute":        "Executer",
  "aarp.appliedSuccess": "Applique avec succes",
  "aarp.allClear":       "Tout est en ordre - aucun probleme trouve.",

  // batch 16 - op.*
  "op.stageArchitect":    "Architecture",
  "op.stageSchema":       "Base de donnees",
  "op.stageBackend":      "Backend",
  "op.stageFrontend":     "Frontend",
  "op.stagePreview":      "Apercu",
  "op.stageValidation":   "Validation",
  "op.stageDeployment":   "Deploiement",
  "op.stagePublishing":   "Publication",
  "op.captionArchitect":  "Intention vers plan systeme",
  "op.captionSchema":     "Generation du schema",
  "op.captionBackend":    "Routes API et services",
  "op.captionFrontend":   "Composants UI et pages",
  "op.captionPreview":    "Conteneur en direct",
  "op.captionValidation": "Reparation automatique",
  "op.captionDeployment": "Mise en production",
  "op.captionPublishing": "Soumission en boutique",

  // batch 16 - car.*
  "car.heading":      "Activite",
  "car.reconnecting": "Reconnexion au serveur...",
  "car.disconnected": "Deconnecte - evenements en pause",
  "car.emptyTitle":   "Aucune activite",
  "car.emptyDesc":    "Les evenements apparaitront ici au fil du travail des collaborateurs",

  // batch 16 - cpr.*
  "cpr.heading":       "Session",
  "cpr.collaborators": "Collaborateurs",
  "cpr.aiAgents":      "Agents IA",
  "cpr.emptyTitle":    "Aucun participant actif",
  "cpr.emptyWaiting":  "En attente de collaborateurs",
  "cpr.notConnected":  "Non connecte",

  // batch 16 - csp.*
  "csp.heading":            "Securite",
  "csp.blockRate":          "Taux de blocage",
  "csp.coordinatorLoading": "Chargement du coordinateur...",
  "csp.crossNodeSync":      "Sync inter-noeuds",
  "csp.active":             "actif",
  "csp.offline":            "hors ligne",
  "csp.recentHighSeverity": "Haute severite recente",
  "csp.operations":         "Operations",
  "csp.assessed":           "Evalues",
  "csp.blocked":            "Bloques",
  "csp.rateLimited":        "Limites par debit",
  "csp.validationFail":     "Echec de validation",
  "csp.quarantined":        "En quarantaine",
  "csp.aiBlocked":          "IA bloquee",
  "csp.loadingMetrics":     "Chargement des metriques...",
  "csp.detectorState":      "Etat du detecteur",
  "csp.users":              "utilisateurs",
  "csp.agents":             "agents",
  "csp.replayTracker":      "Traceur de relecture",
  "csp.changeIds":          "IDs de changement",
  "csp.distribution":       "Distribution",

  // batch 16 - cpl.*
  "cpl.heading":              "Conformite",
  "cpl.passed":               "validee",
  "cpl.nBlockers":            "{n} bloqueurs",
  "cpl.evaluating":           "Evaluation de la conformite...",
  "cpl.evaluated":            "Evalue",
  "cpl.detectedCapabilities": "Capacites detectees",
  "cpl.blockers":             "Bloqueurs ({n})",
  "cpl.requiresApproval":     "Necessite approbation ({n})",
  "cpl.warnings":             "Avertissements ({n})",
  "cpl.allClear":             "Aucun bloqueur de conformite detecte",
  "cpl.errSignIn":            "Connectez-vous pour evaluer la conformite de ce projet.",
  "cpl.errBuildFirst":        "Construisez ce projet d'abord pour evaluer la conformite.",
  "cpl.errGeneric":           "La conformite n'a pas pu etre evaluee. Reessayez bientot.",

  // batch 16 - smp.*
  "smp.heading":           "Metadonnees de boutique",
  "smp.tabAppStore":       "App Store",
  "smp.tabPlayStore":      "Play Store",
  "smp.tabSeo":            "SEO",
  "smp.error":             "erreur",
  "smp.errors":            "erreurs",
  "smp.warning":           "avertissement",
  "smp.warnings":          "avertissements",
  "smp.ready":             "pret",
  "smp.loading":           "Construction des metadonnees...",
  "smp.validationsPassed": "Toutes les validations ont reussi",
  "smp.validation":        "Validation ({n})",
  "smp.fieldTitle":        "Titre",
  "smp.fieldSubtitle":     "Sous-titre",
  "smp.fieldKeywords":     "Mots-cles",
  "smp.fieldDescription":  "Description",
  "smp.fieldShortDesc":    "Description courte",
  "smp.fieldFullDesc":     "Description complete",
  "smp.fieldMetaTitle":    "Titre meta",
  "smp.fieldMetaDesc":     "Description meta",
  "smp.fieldOgDesc":       "Description OG",

  // batch 17 - ppc.*
  "ppc.heading":             "Politique de confidentialite de Nuvra",
  "ppc.effectiveDate":       "Date d'entree en vigueur :",
  "ppc.lastUpdated":         "Derniere mise a jour :",
  "ppc.s1Title":             "1. Introduction",
  "ppc.s1Intro":             "Nuvra (\"Nuvra\", \"nous\", \"notre\" ou \"nos\") exploite une plateforme de creation de logiciels alimentee par IA permettant aux utilisateurs de concevoir, generer, deployer et gerer des applications via l'intelligence artificielle.",
  "ppc.s1Explains":          "Cette politique de confidentialite explique comment nous collectons, utilisons, divulguons et protegeons les informations lorsque vous utilisez :",
  "ppc.s1Li1":               "le site web de Nuvra",
  "ppc.s1Li2":               "la plateforme logicielle de Nuvra",
  "ppc.s1Li3":               "les services et API associes",
  "ppc.s1Li4":               "toute application, outil ou integration connexe",
  "ppc.s1Agree":             "En utilisant Nuvra, vous acceptez la collecte et l'utilisation des informations conformement a cette politique.",
  "ppc.s2Title":             "2. Informations que nous collectons",
  "ppc.s2Intro":             "Nous collectons les informations necessaires au fonctionnement de la plateforme et a la fourniture des services.",
  "ppc.s2AccountTitle":      "2.1 Informations de compte",
  "ppc.s2AccountIntro":      "Lorsque vous creez un compte, nous pouvons collecter :",
  "ppc.s2AccountLi1":        "nom",
  "ppc.s2AccountLi2":        "adresse e-mail",
  "ppc.s2AccountLi3":        "identifiants de compte",
  "ppc.s2AccountLi4":        "informations de profil",
  "ppc.s2AccountLi5":        "coordonnees de facturation",
  "ppc.s2AccountLi6":        "informations sur l'organisation",
  "ppc.s2PlatformTitle":     "2.2 Donnees d'utilisation de la plateforme",
  "ppc.s2PlatformIntro":     "Lorsque vous utilisez la plateforme Nuvra, nous pouvons collecter :",
  "ppc.s2PlatformLi1":       "metadonnees du projet",
  "ppc.s2PlatformLi2":       "journaux de compilation",
  "ppc.s2PlatformLi3":       "informations de deploiement",
  "ppc.s2PlatformLi4":       "configuration de l'application",
  "ppc.s2PlatformLi5":       "requetes API",
  "ppc.s2PlatformLi6":       "analyses d'utilisation des fonctionnalites",
  "ppc.s2PlatformOutro":     "Cela nous aide a ameliorer la fiabilite et les performances.",
  "ppc.s2AiTitle":           "2.3 Donnees d'interaction avec l'IA",
  "ppc.s2AiIntro":           "Lors de l'utilisation des fonctionnalites IA telles que :",
  "ppc.s2AiLi1":             "Createur de logiciels",
  "ppc.s2AiLi2":             "Mode de compilation autonome",
  "ppc.s2AiLi3":             "Assistant IA",
  "ppc.s2AiLi4":             "Generation de code",
  "ppc.s2AiLi5":             "Planification d'architecture",
  "ppc.s2AiProcess":         "nous pouvons traiter :",
  "ppc.s2AiPLi1":            "invites",
  "ppc.s2AiPLi2":            "resultats generes",
  "ppc.s2AiPLi3":            "instructions de compilation",
  "ppc.s2AiPLi4":            "retours systeme",
  "ppc.s2AiOutro":           "Ces donnees aident Nuvra a generer et ameliorer les applications.",
  "ppc.s2TechTitle":         "2.4 Donnees techniques",
  "ppc.s2TechIntro":         "Nous collectons automatiquement certaines informations techniques :",
  "ppc.s2TechLi1":           "adresse IP",
  "ppc.s2TechLi2":           "type de navigateur",
  "ppc.s2TechLi3":           "type d'appareil",
  "ppc.s2TechLi4":           "systeme d'exploitation",
  "ppc.s2TechLi5":           "metriques de performances",
  "ppc.s2TechLi6":           "journaux de diagnostic",
  "ppc.s3Title":             "3. Comment nous utilisons les informations",
  "ppc.s3Intro":             "Nous utilisons les informations collectees pour :",
  "ppc.s3Li1":               "exploiter et maintenir la plateforme Nuvra",
  "ppc.s3Li2":               "generer des applications logicielles via l'IA",
  "ppc.s3Li3":               "fournir des services d'infrastructure et de deploiement",
  "ppc.s3Li4":               "traiter les paiements et abonnements",
  "ppc.s3Li5":               "ameliorer les fonctionnalites de la plateforme",
  "ppc.s3Li6":               "assurer la securite et la fiabilite du systeme",
  "ppc.s3Li7":               "prevenir la fraude ou les abus",
  "ppc.s3Li8":               "respecter les obligations legales",
  "ppc.s3NoSell":            "Nous ne vendons pas de donnees personnelles a des tiers.",
  "ppc.s4Title":             "4. Contenu et projets generes par IA",
  "ppc.s4Intro":             "Les applications generees avec Nuvra peuvent inclure :",
  "ppc.s4Li1":               "interfaces utilisateur",
  "ppc.s4Li2":               "API backend",
  "ppc.s4Li3":               "bases de donnees",
  "ppc.s4Li4":               "configurations d'infrastructure",
  "ppc.s4Ownership":         "La propriete du code de projet genere appartient a l'utilisateur qui a cree le projet, sauf restriction par la loi applicable ou des licences tierces.",
  "ppc.s4Process":           "Nuvra peut traiter les donnees du projet uniquement pour fournir les fonctionnalites de la plateforme et ameliorer les services.",
  "ppc.s5Title":             "5. Stockage des donnees et securite",
  "ppc.s5Intro":             "Nous mettons en oeuvre des mesures de securite standards du secteur, notamment :",
  "ppc.s5Li1":               "transmission de donnees chiffree",
  "ppc.s5Li2":               "controles d'acces",
  "ppc.s5Li3":               "protection de l'authentification",
  "ppc.s5Li4":               "surveillance de l'infrastructure",
  "ppc.s5Li5":               "analyse des vulnerabilites",
  "ppc.s5Caveat":            "Cependant, aucun systeme internet ne peut garantir une securite absolue.",
  "ppc.s6Title":             "6. Conservation des donnees",
  "ppc.s6Intro":             "Nous conservons les donnees uniquement le temps necessaire pour :",
  "ppc.s6Li1":               "fournir les services",
  "ppc.s6Li2":               "maintenir l'integrite du systeme",
  "ppc.s6Li3":               "respecter les obligations legales",
  "ppc.s6Request":           "Les utilisateurs peuvent demander la suppression de leurs comptes et des donnees associees.",
  "ppc.s7Title":             "7. Transferts internationaux de donnees",
  "ppc.s7Intro":             "Nuvra peut traiter des donnees dans plusieurs juridictions.",
  "ppc.s7SafeguardsIntro":   "Lorsque requis, nous utilisons des garanties telles que :",
  "ppc.s7Li1":               "Clauses contractuelles types",
  "ppc.s7Li2":               "mecanismes legaux de transfert de donnees",
  "ppc.s7Li3":               "options d'hebergement regional",
  "ppc.s7Outro":             "pour proteger les informations des utilisateurs a travers les frontieres.",
  "ppc.s8Title":             "8. Droits des utilisateurs",
  "ppc.s8Intro":             "Selon votre juridiction, vous pouvez avoir des droits incluant :",
  "ppc.s8Li1":               "acces a vos donnees",
  "ppc.s8Li2":               "correction des informations inexactes",
  "ppc.s8Li3":               "suppression des informations personnelles",
  "ppc.s8Li4":               "restriction du traitement",
  "ppc.s8Li5":               "portabilite des donnees",
  "ppc.s8Li6":               "retrait du consentement",
  "ppc.s8Contact":           "Les demandes peuvent etre soumises via la plateforme ou en contactant le support.",
  "ppc.s9Title":             "9. Cookies et technologies de suivi",
  "ppc.s9Intro":             "Nuvra peut utiliser des cookies et technologies similaires pour :",
  "ppc.s9Li1":               "maintenir les sessions de connexion",
  "ppc.s9Li2":               "ameliorer les fonctionnalites de la plateforme",
  "ppc.s9Li3":               "analyser les modes d'utilisation",
  "ppc.s9Control":           "Les utilisateurs peuvent controler les cookies via les parametres du navigateur.",
  "ppc.s10Title":            "10. Services tiers",
  "ppc.s10Intro":            "Nuvra peut s'integrer avec des services tiers, notamment :",
  "ppc.s10Li1":              "fournisseurs d'infrastructure cloud",
  "ppc.s10Li2":              "passerelles de paiement",
  "ppc.s10Li3":              "fournisseurs de modeles IA",
  "ppc.s10Li4":              "outils de developpement",
  "ppc.s10Note":             "Ces services fonctionnent sous leurs propres politiques de confidentialite.",
  "ppc.s11Title":            "11. Confidentialite des enfants",
  "ppc.s11NotFor":           "Les services de Nuvra ne sont pas destines aux enfants de moins de 13 ans.",
  "ppc.s11NoCollect":        "Nous ne collectons pas sciemment d'informations personnelles aupres des enfants.",
  "ppc.s12Title":            "12. Modifications de cette politique de confidentialite",
  "ppc.s12Update":           "Nous pouvons mettre a jour cette politique de temps en temps pour refleter des changements legaux ou operationnels.",
  "ppc.s12Posted":           "Les versions mises a jour seront publiees avec une date d'entree en vigueur revisee.",
  "ppc.s13Title":            "13. Informations de contact",
  "ppc.s13Intro":            "Pour les demandes ou questions relatives a la confidentialite :",
  "ppc.s13Email":            "E-mail :",
  "ppc.s13EmailLink":        "hello@nuvrahq.com",

  // batch 18 - cmp.*, of.*, sg.*
  "cmp.placeholder":      "Decrivez le produit a developper...",
  "cmp.submitLabel":      "Envoyer a l'ingenierie",
  "cmp.ariaLabel":        "Compositeur de messages",
  "cmp.stopEngineering":  "Arreter l'ingenierie",
  "cmp.engineering":      "Ingenierie",
  "of.nodeAi":            "Moteur IA",
  "of.nodeCodegen":       "Gen. Code",
  "of.nodeArch":          "Architecte",
  "of.nodeFrontend":      "Frontend",
  "of.nodeApi":           "Passerelle API",
  "of.nodeDb":            "Base de donnees",
  "of.nodeDeploy":        "Deploiement",
  "of.nodeAuth":          "Authentification",
  "of.nodeMonitor":       "Moniteur",
  "of.nucleus":           "NOYAU",
  "sg.fields":            "champs",
  "sg.rels":              "rels",

  // ── Keys added by the main-branch merge (translated for parity) ──
  "nav.item.plugins": "Plugins",
  "nav.item.integrations": "Integrations",
  "nav.item.connectors": "Integrations",
  
  
  
  
  
  
  "pw.preview.hidePreview": "Masquer le panneau d'apercu",
  "pw.preview.showPreview": "Afficher le panneau d'apercu",
  "pw.preview.preview": "Apercu",
  "pw.build.noLlm": "Moteur d'ingenierie indisponible.",
  "pw.build.noLlmHint": "Aucun fournisseur d'IA n'est configure. Les builds sont en pause jusqu'a ce que le moteur d'IA soit en ligne. Aucun credit n'a ete utilise.",
  "pw.build.planLimit": "Limite mensuelle de builds atteinte.",
  "pw.build.planLimitHint": "Ameliorez votre offre pour continuer a construire.",
  "ver.search": "Rechercher des builds",
  "ver.noMatch": "Aucun build ne correspond a votre recherche.",
  "ext.error.unauthenticated": "Connectez-vous pour parcourir et installer des extensions.",
  "ext.error.forbidden": "Vous n'avez pas la permission de gerer les extensions de ce projet.",
  "ext.error.notFound": "Le registre des extensions n'est pas disponible. Reessayez plus tard.",
  "ext.error.server": "Le service des extensions est indisponible. Reessayez dans un instant.",
  "ext.noProjectTitle": "Ouvrez cette page depuis un projet pour installer et gerer ses extensions.",
  "ext.noProjectHint": "Vous pouvez parcourir le registre ci-dessous.",
  "wh.title": "Webhooks",
  "wh.subtitle": "Envoyez des notifications HTTP en temps reel a vos endpoints lorsque des evenements surviennent dans votre espace de travail.",
  "wh.endpoints": "Endpoints",
  "wh.endpointsEmpty": "Aucun endpoint webhook configure. Ajoutez-en un pour commencer a recevoir des evenements.",
  "wh.add": "Ajouter un endpoint",
  "wh.url": "URL de l'endpoint",
  "wh.urlPlaceholder": "https://votre-serveur.com/webhook",
  "wh.name": "Nom",
  "wh.namePlaceholder": "Mon webhook",
  "wh.events": "Abonnements aux evenements",
  "wh.create": "Creer l'endpoint",
  "wh.cancel": "Annuler",
  "wh.creating": "Creation",
  "wh.delete": "Supprimer",
  "wh.deleting": "Suppression",
  "wh.enable": "Activer",
  "wh.disable": "Desactiver",
  "wh.enabled": "Actif",
  "wh.disabled": "En pause",
  "wh.deliveries": "Historique des livraisons",
  "wh.deliveriesEmpty": "Aucune livraison pour cet endpoint pour l'instant.",
  "wh.deliverySuccess": "Livre",
  "wh.deliveryFailed": "Echec",
  "wh.deliveryStatus": "Statut",
  "wh.deliveryAt": "Livre le",
  "wh.deliveryEvent": "Evenement",
  "wh.secretTitle": "Secret de signature",
  "wh.secretDesc": "Utilisez ce secret pour verifier les signatures des webhooks. Il n'est affiche qu'une seule fois.",
  "wh.secretCopy": "Copier le secret",
  "wh.secretCopied": "Copie",
  "wh.secretClose": "J'ai enregistre le secret",
  "wh.testSend": "Envoyer un evenement de test",
  "wh.testing": "Envoi",
  "wh.testSent": "Evenement de test envoye",
  "wh.loadError": "Impossible de charger les endpoints webhook.",
  "wh.createError": "Impossible de creer l'endpoint.",
  "wh.deleteError": "Impossible de supprimer l'endpoint.",
  "wh.noOrg": "Les webhooks necessitent un espace de travail. Creez ou rejoignez-en un pour gerer les endpoints webhook.",
  "wh.eventTypes.build.completed": "build.completed",
  "wh.eventTypes.build.failed": "build.failed",
  "wh.eventTypes.deploy.completed": "deploy.completed",
  "wh.eventTypes.deploy.failed": "deploy.failed",
  "wh.eventTypes.project.created": "project.created",
  "wh.eventTypes.project.deleted": "project.deleted",
  "wh.eventTypes.member.joined": "member.joined",
  "wh.eventTypes.member.removed": "member.removed",
  "wh.loading": "Chargement des endpoints",
  "team.title": "Equipe",
  "team.subtitle": "Gerez les membres de l'espace de travail, leurs roles et les invitations.",
  "team.members": "Membres",
  "team.membersEmpty": "Aucun membre dans l'equipe pour l'instant.",
  "team.invite": "Inviter un membre",
  "team.inviteEmail": "Adresse e-mail",
  "team.inviteRole": "Role",
  "team.inviteSend": "Envoyer l'invitation",
  "team.inviting": "Envoi",
  "team.invitations": "Invitations en attente",
  "team.invitationsEmpty": "Aucune invitation en attente.",
  "team.role.admin": "Administrateur",
  "team.role.member": "Membre",
  "team.role.viewer": "Observateur",
  "team.role.billing": "Facturation",
  "team.remove": "Retirer",
  "team.removing": "Retrait",
  "team.cancel": "Annuler",
  "team.revokeInvite": "Revoquer",
  "team.loadError": "Impossible de charger les membres de l'equipe.",
  "team.inviteError": "Impossible d'envoyer l'invitation.",
  "team.removeError": "Impossible de retirer le membre.",
  "team.noOrg": "Vous n'avez pas encore d'espace de travail.",
  "team.createHint": "Nommez votre espace de travail pour inviter votre equipe.",
  "team.workspaceNamePlaceholder": "Nom de l'espace de travail",
  "team.createWorkspace": "Creer l'espace",
  "team.workspaceNameShort": "Le nom doit contenir au moins 2 caracteres.",
  "team.createError": "Impossible de creer l'espace de travail. Verifiez votre connexion et reessayez.",
  "team.retry": "Reessayer",
  "team.you": "Vous",
  "team.owner": "Proprietaire",
  "team.joinedAt": "A rejoint",
  "team.loading": "Chargement de l'equipe",
  "team.inviteSuccess": "Invitation envoyee.",
  "team.removeSuccess": "Membre retire.",
  "team.revokeSuccess": "Invitation revoquee.",
  "team.seats": "Sieges utilises",
  "settings.privacy.controls.title": "Contrôles de confidentialité et des données",
  "settings.privacy.controls.subtitle": "Contrôlez l’utilisation facultative des données, les communications et les diagnostics. Les changements sont enregistrés sur votre compte Nuvra lorsque le stockage du compte est disponible.",
  "settings.privacy.ai.title": "Données d’amélioration de l’IA",
  "settings.privacy.ai.desc": "Autoriser Nuvra à utiliser des données de build anonymisées pour améliorer le moteur d’IA.",
  "settings.privacy.analytics.title": "Analytique produit",
  "settings.privacy.analytics.desc": "Autoriser Nuvra a collecter des donnees d'usage anonymisees pour ameliorer le produit.",
  "settings.privacy.marketing.title": "Communications marketing",
  "settings.privacy.marketing.desc": "Recevoir les nouveautes du produit, des conseils et des annonces par e-mail.",
  "settings.privacy.crash.title": "Rapports d'erreurs et de plantages",
  "settings.privacy.crash.desc": "Envoyer automatiquement les rapports d'erreur pour corriger les bugs plus vite.",
  "settings.privacy.saved": "Preferences de confidentialite enregistrees.",
  "settings.privacy.saving": "Enregistrement",
  "settings.privacy.saveError": "Impossible d'enregistrer les preferences. Reessayez.",
  "settings.privacy.export.title": "Exportation des données personnelles",
  "settings.privacy.export.desc": "Utilisez le centre d’exportation des données de Nuvra pour télécharger les données disponibles sur votre compte.",
  "settings.privacy.export.btn": "Ouvrir l’exportation des données",
  "settings.privacy.regional.title": "Cadres de confidentialité",
  "settings.privacy.regional.gdpr": "RGPD (UE/EEE)",
  "settings.privacy.regional.ccpa": "CCPA (Californie)",
  "settings.privacy.regional.ndpr": "NDPR (Nigeria)",
  "settings.privacy.policyLink": "Voir la politique de confidentialite complete",
  "settings.appearance.title": "Apparence",
  "settings.appearance.theme.title": "Theme",
  "settings.appearance.theme.dark": "Sombre",
  "settings.appearance.theme.dark.desc": "La palette sombre native de Nuvra. Ideale pour les longues sessions.",
  "settings.appearance.theme.light": "Clair",
  "settings.appearance.theme.light.desc": "Une palette claire pour les espaces lumineux.",
  "settings.appearance.theme.system": "Systeme",
  "settings.appearance.theme.system.desc": "Suit la preference claire/sombre de votre systeme.",
  "settings.appearance.density.title": "Densite de l'interface",
  "settings.appearance.density.default": "Par defaut",
  "settings.appearance.density.default.desc": "Espacement standard pour la plupart des ecrans.",
  "settings.appearance.density.compact": "Compacte",
  "settings.appearance.density.compact.desc": "Plus d'informations, moins de marges.",
  "settings.appearance.saved": "Apparence mise a jour.",
  // Deployment Center (MANDATE 15)
  "dc.providers":          "Choisissez ou deployer",
  "dc.soon":               "Bientot",
  "dc.preview":            "Apercu",
  "dc.ship.liveAt":        "En direct sur",
  "dc.ship.notLive":       "Le deploiement n'est pas passe en direct.",
  "dc.ship.signed":        "Signe via {provider}.",
  "dc.ship.unsigned":      "Sans signature de code (non requise pour le web).",
  "dc.ship.rollbackOk":    "Retour arriere disponible.",
  "dc.railway.title":      "Railway",
  "dc.railway.connected":  "Connecte en tant que {account}",
  "dc.railway.disconnect": "Deconnecter",
  "dc.railway.token":      "Jeton API Railway",
  "dc.railway.service":    "ID de service (optionnel)",
  "dc.railway.env":        "ID d'environnement (optionnel)",
  "dc.railway.connect":    "Connecter Railway",
  "dc.cloud.title":        "Nuvra Cloud",
  "dc.cloud.prod":         "Production",
  "dc.cloud.live":         "en direct",
  "dc.cloud.notLive":      "hors ligne",
  "dc.cloud.empty":        "Aucun deploiement pour l'instant. Publiez votre derniere version pour la voir ici.",
  "dc.cloud.active":       "actif",
  "dc.cloud.rollback":     "Revenir a ce deploiement",
  "dc.cloud.restart":      "Redemarrer ce deploiement",
  "dc.cloud.logs":         "Journaux du deploiement",
  "dc.env.title":          "Variables d'environnement",
  "dc.env.desc":           "Chiffrees au repos. Les valeurs ne sont jamais affichees apres l'enregistrement.",
  "dc.env.remove":         "Retirer",
  "dc.env.name":           "NOM",
  "dc.env.value":          "valeur",
  "dc.env.add":            "Ajouter",
  "dc.error.generic":      "Un probleme est survenu. Reessayez.",
  "dc.history.title":      "Historique des deploiements",
  "dc.history.charged":    "{acu} credits",
  "dc.history.note":       "Chaque deploiement chez tous les fournisseurs, avec les credits utilises. Les deploiements echoues sont rembourses.",
  "dc.history.status.failed":              "Echoue (rembourse)",
  "dc.history.status.blocked_entitlement": "Limite du forfait atteinte",
  "dc.history.status.blocked_acu":         "Credits insuffisants",
  "dc.history.status.refused":             "Refuse",

  // Autorisation d'execution: ce que Nuvra peut depenser et modifier sur ce projet.
  "auth.title":            "Autorisation d'execution",
  "auth.subtitle":         "Nuvra ne peut ni depenser vos credits ni modifier ce projet sans une autorisation enregistree pour cette operation precise. Voici ce qui est autorise maintenant, ce qui a reellement ete utilise, et comment l'arreter.",
  "auth.mode.enforce":     "Applique",
  "auth.mode.observe":     "Observation seule",
  "auth.ledger.durable":   "Registre durable",
  "auth.ledger.memory":    "Registre en memoire (ce processus uniquement)",
  "auth.active.title":     "Autorise maintenant",
  "auth.active.none":      "Rien n'est autorise a depenser sur ce projet. Lancer une construction, un deploiement ou une analyse cree une autorisation pour cette operation.",
  "auth.history.title":    "Autorisations recentes",
  "auth.history.none":     "Aucune autorisation enregistree pour ce projet pour l'instant.",
  "auth.class.analysis":   "Analyse par IA",
  "auth.class.execution":  "Construire et modifier",
  "auth.class.deployment": "Deployer",
  "auth.class.rollback":   "Revenir en arriere",
  "auth.class.destructive": "Changement destructif",
  "auth.badge.production": "Production approuvee",
  "auth.badge.destructive": "Destructif approuve",
  "auth.remaining":        "{remaining} sur {total} credits restants",
  "auth.used":             "{used}/{total} credits",
  "auth.revoke":           "Revoquer",
  "auth.draws.none":       "Rien n'a ete preleve sur cette autorisation.",
  "auth.draw.open":        "{acu} retenus",
  "auth.draw.settled":     "{acu} utilises sur {held} retenus",
  "auth.draw.released":    "{acu} rendus",
  "auth.error.load":       "Impossible de charger les autorisations.",
  "auth.error.revoke":     "Impossible de revoquer cette autorisation.",

  // Nuvra Support (surface client)
  "support.title":                "Support Nuvra",
  "support.subtitle":             "Decrivez ce qui ne va pas. Le support Nuvra enquete, resout ce qu il peut faire sans risque, et fait entrer un ingenieur du support dans la meme conversation quand il ne le peut pas.",
  "support.newConversation":      "Nouvelle conversation",
  "support.startTopic":           "Demarrer une conversation sur {topic}",
  "support.subjectPlaceholder":   "De quoi s agit il?",
  "support.describePlaceholder":  "Decrivez le probleme. Que faisiez vous, qu attendiez vous, et que s est il passe?",
  "support.start":                "Demarrer la conversation",
  "support.starting":             "Demarrage",
  "support.cancel":               "Annuler",
  "support.emptyTitle":           "Comment pouvons nous aider?",
  "support.emptyBody":            "Demarrez une conversation et le support Nuvra l examinera. S il ne peut pas resoudre le probleme sans risque, un ingenieur du support rejoint la meme conversation et voit tout ce que vous avez deja dit.",
  "support.loading":              "Chargement",
  "support.composerPlaceholder":  "Ecrire au support Nuvra…",
  "support.send":                 "Envoyer",
  "support.sending":              "Envoi",
  "support.working":              "Le support Nuvra examine cela",
  "support.sendFailed":           "Non envoye",
  "support.retry":                "Reessayer",
  "support.reconnecting":         "Reconnexion",
  "support.backToList":           "Retour aux conversations",
  "support.attachFiles":          "Joindre des fichiers",
  "support.attachRejected.type":    "ce type de fichier n’est pas accepte. Envoyez un PNG, JPEG, GIF, WebP ou PDF.",
  "support.attachRejected.size":    "ce fichier depasse 10 Mo.",
  "support.attachRejected.session": "votre session a expire. Reconnectez-vous pour joindre des fichiers.",
  "support.attachRejected.failed":  "ce fichier n’a pas pu etre joint. Reessayez.",
  "support.removeAttachment":     "Retirer",
  "support.uploading":            "Envoi en cours",
  "support.attachmentLimit":      "Vous pouvez joindre jusqu’a 5 fichiers a un message.",
  "support.attachmentUnavailable": "n’a pas pu etre charge",
  "support.withEngineer":         "Un ingenieur du support s en occupe",
  "support.listWithEngineer":     "avec un ingenieur du support",
  "support.resolvedNote":         "Cette conversation est resolue. Repondez ci dessous a tout moment pour la rouvrir.",
  "support.openFailed":           "Impossible de demarrer la conversation",
  "support.status.open":          "Ouverte",
  "support.status.answered":      "Repondue",
  "support.status.escalated":     "Avec un ingenieur",
  "support.status.resolved":      "Resolue",
  "nav.item.support":             "Support",
  "support.homeHeading": "Un support d'ingenierie qui comprend votre espace de travail.",
  "support.homeBody": "Dites a Nuvra ce que vous essayez de faire ou ce qui ne fonctionne pas. Nous analysons, resolvons ce qui peut l'etre en toute securite et faisons intervenir un ingenieur du support si besoin.",
  "support.topicsHeading": "Sujets courants",
  "support.topic.builds": "Compilations",
  "support.topic.deployments": "Deploiements",
  "support.topic.projects": "Projets",
  "support.topic.billing": "Facturation",
  "support.topic.account": "Compte",
  "support.starter.account": "J'ai besoin d'aide pour mon compte : ",
  "support.starter.billing": "J'ai une question sur la facturation : ",
  "support.starter.projects": "J'ai un probleme avec un projet : ",
  "support.starter.deployments": "J'ai un probleme avec un deploiement : ",
  "support.starter.builds": "J'ai un probleme avec une compilation : ",
  "support.searchPlaceholder": "Rechercher des conversations",
  "support.filterLabel": "Filtrer les conversations",
  "support.filter.all": "Toutes",
  "support.filter.open": "Ouvertes",
  "support.filter.waiting": "En attente de vous",
  "support.filter.resolved": "Resolues",
  "support.state.open": "Ouverte",
  "support.state.waiting": "En attente de vous",
  "support.state.with_engineer": "Avec un ingenieur",
  "support.state.resolved": "Resolue",
  "support.listEmpty": "Aucune conversation pour le moment.",
  "support.listNoMatch": "Aucune conversation ne correspond.",
  "support.untitled": "Demande de support",
  "support.markResolved": "Marquer resolue",
  "support.threadLoadFailed": "Nous n'avons pas pu charger cette conversation.",
  "support.tryAgain": "Reessayer",
  "support.withEngineerNote": "Un ingenieur du support Nuvra suit cette conversation. Il voit tout ce que vous avez deja partage, vous n'avez donc pas besoin de vous repeter.",
  "support.category.how_to": "Comment faire",
  "support.category.bug": "Probleme",
  "support.category.billing": "Facturation",
  "support.category.account": "Compte",
  "support.category.policy_appeal": "Politique",
  "support.category.payout": "Paiement",
  "support.category.dispute": "Litige",
  "support.voice.you":            "Vous",
  "support.voice.ai":             "Support Nuvra",
  "support.voice.engineer":       "Ingenieur du support",
  "support.voice.system":         "Nuvra",
  "help.supportPitch":            "Ouvrez une conversation avec le support Nuvra. Le support enquete et fait entrer un ingenieur dans la meme conversation quand il ne peut pas resoudre le probleme sans risque.",
  "help.openSupport":             "Ouvrir le support Nuvra",

  // Project infrastructure panel (what Nuvra can actually do with connected services).
  // The server sends stable ids and enums; every sentence below is the client's own copy,
  // which is why the panel needs no fallback to the server's English.
  "infra.triggerLabel": "Infrastructure",
  "infra.triggerTitle": "Ce que Nuvra peut faire avec vos services connectes",
  "infra.triggerAria": "Infrastructure du projet",
  "infra.heading": "Infrastructure",
  "infra.unreadable": "Nuvra n'a pas pu lire les connexions de ce projet pour le moment. Rien n'a change.",
  "infra.needsApproval": "(vous demande d'abord)",
  "infra.ready": "Ce projet a une destination ou construire.",
  "infra.notReady": "Connectez un depot pour donner a ce projet une destination ou construire.",
  "infra.autonomy.assisted": "Nuvra demande avant de changer quoi que ce soit.",
  "infra.autonomy.autonomous": "Nuvra travaille seul, y compris sur votre produit en production.",
  "infra.autonomy.autonomous_with_production_approval": "Nuvra travaille seul, et demande avant de changer votre produit en production.",
  "infra.cap.github.create_repo": "Creer un nouveau depot",
  "infra.cap.github.read_code": "Lire votre code source",
  "infra.cap.github.branch": "Creer des branches",
  "infra.cap.github.push": "Valider et pousser les changements",
  "infra.cap.github.pull_request": "Ouvrir des demandes de fusion",
  "infra.cap.github.ci": "Lancer la CI et lire les resultats",
  "infra.cap.supabase.inspect": "Inspecter votre schema",
  "infra.cap.supabase.migrate": "Ecrire des migrations",
  "infra.cap.supabase.apply": "Appliquer les changements a votre base de donnees en production",
  "infra.cap.vercel.preview": "Creer des deploiements de previsualisation",
  "infra.cap.vercel.production": "Deployer vers votre site en production",
  "infra.cap.vercel.logs": "Lire les journaux de deploiement",
  "infra.cap.railway.deploy": "Deployer vos services backend",
  "infra.cap.railway.logs": "Lire les journaux du service",
  "infra.state.github.not_configured": "GitHub n'est pas encore disponible sur cette installation de Nuvra.",
  "infra.state.github.upgrade_required": "Passez a un plan superieur pour que Nuvra travaille avec vos depots.",
  "infra.state.github.not_connected": "Connectez GitHub pour que Nuvra enregistre et mette a jour votre code.",
  "infra.state.github.no_resource_selected": "GitHub est connecte. Choisissez un depot pour ce projet, ou laissez Nuvra en creer un.",
  "infra.state.github.paused": "Nuvra est en pause pour ce depot. Reprenez quand vous voulez que Nuvra continue.",
  "infra.state.github.connected": "Nuvra travaille dans ce depot.",
  "infra.state.supabase.not_connected": "Connectez Supabase pour que Nuvra gere votre base de donnees.",
  "infra.state.supabase.paused": "Nuvra est en pause pour cette base de donnees. Reprenez quand vous voulez que Nuvra continue.",
  "infra.state.supabase.connected": "Nuvra travaille avec cette base de donnees.",
  "infra.state.vercel.not_connected": "Connectez Vercel pour que Nuvra deploie votre interface.",
  "infra.state.vercel.paused": "Nuvra est en pause pour ce site. Reprenez quand vous voulez que Nuvra continue.",
  "infra.state.vercel.connected": "Nuvra deploie vers ce site.",
  "infra.state.railway.not_connected": "Connectez Railway pour que Nuvra execute vos services backend.",
  "infra.state.railway.paused": "Nuvra est en pause pour ce service. Reprenez quand vous voulez que Nuvra continue.",
  "infra.state.railway.connected": "Nuvra execute ce service.",
  "settings.privacy.loading": "Chargement des préférences de confidentialité...",
  "settings.privacy.synced": "Synchronisé avec votre compte Nuvra",
  "settings.privacy.localFallback": "Enregistré sur cet appareil ; la synchronisation du compte est indisponible",
  "settings.privacy.notSynced": "État de synchronisation du compte indisponible",
  "settings.privacy.data.title": "Vos données",
  "settings.privacy.data.desc": "Consultez les données que Nuvra met à disposition de votre compte et utilisez les contrôles d’exportation lorsque vous avez besoin d’une copie.",
  "settings.privacy.regional.subtitle": "Les exigences de confidentialité varient selon la région. Ces références vous aident à comprendre les cadres susceptibles de s’appliquer à votre utilisation de Nuvra.",
  "settings.privacy.rightsHint": "Vous pouvez modifier vos choix de confidentialité à tout moment",
  "settings.systemStatus.subtitle": "Signaux de santé en direct de l’API, des fournisseurs IA, de l’infrastructure de build, de la base de données et du service d’incidents de Nuvra.",
  "settings.systemStatus.refresh": "Actualiser",
  "settings.systemStatus.monitoring": "Surveillance en direct activée",
  "settings.systemStatus.lastChecked": "Vérifié à {time}",
  "settings.systemStatus.incidentsSubtitle": "Les incidents actifs sont mis à jour en temps réel lorsque cette fonction est disponible.",
  "settings.systemStatus.noIncidentsHint": "Aucun incident actif n’est actuellement enregistré par Nuvra.",
  "settings.systemStatus.recentIncidents": "Incidents récents",
  "settings.systemStatus.pageChecked": "Page vérifiée à {time}",
  "settings.systemStatus.autoRefresh": "Actualisation automatique toutes les 30 secondes",
  "settings.privacy.regional.gdpr.desc": "Droits à la confidentialité dans l’UE/EEE et cadre de protection des données.",
  "settings.privacy.regional.ccpa.desc": "Droits à la confidentialité en Californie et contrôles des données des consommateurs.",
  "settings.privacy.regional.ndpr.desc": "Droits à la confidentialité au Nigeria et cadre de protection des données.",
  // Phase 6 build journey spine
  "bj.heading": "Parcours de construction",
  "bj.understand": "Comprendre",
  "bj.plan": "Planifier",
  "bj.build": "Construire",
  "bj.verify": "Vérifier",
  "bj.live": "En ligne",
  "bj.complete": "Terminé",
  "bj.active": "En cours",
  "bj.failed": "Attention requise",
  "bj.pending": "Pas commencé",
  "bj.liveHint": "L'état en direct repose sur le travail réellement signalé par Nuvra, pas sur le temps écoulé.",

  "landing.showcase.eyebrow": "VOYEZ NUVRA CONSTRUIRE",
  "landing.showcase.h2a": "De l'idée",
  "landing.showcase.h2b": "à la production",
  "landing.showcase.desc": "Découvrez le parcours d'ingénierie que Nuvra est conçu pour exécuter : résoudre l'intention, planifier le système, construire par étapes, vérifier le résultat et faire avancer le produit vers la production.",
  "landing.showcase.cta": "Commencer à construire",
  "landing.showcase.step1.label": "Commencer par une idée",
  "landing.showcase.step1.title": "Décrivez le système souhaité.",
  "landing.showcase.step1.desc": "Commencez en langage naturel. Le planificateur public de Nuvra transforme l'idée en aperçu d'architecture concret avant le lancement d'une construction authentifiée.",
  "landing.showcase.step2.label": "Architecture d'abord",
  "landing.showcase.step2.title": "Transformer l'intention en plan système.",
  "landing.showcase.step2.desc": "Les exigences, services, données, interfaces, rôles et limites de sécurité sont structurés avant l'exécution du code.",
  "landing.showcase.step3.label": "Voir la construction",
  "landing.showcase.step3.title": "Construire les couches dans un ordre contrôlé.",
  "landing.showcase.step3.desc": "Le frontend, le backend, les données et la sécurité avancent par étapes délimitées plutôt que par une génération opaque.",
  "landing.showcase.step4.label": "Vérifier et réparer",
  "landing.showcase.step4.title": "La validation reste dans la boucle.",
  "landing.showcase.step4.desc": "Les contrôles fournissent les preuves nécessaires à l'étape suivante. Lorsqu'un échec pris en charge est détecté, une réparation ciblée peut fermer la boucle.",
  "landing.showcase.step5.label": "Avancer vers la production",
  "landing.showcase.step5.title": "Prévisualiser, gouverner et déployer.",
  "landing.showcase.step5.desc": "Le résultat n'est pas un simple dépôt de code. Nuvra est conçu pour accompagner le produit vers la prévisualisation, le déploiement, la surveillance et le contrôle de production.",
};
